import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Music, RotateCcw, BellOff, Play, Pause, Trash2, GripVertical, Search, Filter, X, ExternalLink, CheckCircle, Disc3, ArrowUpDown, ArrowUp, ArrowDown, StickyNote, ChevronUp, ChevronDown, Save, Flame, Loader2, Users, Clock, AlertTriangle, SkipForward, SkipBack, Sparkles, Check, Crosshair, Inbox, MessageSquare, ListMusic, Link2, SearchX, Lock, BarChart3, RefreshCw, QrCode, Plus, Timer, ToggleLeft, ToggleRight, StopCircle, Smartphone, ShieldCheck, ShieldX, Printer, ShieldAlert, Flag, Power, Crown, FileMusic, EyeOff, FlaskConical, Copy, Trash, Monitor, Brain, Wrench } from 'lucide-react';
import { PEOPLES_PICK_HIDDEN, OVERFLOW_POOL_ENABLED } from '../../utils/featureFlags';
import LimitRaiseModal from '../../components/dj/LimitRaiseModal';
import QrPrintModal from '../../components/QrPrintModal';
import { attendeeDisplayName } from '../../utils/attendeeDisplayName';
import { getRequestsByEventId, subscribeToRequestsByEventId, deleteRequest, updateRequest, getRequestById, matchedLinkFields, updateManualOrder, createRequest, SongRequest, getNowPlaying, subscribeToNowPlaying, isTrending, isFuzzySongMatch, calculateBothCopelandScoresWithStaleness, findHiddenGems, getVotersForRequest, getVoteBreakdown, countParticipantsSince, Voter, approveSuperRequestManually, subscribeToCachedCopelandScores } from '../../services/requests';
import { subscribeToUpvoteCountsForEvent, makePPGroupKey } from '../../services/nominationPool';
import { analyzeSong, reanalyzeSongWithLyrics, SongAnalysis } from '../../services/songAnalysis';
import { getSongVerdict, setSongVerdict, batchGetVerdicts } from '../../services/songReviews';
import { batchGetFlagCounts, getFlagsForEvent, reviewFlag, blockFlagger, SongFlag } from '../../services/songFlags';
import { getEventById, getEventStartDate, generateQrCodeUrl, updateEvent, updateEventSettingFields, updateEventStart, setLastSong, resumeShow, setEndAfterLastSong, endEvent, reopenEvent } from '../../services/events';
import { useRequestWindow } from '../../hooks/useRequestWindow';
import { areRequestsClosed, djRequestCloseStatus, nextRequestsCloseAtMs, requestsCloseAtMsOf } from '../../utils/requestWindow';
import { getLinkConfidenceThreshold, saveDJDefaults } from '../../services/djDefaults';
import { useAuth } from '../../context/AuthContext';
import { useActivePlayback } from '../../context/ActivePlaybackContext';
import { useEventManageContext } from './EventManagePage';
import { linkFitsPlayMode, modeLinkFrom, modeTakesPastedLinks, needsLinkMatch, openUrlFor, playModeOf, playerNameMatchesMode, searchUrlFor } from '../../services/playModes';
import { buildPreviewTrack } from '../../utils/playerLinks';
import { formatDuration, SongSearchResult as iTunesResult } from '../../services/itunesApi';
import { searchSongs, getSongByTrackId } from '../../services/songSearch';
import { isrcForMatchedSong } from '../../utils/songIdentity';
import { resolveFromUrl } from '../../services/odesliApi';
import { canonicalLinkOrSame, cleanPastedLink, parseMusicLink } from '../../utils/songLinks';
import { isWaitingOnCoApprover, shouldRouteToCoApprover, staleCoApprovalFields } from '../../utils/coApprovalRouting';
import { buildBackToQueueUpdate, buildNowPlayingUpdate, buildPlayedToQueueUpdate, buildPlayedUpdate, buildUndoBackToQueueUpdate } from '../../utils/nowPlayingTransition';
import { allQueuedRows, autoLockCap, buildManualLockToggleUpdate, buildQueueNextUpdate, buildUnqueueUpdate, clampQueueDepth, cleanQueueRows, compareQueueLocks, decideQueueNextLock, isManualPlayMode, isQueuedInPlayer, nextPlayerSong, playerSongsInOrder, topQueueStatus } from '../../utils/playerQueueSync';
import { shouldShowPlayerMismatch } from '../../utils/playerMismatch';
import { PlayerMismatchNote } from '../../components/dj/PlayerMismatchNote';
import {
  createPreviewSession,
  getPreviewSession,
  clearPreviewResult,
  deletePreviewSession,
  generatePreviewQrUrl,
  getPreviewPageUrl,
  sendTrackToPreview,
  type PreviewSession,
} from '../../services/previewDevice';
import toast from 'react-hot-toast';
import { getUniqueVisitorCount } from '../../services/analytics';
import { useLocalFileDeck } from '../../hooks/useLocalFileDeck';
import { HEARTBEAT_INTERVAL_MS } from '../../utils/bridgeStatus';
import {
  compareChecklistOrder,
  crossfadeSecondsFor,
  localPlayerClaimStatus,
  playerSongToPrepare,
  pressPlayCountdownMs,
  shouldAutoClaim,
  type QueueLevel,
} from '../../utils/localFileLevels';
import { LocalFilePlayerStrip } from '../../components/dj/LocalFilePlayerStrip';
import { LocalFileEndCountdown } from '../../components/dj/LocalFileEndCountdown';
import LocalFileUploadModal from '../../components/dj/LocalFileUploadModal';
import DjLocalLibraryModal from '../../components/dj/DjLocalLibraryModal';
import SongMatchModal from '../../components/dj/SongMatchModal';
import { WrongSongPanel } from '../../components/dj/WrongSongPanel';
import { confidenceText, isBelowThreshold } from '../../utils/linkConfidence';
import { buildDurationWriteBack } from '../../utils/durationWriteBack';
import { difficultyBreakdown, formatDifficultyBreakdown } from '../../utils/triviaDifficulty';
import { MatchRun, MatchRunStep, startMatchRun, advanceMatchRun } from '../../utils/matchRun';
import { useCoApprovalDjActive } from '../../hooks/useCoApprovalDjActive';
import { useReviewHold } from '../../hooks/useReviewHold';
import { holderLabel, isHeldByOther } from '../../utils/reviewHold';
import { ReviewHoldBanner } from '../../components/dj/ReviewHoldBanner';
import { ReviewedByBadge } from '../../components/dj/ReviewedByBadge';
import LibraryQueueModal from '../../components/dj/LibraryQueueModal';
import {
  QUEUED_FOR_REVIEW_MESSAGE,
  getLibrarySongsByKeys,
  librarySongOf,
  resolveSpotifyLink,
  trySaveSongLink,
  type LibrarySong,
} from '../../services/songMatchMemory';
import { quickInlineCheck } from '../../services/matchIntegrity';
import { populatePracticeQueue, clearPracticeData, hasPracticeData } from '../../services/practiceMode';
import TriviaCluePanel from '../../components/games/TriviaCluePanel';
import { subscribeToPredictionAnswer, subscribeToPredictionGame } from '../../services/prediction';
import { getLockedClues, subscribeToLockedClues, type TriviaClue } from '../../services/triviaClues';
import { BRIDGE_LATEST_RELEASE_URL } from '../../config/bridgeDownloads';
import { useBridgeLauncher } from '../../hooks/useBridgeLauncher';
import { BridgeLaunchNotice } from '../../components/dj/BridgeLaunchNotice';
import { MIN_BRIDGE_VERSION, isBridgeOutdated } from '../../utils/bridgeLaunch';
import { useRecognitionFlags } from '../../hooks/useRecognitionFlags';
import { setBridgeRecognitionFlag } from '../../services/songRecognition';
import { RECOGNITION_WARNING_TOP, nextSongLockState, recognitionWarningLevel, shouldPromptRemoveFlag } from '../../utils/recognitionWarnings';
import { canFlagPick, detectedTrackFor, type RecoveryEntry, type RecoveryOutcome } from '../../utils/nowPlayingRecovery';
import { FixNowPlayingDialog } from '../../components/dj/FixNowPlayingDialog';
import { UnmatchedSongPrompt } from '../../components/dj/UnmatchedSongPrompt';
import { RecognitionWarning } from '../../components/dj/RecognitionWarning';
import { LateQueueWarning, lateQueueCardOutline } from '../../components/dj/LateQueueWarning';
import { lateQueueApplies, lateQueueStage, songEndBanner } from '../../utils/lateQueueWarning';
import { RemoveFlagPrompt } from '../../components/dj/RemoveFlagPrompt';
import { QueuePositionPrompt } from '../../components/dj/QueuePositionPrompt';
import { compareManualPositions, hasManualPosition, manualOrderAfterDrop, placeInManualOrder, refreshKeepingListOrder } from '../../utils/manualQueuePlacement';
import { DragHandle, SortableItem, SortableList } from '../../components/SortableList';
import { orderByFrozenIds } from '../../utils/sortableDrag';
import { ESTIMATE_DEFAULT_MS, knownDurationMs, resolveNowPlayingCountdown } from '../../utils/nowPlayingCountdown';
import { rankSizeOf } from '../../utils/rankingOrder';
import {
  VOTED_OUT_QUEUE_REFUSAL,
  canQueueOrLock,
  compareCrowdTier,
  crowdFavoriteLabel,
  downvoteBadgeText,
  downvoteBadgeTitle,
  isCrowdFavorite,
  isVotedOut,
  shouldForceLockOnQueue,
  splitVoteStats,
  votedOutLabel,
  voteRulesOf,
  wouldBeVotedOut,
  type VoteStats,
} from '../../utils/voteOut';
import { LAST_SONG_MIN_BRIDGE_VERSION, buildClearShowStopUpdate, isLastSongActive, lastSongBridgeSupport } from '../../utils/lastSong';
import { ShowStoppedCard } from '../../components/dj/ShowStoppedCard';
import { END_EVENT_CONFIRM_TEXT, REOPEN_EVENT_CONFIRM_TEXT } from '../../utils/endOfShow';

// Vote count filter options
const VOTE_FILTERS = [
  { value: 'all', label: 'All Votes' },
  { value: '5+', label: '5+ Votes' },
  { value: '10+', label: '10+ Votes' },
  { value: '20+', label: '20+ Votes' },
];

// Infinite scroll settings
const INITIAL_ITEMS_COUNT = 20;
const LOAD_MORE_COUNT = 20;

// Helper functions for persisting filter state
const FILTER_STORAGE_KEY = 'votebeats_queue_filters';

interface QueueFilters {
  searchQuery: string;
  voteFilter: string;
  sortDirection: 'desc' | 'asc';
}

const getPersistedFilters = (eventId: string): QueueFilters => {
  try {
    const stored = localStorage.getItem(`${FILTER_STORAGE_KEY}_${eventId}`);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore parse errors
  }
  return { searchQuery: '', voteFilter: 'all', sortDirection: 'desc' };
};

const persistFilters = (eventId: string, filters: QueueFilters) => {
  try {
    localStorage.setItem(`${FILTER_STORAGE_KEY}_${eventId}`, JSON.stringify(filters));
  } catch {
    // Ignore storage errors
  }
};

const isAwaitingDjApproval = (request: SongRequest) =>
  request.status === 'pending' && (!request.superRequest || request.poolApproved);

export default function QueuePage() {
  const { eventId } = useParams<{ eventId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const { activePlayback, lockPlayback, unlockPlayback, isLockedToOtherEvent, detectedPlayerName, sendRemoteCommand, sendSwitchEventCommand, playerMismatch, acknowledgePlayerMismatch, clearPlayerMismatch, publishQueueNextUp, deviceId, bridgeStatusFor, localFilePlayer, localFilePlayerLoaded, claimLocalFilePlayer, releaseLocalFilePlayer, beatLocalFilePlayer, playerPositionAnchorMs } = useActivePlayback();
  const bridgeStatus = bridgeStatusFor(eventId);
  const level: QueueLevel = bridgeStatus.kind === 'live-here' ? 'bridge' : 'checklist';
  const { event: eventData, setEvent: setEventData } = useEventManageContext();
  useRequestWindow(eventData?.settings);
  const isLocked = eventData?.locked ?? false;
  const playMode = playModeOf(eventData);
  const searchCatalog = playMode.searchCatalog;
  const playerLabel = playMode.playerName;
  const [switchEventConfirm, setSwitchEventConfirm] = useState<{ action: () => Promise<void>; lockedEventName: string } | null>(null);
  const [requests, setRequests] = useState<SongRequest[]>([]);
  const [overflowRequests, setOverflowRequests] = useState<SongRequest[]>([]);
  const [nowPlayingRequest, setNowPlayingRequest] = useState<SongRequest | null>(null);
  const [countdownNowMs, setCountdownNowMs] = useState(() => Date.now());
  // Persists the last non-null countdown so warning banners stay visible after
  // nowPlayingRequest clears (e.g. playback stops before warnings were dismissed)
  const lastKnownCountdownRef = useRef<number | null>(null);
  // Countdown pause: wall-clock timestamp when pause was pressed, null when running
  const [countdownPausedAt, setCountdownPausedAt] = useState<number | null>(null);
  const pauseOffsetRef = useRef<number>(0); // accumulated total paused time in ms
  const [loading, setLoading] = useState(true);

  // Load persisted filter state - URL params take priority over localStorage
  const persistedFilters = eventId ? getPersistedFilters(eventId) : { searchQuery: '', voteFilter: 'all', sortDirection: 'desc' as const };

  // Read initial values from URL query params, fallback to localStorage/defaults
  const getInitialSearch = () => searchParams.get('search') ?? persistedFilters.searchQuery;
  const getInitialVoteFilter = () => {
    const votes = searchParams.get('votes');
    if (votes && VOTE_FILTERS.some(f => f.value === votes)) {
      return votes;
    }
    return persistedFilters.voteFilter;
  };
  const getInitialSortDirection = (): 'desc' | 'asc' => {
    const sort = searchParams.get('sort');
    if (sort === 'asc' || sort === 'desc') {
      return sort;
    }
    return persistedFilters.sortDirection;
  };

  // Filter states (initialized from URL params, then localStorage)
  const [searchQuery, setSearchQuery] = useState(getInitialSearch);
  const [searchMatchIds, setSearchMatchIds] = useState<string[]>([]);
  const [searchMatchIndex, setSearchMatchIndex] = useState(0);
  const [voteFilter, setVoteFilter] = useState(getInitialVoteFilter);
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>(getInitialSortDirection);

  // Infinite scroll state
  const [visibleCount, setVisibleCount] = useState(INITIAL_ITEMS_COUNT);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  // Drag and drop states: the list holds still while a song is held
  const [frozenQueueIds, setFrozenQueueIds] = useState<string[] | null>(null);
  const [useManualOrder, setUseManualOrder] = useState(false);
  const useManualOrderRef = useRef(false);
  useManualOrderRef.current = useManualOrder;
  const requestsRef = useRef<SongRequest[]>([]);
  requestsRef.current = requests;
  const [manualRevertConfig, setManualRevertConfig] = useState<{
    songsRemaining: number;
    revertToMode: 'votes' | 'copeland' | 'fifo';
  } | null>(null);

  // DJ Notes states
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set());
  const [editingNotes, setEditingNotes] = useState<{ [key: string]: string }>({});
  const [savingNotes, setSavingNotes] = useState<Set<string>>(new Set());

  // Voters modal state
  const [votersModal, setVotersModal] = useState<{ isOpen: boolean; requestId: string | null; songTitle: string; voters: Voter[] }>({
    isOpen: false,
    requestId: null,
    songTitle: '',
    voters: []
  });
  const [voteStatsState, setVoteStatsState] = useState<
    | { status: 'idle' | 'loading' | 'error' }
    | { status: 'loaded'; stats: VoteStats; newSinceStart: number | null }
  >({ status: 'idle' });

  // AI Analysis modal state
  const [analysisModal, setAnalysisModal] = useState<{
    isOpen: boolean;
    songTitle: string;
    songArtist: string;
    analysis: SongAnalysis | null;
    loading: boolean;
    error: string | null;
    djVerdict: 'passed' | 'blocked' | null;
    verdictLoading: boolean;
  }>({
    isOpen: false,
    songTitle: '',
    songArtist: '',
    analysis: null,
    loading: false,
    error: null,
    djVerdict: null,
    verdictLoading: false,
  });
  const [lyricsPaste, setLyricsPaste] = useState({ text: '', loading: false });
  const [songVerdicts, setSongVerdicts] = useState<Map<string, 'passed' | 'blocked'>>(new Map());
  const [ignoredBlockedIds, setIgnoredBlockedIds] = useState<Set<string>>(new Set());
  const [showBlockedReviewModal, setShowBlockedReviewModal] = useState(false);
  // Song flagging state
  const [flagCounts, setFlagCounts] = useState<Map<string, number>>(new Map());
  const [flagReviewModal, setFlagReviewModal] = useState<{ requestId: string; songTitle: string; songArtist: string } | null>(null);
  const [flagsForReview, setFlagsForReview] = useState<SongFlag[]>([]);

  // People's Pick nomination tracking
  const [nominationGroups, setNominationGroups] = useState<Array<{ title: string; artist: string; albumArtUrl?: string; count: number; threshold: number; requestIds: string[]; primaryRequestId: string }>>([]);
  const [nominationPanelExpanded, setNominationPanelExpanded] = useState(false);
  const [poolUpvoteCounts, setPoolUpvoteCounts] = useState<Map<string, number>>(new Map());
  const notifiedNominationsRef = useRef<Set<string>>(new Set());
  const eventDataRef = useRef(eventData);
  eventDataRef.current = eventData;

  // Ranked choice voting states
  const [isRankedMode, setIsRankedMode] = useState(false);
  const [scoringMode, setScoringMode] = useState<'consensus' | 'discovery'>('consensus');
  // DJ-local sort mode switcher (UI-only, not persisted to Firestore)
  const [djSortMode, setDjSortMode] = useState<'votes' | 'copeland' | 'fifo'>('votes');
  const [consensusScores, setConsensusScores] = useState<Map<string, number>>(new Map());
  const [discoveryScores, setDiscoveryScores] = useState<Map<string, number>>(new Map());
  const [hiddenGems, setHiddenGems] = useState<Array<{ requestId: string; consensusRank: number; discoveryRank: number; delta: number }>>([]);
  const [rankingStats, setRankingStats] = useState<{ totalRankings: number; uniqueVoters: number; averageRankedItems: number } | null>(null);
  const [thresholdMet, setThresholdMet] = useState(false);
  const [minParticipants, setMinParticipants] = useState(3);
  const [staleCount, setStaleCount] = useState(0);
  const cachedScoresArrivedRef = useRef(false);

  // Trending status for each request (computed async)
  const [trendingIds, setTrendingIds] = useState<Set<string>>(new Set());

  // Live attendee stats
  const [attendeeStats, setAttendeeStats] = useState<{ visitors: number; requesters: number; voters: number; rankers: number }>({ visitors: 0, requesters: 0, voters: 0, rankers: 0 });

  // Pending requests (incoming)
  const [pendingRequests, setPendingRequests] = useState<SongRequest[]>([]);
  // Songs waiting on the co-approver are out of the DJ's hands, unless co-approval has since been switched off
  const isCoApprovalOn = eventData?.settings?.coApprovalEnabled ?? false;
  const actionablePendingRequests = useMemo(
    () => pendingRequests.filter(r => !isWaitingOnCoApprover(isCoApprovalOn, r.coApprovalStatus)),
    [pendingRequests, isCoApprovalOn]
  );
  // A co-approval DJ reviews the same pending songs; each side reserves a song while its pop-up is open (ITER-194)
  const coApprovalDjActive = useCoApprovalDjActive(eventId, user?.uid);
  const reviewHold = useReviewHold({
    eventId,
    enabled: coApprovalDjActive,
    onExpired: () => {
      handleCloseMatchModal();
      toast('Your hold ran out — the song is back on the list', { icon: '⏱️' });
    },
  });

  // The prediction answer is kept next from its DJ-only doc, never from a public lockedNext flag (ITER-118).
  // Follows the live game: the event context's prediction settings aren't kept live after a game is created.
  const [pinnablePredictionGameId, setPinnablePredictionGameId] = useState<string | null>(null);
  const [predictionPinnedId, setPredictionPinnedId] = useState<string | null>(null);
  useEffect(() => {
    if (!eventId) return;
    return subscribeToPredictionGame(eventId, (game) => {
      const pinnable = game && game.answerLocked && (game.status === 'ready' || game.status === 'active');
      setPinnablePredictionGameId(pinnable ? game.id : null);
    });
  }, [eventId]);
  useEffect(() => {
    if (!pinnablePredictionGameId) {
      setPredictionPinnedId(null);
      return;
    }
    return subscribeToPredictionAnswer(pinnablePredictionGameId, (answer) => setPredictionPinnedId(answer?.requestId ?? null));
  }, [pinnablePredictionGameId]);
  const isPinnedNext = (request: SongRequest): boolean => Boolean(request.lockedNext) || request.id === predictionPinnedId;
  const [pendingCollapsed, setPendingCollapsed] = useState(false);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [rejectingRequest, setRejectingRequest] = useState<SongRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Played songs section
  const [playedRequests, setPlayedRequests] = useState<SongRequest[]>([]);
  const [playedCollapsed, setPlayedCollapsed] = useState(true);

  // Link library trust (ITER-139): Wrong song panel, clean-use counts on the player chip
  const [wrongSongPanel, setWrongSongPanel] = useState<{ requestId: string; mode: 'report' | 'fix' } | null>(null);
  const [fixDialog, setFixDialog] = useState<{ entry: RecoveryEntry; noFlag?: boolean } | null>(null);
  const [flagBusy, setFlagBusy] = useState(false);
  const [dismissedRemovePrompts, setDismissedRemovePrompts] = useState<Set<string>>(new Set());
  const [libraryStats, setLibraryStats] = useState<Map<string, LibrarySong>>(new Map());
  const [confidenceThreshold, setConfidenceThreshold] = useState(2);

  // Queue refresh
  const [refreshing, setRefreshing] = useState(false);

  // QR code modal
  const [showQrModal, setShowQrModal] = useState(false);
  const [showQrPrint, setShowQrPrint] = useState(false);

  // NFC tag writing
  const nfcSupported = 'NDEFReader' in window;
  const [nfcWriting, setNfcWriting] = useState(false);
  const [nfcLockTag, setNfcLockTag] = useState(false);
  const nfcAbortRef = useRef<AbortController | null>(null);

  // Song Match Modal state (platform-agnostic)
  const [matchingRequest, setMatchingRequest] = useState<SongRequest | null>(null);
  const [matchInitialLinkUrl, setMatchInitialLinkUrl] = useState<string | undefined>(undefined);

  // Library Queue Modal state
  const [showLibraryQueueModal, setShowLibraryQueueModal] = useState(false);

  // Trivia curation panel state
  const [curationRequest, setCurationRequest] = useState<SongRequest | null>(null);
  const [lockedClueIds, setLockedClueIds] = useState<Set<string>>(new Set());
  const [lockedClues, setLockedClues] = useState<TriviaClue[]>([]);
  const [movingBackIds, setMovingBackIds] = useState<Set<string>>(new Set());
  const [positionPrompt, setPositionPrompt] = useState<{ songTitle: string; place: (position: number) => Promise<void> } | null>(null);
  const [placingPosition, setPlacingPosition] = useState(false);
  const curatedPlayedCount = playedRequests.filter(r => lockedClueIds.has(r.id)).length;
  const playedIds = new Set(playedRequests.map(r => r.id));
  const playedClueBreakdown = difficultyBreakdown(lockedClues.filter(c => playedIds.has(c.songRequestId)));
  const prevTriviaEnabled = useRef(eventData?.settings?.triviaEnabled ?? false);

  // Preview Device state
  const [previewSession, setPreviewSession] = useState<PreviewSession | null>(null);
  const [showPreviewQr, setShowPreviewQr] = useState(false);
  const [previewResultUrl, setPreviewResultUrl] = useState<string | null>(null);

  // Local Files modal state
  const [showLocalFileUpload, setShowLocalFileUpload] = useState(false);
  const [showLocalLibrary, setShowLocalLibrary] = useState(false);

  // Paste track URL inline state (keyed by request ID)
  const [pasteUrlRequestId, setPasteUrlRequestId] = useState<string | null>(null);
  const [pasteUrlValue, setPasteUrlValue] = useState('');
  const [pasteUrlLoading, setPasteUrlLoading] = useState(false);

  // Inline duration editor state
  const [editingDurationId, setEditingDurationId] = useState<string | null>(null);
  const [durationEditValue, setDurationEditValue] = useState('');
  const [durationEditSaving, setDurationEditSaving] = useState(false);
  const [durationDismissedForEvent, setDurationDismissedForEvent] = useState(() =>
    sessionStorage.getItem('votebeats_duration_verify_dismissed') === 'true'
  );
  const { state: bridgeLaunchState, launch: launchBridge, recheck: recheckBridge, dismiss: dismissBridgeLaunch } = useBridgeLauncher(eventId, eventId);

  // F7: Auto-open next pending match modal tracking
  const [pendingMatchIndex, setPendingMatchIndex] = useState(0);
  const [pendingMatchTotal, setPendingMatchTotal] = useState(0);
  const [matchRun, setMatchRun] = useState<MatchRun | null>(null);

  // Clean Queue modal state
  const [showCleanQueueModal, setShowCleanQueueModal] = useState(false);
  const [cleaningQueueIds, setCleaningQueueIds] = useState<Set<string>>(new Set());

  // Queue depth — from event settings with fallback to 1
  const queueDepth = eventData?.settings?.queueDepth ?? 1;
  const lockWhenQueued = eventData?.settings?.lockWhenQueued ?? false;
  const [editingQueueDepth, setEditingQueueDepth] = useState(false);
  const [queueDepthDraft, setQueueDepthDraft] = useState(queueDepth);
  // Queue depth and "Lock songs when queued" share one "this event / all future events" confirm
  const [pendingQueueSetting, setPendingQueueSetting] = useState<
    { key: 'queueDepth'; value: number } | { key: 'lockWhenQueued'; value: boolean } | null
  >(null);

  // Practice Mode state
  const [showPracticeMenu, setShowPracticeMenu] = useState(false);
  const [practiceLoading, setPracticeLoading] = useState(false);
  const [hasPracticeRequests, setHasPracticeRequests] = useState(false);
  const [simulateCount, setSimulateCount] = useState(2);

  // F3: DJ Add Song modal state
  const [showAddSongModal, setShowAddSongModal] = useState(false);
  const [showLimitRaiseModal, setShowLimitRaiseModal] = useState(false);
  const [addSongQuery, setAddSongQuery] = useState('');
  const [addSongResults, setAddSongResults] = useState<iTunesResult[]>([]);
  const [addSongLoading, setAddSongLoading] = useState(false);
  const [addingSongId, setAddingSongId] = useState<string | null>(null);

  // Inline end time & allowOverlap editor state
  const [showEndTimeEditor, setShowEndTimeEditor] = useState(false);
  const [editEndTime, setEditEndTime] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [editAllowOverlap, setEditAllowOverlap] = useState(false);

  // Inline start time editor state
  const [showStartTimeEditor, setShowStartTimeEditor] = useState(false);
  const [editStartTime, setEditStartTime] = useState('');
  const [editStartDate, setEditStartDate] = useState('');

  // Live clock for current time display
  const [currentTime, setCurrentTime] = useState(new Date());

  // Warning banners: sessionStorage keys for per-event, per-session dismissal
  // (persists across navigations within the same tab, clears when tab/browser closes)
  const WARNING_30S_KEY = eventId ? `votebeats_warn30_${eventId}` : null;
  const WARNING_45S_KEY = eventId ? `votebeats_warn45_${eventId}` : null;
  const [warn30Dismissed, setWarn30Dismissed] = useState(() =>
    WARNING_30S_KEY ? sessionStorage.getItem(WARNING_30S_KEY) === 'dismissed' : false
  );
  const [warn30DismissedForCurrentSong, setWarn30DismissedForCurrentSong] = useState(false);
  const [warn45Dismissed, setWarn45Dismissed] = useState(() =>
    WARNING_45S_KEY ? sessionStorage.getItem(WARNING_45S_KEY) === 'dismissed' : false
  );
  const dismissWarn30 = () => {
    if (WARNING_30S_KEY) sessionStorage.setItem(WARNING_30S_KEY, 'dismissed');
    setWarn30Dismissed(true);
  };
  const dismissWarn45 = () => {
    if (WARNING_45S_KEY) sessionStorage.setItem(WARNING_45S_KEY, 'dismissed');
    setWarn45Dismissed(true);
  };
  const restoreSongEndWarnings = () => {
    if (WARNING_30S_KEY) sessionStorage.removeItem(WARNING_30S_KEY);
    if (WARNING_45S_KEY) sessionStorage.removeItem(WARNING_45S_KEY);
    setWarn30Dismissed(false);
    setWarn45Dismissed(false);
    setWarn30DismissedForCurrentSong(false);
  };

  // Reset per-song dismiss when the now-playing song changes
  useEffect(() => {
    setWarn30DismissedForCurrentSong(false);
  }, [nowPlayingRequest?.id]);

  useEffect(() => {
    if (!user?.uid) return;
    getLinkConfidenceThreshold(user.uid).then(setConfidenceThreshold);
  }, [user?.uid]);

  const libraryKeysJoined = [...requests, ...playedRequests, ...(nowPlayingRequest ? [nowPlayingRequest] : [])]
    .map(request => request.linkLibraryKey)
    .filter((key): key is string => Boolean(key))
    .sort()
    .filter((key, index, keys) => keys.indexOf(key) === index)
    .join(',');

  useEffect(() => {
    if (!libraryKeysJoined) return;
    getLibrarySongsByKeys(libraryKeysJoined.split(','))
      .then(setLibraryStats)
      .catch((err: unknown) => console.error('[QueuePage] Loading link-library counts failed:', err));
  }, [libraryKeysJoined]);

  // Event countdown state
  const [eventCountdown, setEventCountdown] = useState('');

  // Helper: format 24h "HH:MM" to 12h "h:MM AM/PM"
  const formatTime12h = (time24: string): string => {
    const [h, m] = time24.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  // Helper: format "YYYY-MM-DD" to short display like "Fri 2/20"
  const formatDateShort = (dateStr: string): string => {
    const [yr, mo, dy] = dateStr.split('-').map(Number);
    const d = new Date(yr, mo - 1, dy);
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return `${days[d.getDay()]} ${mo}/${dy}`;
  };

  // Helper: format date + time for display like "Fri 2/20 7:00 PM"
  const formatDateTime = (dateStr: string, timeStr: string): string => {
    return `${formatDateShort(dateStr)} ${formatTime12h(timeStr)}`;
  };

  // Event countdown timer — updates every 30s (or every 1s under 10 min)
  useEffect(() => {
    if (!eventData?.endTime) {
      setEventCountdown('');
      return;
    }

    const computeCountdown = () => {
      const now = new Date();
      const [endH, endM] = eventData.endTime!.split(':').map(Number);
      const edStr = eventData.endDate || eventData.date;
      const [eyr, emo, edy] = edStr.split('-').map(Number);
      const endDate = new Date(eyr, emo - 1, edy, endH, endM, 0, 0);
      const diffMs = endDate.getTime() - now.getTime();
      if (diffMs <= 0) {
        setEventCountdown('Event ended');
        return diffMs;
      }
      const totalSec = Math.floor(diffMs / 1000);
      const hrs = Math.floor(totalSec / 3600);
      const mins = Math.floor((totalSec % 3600) / 60);
      const secs = totalSec % 60;
      if (diffMs <= 600000) {
        // Under 10 min: show mm:ss
        setEventCountdown(`${mins}:${secs.toString().padStart(2, '0')} left`);
      } else {
        // Over 10 min: show Xh Ym left
        setEventCountdown(hrs > 0 ? `${hrs}h ${mins}m left` : `${mins}m left`);
      }
      return diffMs;
    };

    const diffMs = computeCountdown();
    // Under 10 min → tick every second, otherwise every 30s
    const interval = (diffMs !== undefined && diffMs <= 600000) ? 1000 : 30000;
    const timer = setInterval(computeCountdown, interval);
    return () => clearInterval(timer);
  }, [eventData?.endTime, eventData?.endDate, eventData?.date]);

  // Live clock: update current time every 30s
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 30000);
    return () => clearInterval(timer);
  }, []);

  // Get the sort value for a request based on active DJ sort mode
  const getSortValue = (r: SongRequest): number => {
    switch (djSortMode) {
      case 'copeland': {
        // Fall back to vote-based sorting until threshold is met
        if (!thresholdMet) return r.voteCount;
        const scores = scoringMode === 'consensus' ? consensusScores : discoveryScores;
        return scores.get(r.id) || 0;
      }
      case 'fifo':
        // Negative so desc = oldest first (true FIFO)
        return -new Date(r.createdAt).getTime();
      case 'votes':
      default:
        return r.voteCount;
    }
  };

  // Comparator that applies getSortValue with a voteCount tiebreaker when scores are equal.
  const compareBySort = (a: SongRequest, b: SongRequest): number => {
    const diff = sortDirection === 'desc'
      ? getSortValue(b) - getSortValue(a)
      : getSortValue(a) - getSortValue(b);
    if (diff !== 0) return diff;
    return sortDirection === 'desc'
      ? b.voteCount - a.voteCount
      : a.voteCount - b.voteCount;
  };

  // Pure sort — NO React state setters inside. Locked songs float to the top and
  // are immune to vote/copeland displacement. Manual order (drag-drop) takes priority
  // over locked-sort when both exist, but locked songs are still undraggable.
  const sortRequests = (reqs: SongRequest[]): SongRequest[] => {
    const hasManualOrder = reqs.some(hasManualPosition);
    if (hasManualOrder) {
      return [...reqs].sort((a, b) => {
        // Crowd favorites first and voted-out songs last, even over the manual order
        const byCrowd = compareCrowdTier(a, b);
        if (byCrowd !== 0) return byCrowd;
        const byPosition = compareManualPositions(a, b);
        if (byPosition !== 0) return byPosition;
        return compareBySort(a, b);
      });
    }
    return [...reqs].sort((a, b) => {
      // Locked songs float to the top; preserve relative order among locked songs
      const aPinned = isPinnedNext(a);
      const bPinned = isPinnedNext(b);
      if (aPinned && !bPinned) return -1;
      if (!aPinned && bPinned) return 1;
      if (aPinned && bPinned) return compareQueueLocks(a, b);
      // Crowd favorites come right after locked songs; voted-out songs go to the bottom
      const byCrowd = compareCrowdTier(a, b);
      if (byCrowd !== 0) return byCrowd;
      return compareBySort(a, b);
    });
  };

  const sortRequestsRef = useRef(sortRequests);
  sortRequestsRef.current = sortRequests;

  // Sync useManualOrder flag separately — never inside sortRequests
  useEffect(() => {
    setUseManualOrder(requests.some(hasManualPosition));
  }, [requests]);

  useEffect(() => {
    const loadData = async () => {
      if (eventId) {
        // Get all requests for this event
        const allEventRequests = await getRequestsByEventId(eventId);

        // Separate queued, pending, and played
        const eventRequests = allEventRequests.filter(r => r.status === 'queued');
        setRequests(sortRequests(eventRequests));

        const pending = allEventRequests.filter(isAwaitingDjApproval);
        setPendingRequests(pending);

        const played = allEventRequests.filter(r => r.status === 'played');
        setPlayedRequests(played);

        // Get currently playing song
        const nowPlaying = await getNowPlaying(eventId);
        setNowPlayingRequest(nowPlaying);

        // Compute trending status for all queued requests
        const trendingResults = await Promise.all(
          eventRequests.map(async (r) => ({ id: r.id, trending: await isTrending(r.id) }))
        );
        setTrendingIds(new Set(trendingResults.filter(r => r.trending).map(r => r.id)));

        // Load Copeland scores from cache (real-time listener handles updates)
        const savedMode = eventData?.settings?.rankedScoringMode || 'consensus';
        setScoringMode(savedMode);

        const threshold = eventData?.settings?.rankingMinParticipants ?? 3;
        setMinParticipants(threshold);

        // Scores will be populated by the real-time subscribeToCachedCopelandScores listener
        // Fallback: if no cached scores exist yet, compute them once
        const stalenessMin = eventData?.settings?.rankingStalenessMinutes ?? 0;
        const eventRankSize = rankSizeOf(eventData?.settings);
        if (!cachedScoresArrivedRef.current) {
          const bothScores = await calculateBothCopelandScoresWithStaleness(eventId, stalenessMin, eventRankSize);
          if (!cachedScoresArrivedRef.current) {
            setConsensusScores(bothScores.consensus);
            setDiscoveryScores(bothScores.discovery);
            setStaleCount(bothScores.staleCount);
          }
        }

        const gems = await findHiddenGems(eventId, eventRankSize, 1);
        setHiddenGems(gems);

        // Load existing preview session
        // ProtectedRoute only renders this page once a DJ user is signed in
        const existingPreview = user?.uid ? await getPreviewSession(eventId, user.uid) : null;
        if (existingPreview) setPreviewSession(existingPreview);

        // People's Pick nominations — populate on initial load
        const currentSettings = eventDataRef.current?.settings;
        if (currentSettings?.superRequestEnabled) {
          const superReqs = allEventRequests.filter(r => r.superRequest && r.status === 'pending' && r.poolApproved);
          const groups: Array<{ title: string; artist: string; albumArtUrl?: string; oderIds: Set<string>; requestIds: string[] }> = [];
          for (const req of superReqs) {
            const existingGroup = groups.find(g => isFuzzySongMatch(g.title, g.artist, req.song.title, req.song.artist));
            if (existingGroup) {
              existingGroup.oderIds.add(req.requestedBy.oderId);
              existingGroup.requestIds.push(req.id);
            } else {
              groups.push({ title: req.song.title, artist: req.song.artist, albumArtUrl: req.song.albumArtUrl, oderIds: new Set([req.requestedBy.oderId]), requestIds: [req.id] });
            }
          }
          const totalAttendees = new Set(allEventRequests.map(r => r.requestedBy.oderId)).size;
          const thresholdType = currentSettings.superRequestThresholdType ?? 'count';
          const rawThreshold = thresholdType === 'percentage'
            ? (currentSettings.superRequestThresholdPercent ?? 10)
            : (currentSettings.superRequestThresholdCount ?? 5);
          const effectiveThreshold = thresholdType === 'percentage'
            ? Math.max(2, Math.ceil((rawThreshold / 100) * totalAttendees))
            : rawThreshold;
          setNominationGroups(groups.map(g => ({ title: g.title, artist: g.artist, albumArtUrl: g.albumArtUrl, count: g.oderIds.size, threshold: effectiveThreshold, requestIds: g.requestIds, primaryRequestId: g.requestIds[0] })));
        }
      }
      setLoading(false);
    };
    loadData();
  }, [eventId]);

  // Real-time listener: update nowPlayingRequest when desktop helper (or any client) changes status
  useEffect(() => {
    if (!eventId) return;
    let prevNowPlayingId: string | null = null;
    const unsub = subscribeToNowPlaying(eventId, (nowPlaying) => {
      setNowPlayingRequest(nowPlaying);
      if (nowPlaying) {
        // Remove from queue list if it just became nowPlaying
        setRequests((prev) => prev.filter((r) => r.id !== nowPlaying.id));
        prevNowPlayingId = nowPlaying.id;
      } else if (prevNowPlayingId) {
        // Previous song moved to played — refresh played list
        getRequestsByEventId(eventId).then((all) => {
          setPlayedRequests(all.filter((r) => r.status === 'played'));
        }).catch(() => {});
        prevNowPlayingId = null;
      }
    });
    return unsub;
  }, [eventId]);

  // Reset pause offset when a new song starts playing
  useEffect(() => {
    setCountdownPausedAt(null);
    pauseOffsetRef.current = 0;
  }, [nowPlayingRequest?.id]);

  // Mid-show enable: when triviaEnabled flips to true, notify DJ about uncurated played songs
  useEffect(() => {
    const current = eventData?.settings?.triviaEnabled ?? false;
    if (current && !prevTriviaEnabled.current && eventId && playedRequests.length > 0) {
      getLockedClues(eventId).then(lockedClues => {
        const lockedIds = new Set(lockedClues.map(c => c.songRequestId));
        const uncurated = playedRequests.filter(r => !lockedIds.has(r.id));
        if (uncurated.length > 0) {
          toast(`${uncurated.length} played song${uncurated.length !== 1 ? 's' : ''} haven't been curated yet — open the Played section to add clues.`, { icon: '🧠' });
        }
      }).catch(() => {});
    }
    prevTriviaEnabled.current = current;
  }, [eventData?.settings?.triviaEnabled]);

  // Track which played songs have a locked clue
  useEffect(() => {
    if (!eventId) return;
    const unsub = subscribeToLockedClues(eventId, (clues) => {
      setLockedClueIds(new Set(clues.map(c => c.songRequestId)));
      setLockedClues(clues);
    });
    return unsub;
  }, [eventId]);

  const countdownTicks = level === 'bridge' && nowPlayingRequest !== null && !nowPlayingRequest.localFile;
  useEffect(() => {
    if (!countdownTicks) return;
    setCountdownNowMs(Date.now());
    const timer = setInterval(() => setCountdownNowMs(Date.now()), 1000);
    return () => clearInterval(timer);
  }, [countdownTicks]);

  const awaitingOffTrackChoice = Boolean(
    nowPlayingRequest && playerMismatch?.noAdvance && shouldShowPlayerMismatch(playerMismatch, eventId, nowPlayingRequest.id),
  );
  const countdown = resolveNowPlayingCountdown({
    level,
    nowPlaying: nowPlayingRequest ? {
      id: nowPlayingRequest.id,
      durationMs: nowPlayingRequest.song.durationMs,
      startedPlaying: nowPlayingRequest.timestamps?.startedPlaying ?? null,
      isLocalFile: Boolean(nowPlayingRequest.localFile),
      manualPick: Boolean(nowPlayingRequest.manualPickAt),
      awaitingOffTrackChoice,
    } : null,
    position: activePlayback?.playerPosition ?? null,
    positionAnchorMs: playerPositionAnchorMs,
    estimatePausedAtMs: countdownPausedAt,
    estimatePauseOffsetMs: pauseOffsetRef.current,
    nowMs: countdownNowMs,
  });
  const nowPlayingCountdown = countdown.kind === 'running' ? countdown.remainingMs : null;

  useEffect(() => {
    if (level === 'checklist') {
      lastKnownCountdownRef.current = null;
      return;
    }
    if (nowPlayingCountdown !== null) lastKnownCountdownRef.current = nowPlayingCountdown;
  }, [level, nowPlayingCountdown]);

  // The song-end warnings follow the countdown, and stay quiet while the off-track prompt is waiting
  const songEndCountdown = awaitingOffTrackChoice ? null : nowPlayingCountdown ?? lastKnownCountdownRef.current;

  // Last Song (ITER-175): saved on the event; Bridge pauses the player when the marked song ends.
  // While it plays, and once the show has stopped, nothing nags the DJ to queue the next song.
  const lastSongActive = isLastSongActive(eventData, nowPlayingRequest?.id);
  const lastSongSupport = lastSongBridgeSupport(level, activePlayback?.bridgeVersion);
  const nextSongWarningsQuiet = lastSongActive || Boolean(eventData?.showStop);

  const songEndedNoAdvance = (() => {
    const cd = songEndCountdown;
    return level === 'bridge' && !nowPlayingRequest?.localFile && !nextSongWarningsQuiet && cd !== null && cd <= 0;
  })();

  // Sync derived state when event settings change (e.g., DJ saves on Settings tab)
  useEffect(() => {
    if (!eventData) return;
    const rankedMode = eventData.settings?.queueMode === 'ranked';
    setIsRankedMode(rankedMode);
    const eventQueueMode = eventData.settings?.queueMode || 'standard';
    setDjSortMode(eventQueueMode === 'ranked' ? 'copeland' : eventQueueMode === 'fifo' ? 'fifo' : 'votes');
  }, [eventData?.settings?.queueMode]);

  // Auto-reset attendeeVoteTab when DJ switches away from vote sorting on a ranked event
  useEffect(() => {
    if (djSortMode !== 'votes' && isRankedMode && eventData?.settings?.attendeeVoteTab && eventId) {
      const newSettings = { ...eventData.settings, attendeeVoteTab: false };
      setEventData({ ...eventData, settings: newSettings });
      updateEvent(eventId, { settings: newSettings });
    }
  }, [djSortMode]);

  // Populate People's Pick nominations when eventData first becomes available (or settings change)
  useEffect(() => {
    if (!eventId || !eventData?.settings?.superRequestEnabled) return;
    (async () => {
      const allRequests = await getRequestsByEventId(eventId);
      const superReqs = allRequests.filter(r => r.superRequest && r.status === 'pending' && r.poolApproved);
      const groups: Array<{ title: string; artist: string; albumArtUrl?: string; oderIds: Set<string>; requestIds: string[] }> = [];
      for (const req of superReqs) {
        const existingGroup = groups.find(g => isFuzzySongMatch(g.title, g.artist, req.song.title, req.song.artist));
        if (existingGroup) {
          existingGroup.oderIds.add(req.requestedBy.oderId);
          existingGroup.requestIds.push(req.id);
        } else {
          groups.push({ title: req.song.title, artist: req.song.artist, albumArtUrl: req.song.albumArtUrl, oderIds: new Set([req.requestedBy.oderId]), requestIds: [req.id] });
        }
      }
      const totalAttendees = new Set(allRequests.map(r => r.requestedBy.oderId)).size;
      const thresholdType = eventData.settings.superRequestThresholdType ?? 'count';
      const rawThreshold = thresholdType === 'percentage'
        ? (eventData.settings.superRequestThresholdPercent ?? 10)
        : (eventData.settings.superRequestThresholdCount ?? 5);
      const effectiveThreshold = thresholdType === 'percentage'
        ? Math.max(2, Math.ceil((rawThreshold / 100) * totalAttendees))
        : rawThreshold;
      setNominationGroups(groups.map(g => ({ title: g.title, artist: g.artist, albumArtUrl: g.albumArtUrl, count: g.oderIds.size, threshold: effectiveThreshold, requestIds: g.requestIds, primaryRequestId: g.requestIds[0] })));
    })();
  }, [eventId, eventData?.settings?.superRequestEnabled]);

  // Preview Device: poll for results from preview phone
  useEffect(() => {
    if (!previewSession?.id || !eventId || !user?.uid) return;

    const pollPreview = setInterval(async () => {
      try {
        const session = await getPreviewSession(eventId, user.uid);
        if (!session) return;

        // Update connection status
        if (session.status !== previewSession.status) {
          setPreviewSession(session);
        }

        // Check for incoming result — route through SongMatchModal for Odesli resolution
        if (session.result && matchingRequest) {
          if (session.result.requestId !== matchingRequest.id) return; // Wrong request

          if (session.result.status === 'found' && !modeTakesPastedLinks(playMode)) {
            await clearPreviewResult(session.id);
          } else if (session.result.status === 'found' && session.result.link) {
            setPreviewResultUrl(session.result.link);
            toast.success('URL received from preview device — resolving...');
            await clearPreviewResult(session.id);
          } else if (session.result.status === 'not_found') {
            toast('Preview device: Track not found', { icon: '❌' });
            await clearPreviewResult(session.id);
          }
        }
      } catch {
        // Polling error — will retry
      }
    }, 2000);

    return () => clearInterval(pollPreview);
  }, [previewSession?.id, previewSession?.status, eventId, user?.uid, matchingRequest?.id, playMode]);

  // End time & allowOverlap inline editor handlers
  const handleOpenEndTimeEditor = () => {
    setEditEndTime(eventData?.endTime || '');
    setEditEndDate(eventData?.endDate || eventData?.date || '');
    setEditAllowOverlap(eventData?.settings?.allowOverlap ?? false);
    setShowEndTimeEditor(true);
  };

  const handleSaveEndTime = async () => {
    if (!eventId || !eventData) return;
    try {
      const updates: Record<string, any> = {};
      if (editEndTime) {
        updates.endTime = editEndTime;
        updates.endDate = editEndDate || eventData.date;
      } else {
        updates.endTime = null;
        updates.endDate = null;
      }
      updates.settings = { ...eventData.settings, allowOverlap: editAllowOverlap };
      const updated = await updateEvent(eventId, updates);
      setEventData(updated);
      setShowEndTimeEditor(false);
      toast.success(editEndTime ? `End time set to ${formatTime12h(editEndTime)}` : 'End time removed');
    } catch {
      toast.error('Failed to update end time');
    }
  };

  // Start time inline editor handlers
  const handleOpenStartTimeEditor = () => {
    setEditStartTime(eventData?.startTime || '');
    setEditStartDate(eventData?.startDate || eventData?.date || '');
    setShowStartTimeEditor(true);
  };

  const handleSaveStartTime = async () => {
    if (!eventId || !eventData || !editStartTime) return;
    try {
      const startDate = editStartDate || eventData.date;
      const closeAtStart = eventData.settings.closeRequestsAtStart === true;
      const requestsCloseAtMs = closeAtStart
        ? nextRequestsCloseAtMs({
            closeAtStart,
            startMs: getEventStartDate({ date: eventData.date, startDate, startTime: editStartTime })?.getTime() ?? null,
            previous: {
              closeAtStart,
              startMs: getEventStartDate(eventData)?.getTime() ?? null,
              closeAtMs: requestsCloseAtMsOf(eventData.settings),
            },
          })
        : undefined;
      const updated = await updateEventStart(eventId, { startTime: editStartTime, startDate, requestsCloseAtMs });
      setEventData(updated);
      setShowStartTimeEditor(false);
      toast.success(`Start time set to ${formatTime12h(editStartTime)}`);
    } catch {
      toast.error('Failed to update start time');
    }
  };

  const handleDeleteRequest = async (requestId: string, songTitle: string) => {
    if (!confirm(`Are you sure you want to remove "${songTitle}" from the queue?`)) {
      return;
    }

    try {
      await deleteRequest(requestId);
      setRequests(requests.filter(r => r.id !== requestId));
      toast.success('Song removed from queue');
    } catch {
      toast.error('Failed to remove song');
    }
  };

  const handleClearFilters = () => {
    setSearchQuery('');
    setVoteFilter('all');
    toast.success('Filters cleared');
  };

  // Toggle voting open/closed from the queue page
  type OpenCloseMode = 'all-open' | 'requests-closed' | 'all-closed';
  const getCurrentMode = (): OpenCloseMode => {
    if (!eventData) return 'all-open';
    if (eventData.settings.votingClosed) return 'all-closed';
    if (areRequestsClosed(eventData.settings, Date.now())) return 'requests-closed';
    return 'all-open';
  };

  const requestCloseStatus = eventData
    ? djRequestCloseStatus(eventData.settings, getEventStartDate(eventData)?.getTime() ?? null, Date.now())
    : null;

  const handleSetMode = async (mode: OpenCloseMode) => {
    if (!eventData || !user) return;
    const votingClosed = mode === 'all-closed';
    const requestsOpen = mode === 'all-open';
    const reopenFields = requestsOpen ? { requestsCloseAtMs: null } : {};
    try {
      await updateEvent(eventData.id, {
        settings: {
          ...eventData.settings,
          votingClosed,
          requestsOpen,
          ...reopenFields,
          votingClosedAt: votingClosed ? new Date().toISOString() : undefined,
        }
      });
      setEventData(prev => prev ? {
        ...prev,
        settings: {
          ...prev.settings,
          votingClosed,
          requestsOpen,
          ...reopenFields,
          votingClosedAt: votingClosed ? new Date().toISOString() : undefined,
        }
      } : null);
      const messages = {
        'all-open': 'Requests and voting are open',
        'requests-closed': 'Requests closed — voting still open',
        'all-closed': 'Voting and requests paused',
      };
      toast.success(messages[mode]);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update mode');
    }
  };

  // View voters for a request
  const handleViewVoters = async (requestId: string, songTitle: string) => {
    const voters = await getVotersForRequest(requestId);
    setVotersModal({
      isOpen: true,
      requestId,
      songTitle,
      voters
    });
    setVoteStatsState({ status: 'loading' });
    try {
      const start = getEventStartDate(eventData);
      const [breakdown, newSinceStart] = await Promise.all([
        getVoteBreakdown(requestId),
        start && eventId ? countParticipantsSince(eventId, start) : Promise.resolve(null),
      ]);
      setVoteStatsState({ status: 'loaded', stats: splitVoteStats(breakdown, start), newSinceStart });
    } catch (err) {
      console.error('Failed to load vote stats for request', requestId, err);
      setVoteStatsState({ status: 'error' });
    }
  };

  const handleCloseVotersModal = () => {
    setVotersModal({
      isOpen: false,
      requestId: null,
      songTitle: '',
      voters: []
    });
    setVoteStatsState({ status: 'idle' });
  };

  // Batch-load DJ verdicts for all visible songs (including played)
  useEffect(() => {
    const allSongs = [
      ...pendingRequests.map(r => ({ title: r.song.title, artist: r.song.artist })),
      ...requests.map(r => ({ title: r.song.title, artist: r.song.artist })),
      ...playedRequests.map(r => ({ title: r.song.title, artist: r.song.artist })),
    ];
    if (allSongs.length === 0) return;
    (async () => {
      const verdicts = await batchGetVerdicts(allSongs);
      setSongVerdicts(verdicts);
    })();
  }, [pendingRequests, requests, playedRequests]);

  // Load flag counts when event has flagging enabled
  useEffect(() => {
    if (!eventId || !eventData?.settings?.allowAttendeeFlagging) return;
    (async () => {
      const counts = await batchGetFlagCounts(eventId);
      setFlagCounts(counts);
    })();
  }, [eventId, eventData?.settings?.allowAttendeeFlagging, requests]);

  const undoBackToQueue = async (requestId: string, previous: Pick<SongRequest, 'timestamps' | 'manualOrder'>) => {
    try {
      const fresh = await getRequestById(requestId);
      if (!fresh || fresh.status !== 'queued') {
        toast.error('Too late to undo — the song has moved on');
        return;
      }
      await updateRequest(requestId, buildUndoBackToQueueUpdate(previous));
    } catch (error) {
      console.error('Failed to undo back to queue:', { requestId, error });
      toast.error("Couldn't undo");
    }
  };

  const reloadQueuedRequests = async () => {
    const eventRequests = (await getRequestsByEventId(eventId!)).filter(r => r.status === 'queued');
    setRequests(sortRequests(eventRequests));
  };

  const handlePlacePosition = async (position: number) => {
    if (!positionPrompt) return;
    setPlacingPosition(true);
    try {
      await positionPrompt.place(position);
    } finally {
      setPlacingPosition(false);
      setPositionPrompt(null);
    }
  };

  const handleBackToQueueFromPlayed = (request: SongRequest) => {
    if (useManualOrder) {
      setPositionPrompt({ songTitle: request.song.title, place: (position) => moveBackToQueue(request, position) });
      return;
    }
    void moveBackToQueue(request);
  };

  const moveBackToQueue = async (request: SongRequest, position?: number) => {
    const previous = { timestamps: request.timestamps, manualOrder: request.manualOrder };
    setMovingBackIds(prev => new Set(prev).add(request.id));
    try {
      if (position === undefined) {
        await updateRequest(request.id, buildPlayedToQueueUpdate(request));
      } else {
        const placement = placeInManualOrder(displayQueue, position);
        await updateManualOrder(placement.otherOrders);
        await updateRequest(request.id, buildPlayedToQueueUpdate(request, placement.manualOrder));
        await reloadQueuedRequests();
      }
      toast((t) => (
        <span className="flex items-center gap-3">
          <span>"{request.song.title}" is back in the queue</span>
          <button
            onClick={() => { toast.dismiss(t.id); void undoBackToQueue(request.id, previous); }}
            className="font-semibold text-blue-600 dark:text-blue-400"
          >
            Undo
          </button>
        </span>
      ), { duration: 6000 });
    } catch (error) {
      console.error('Failed to move played song back to queue:', { requestId: request.id, error });
      toast.error("Couldn't move the song back to the queue");
    } finally {
      setMovingBackIds(prev => {
        const next = new Set(prev);
        next.delete(request.id);
        return next;
      });
    }
  };

  const handleAnalyzeSong = async (title: string, artist: string) => {
    setAnalysisModal({
      isOpen: true,
      songTitle: title,
      songArtist: artist,
      analysis: null,
      loading: true,
      error: null,
      djVerdict: null,
      verdictLoading: false,
    });

    // Load analysis and existing verdict in parallel
    const [analysisResult, verdict] = await Promise.allSettled([
      analyzeSong(title, artist),
      getSongVerdict(title, artist),
    ]);

    if (analysisResult.status === 'fulfilled') {
      setAnalysisModal(prev => ({
        ...prev,
        analysis: analysisResult.value,
        loading: false,
        djVerdict: verdict.status === 'fulfilled' ? verdict.value : null,
      }));
    } else {
      const errMsg = analysisResult.reason instanceof Error ? analysisResult.reason.message : 'Failed to analyze song';
      setAnalysisModal(prev => ({
        ...prev,
        loading: false,
        error: errMsg,
        djVerdict: verdict.status === 'fulfilled' ? verdict.value : null,
      }));
      toast.error('Failed to analyze song');
    }
  };

  const handleSetVerdict = async (verdict: 'passed' | 'blocked') => {
    if (!user?.uid) return;
    const { songTitle, songArtist, djVerdict } = analysisModal;
    const newVerdict = djVerdict === verdict ? null : verdict; // Toggle if same
    setAnalysisModal(prev => ({ ...prev, verdictLoading: true }));
    try {
      await setSongVerdict(songTitle, songArtist, newVerdict, user.uid);
      setAnalysisModal(prev => ({ ...prev, djVerdict: newVerdict, verdictLoading: false }));
      // Update inline verdicts map
      const key = `${songTitle.toLowerCase().trim()}|${songArtist.toLowerCase().trim()}`;
      setSongVerdicts(prev => {
        const next = new Map(prev);
        if (newVerdict) next.set(key, newVerdict);
        else next.delete(key);
        return next;
      });
      toast.success(newVerdict === 'passed' ? 'Song marked as passed' : newVerdict === 'blocked' ? 'Song marked as blocked' : 'Verdict cleared');
    } catch {
      setAnalysisModal(prev => ({ ...prev, verdictLoading: false }));
      toast.error('Failed to save verdict');
    }
  };

  const handleCloseAnalysisModal = () => {
    setAnalysisModal({
      isOpen: false,
      songTitle: '',
      songArtist: '',
      analysis: null,
      loading: false,
      error: null,
      djVerdict: null,
      verdictLoading: false,
    });
    setLyricsPaste({ text: '', loading: false });
  };

  const handleReanalyzeWithLyrics = async () => {
    if (!lyricsPaste.text.trim()) return;
    setLyricsPaste(prev => ({ ...prev, loading: true }));
    try {
      const result = await reanalyzeSongWithLyrics(analysisModal.songTitle, analysisModal.songArtist, lyricsPaste.text);
      setAnalysisModal(prev => ({ ...prev, analysis: result }));
      setLyricsPaste({ text: '', loading: false });
      toast.success('Re-analyzed with provided lyrics');
    } catch {
      setLyricsPaste(prev => ({ ...prev, loading: false }));
      toast.error('Failed to re-analyze with lyrics');
    }
  };

  const handleOpenFlagReview = async (requestId: string, songTitle: string, songArtist: string) => {
    if (!eventId) return;
    try {
      const allFlags = await getFlagsForEvent(eventId);
      const relevant = allFlags.filter(f => f.requestId === requestId && f.status === 'pending');
      setFlagsForReview(relevant);
      setFlagReviewModal({ requestId, songTitle, songArtist });
    } catch {
      toast.error('Failed to load flags');
    }
  };

  const handleReviewFlag = async (flagId: string, action: 'removed' | 'kept' | 'blocked-flagger', flaggedBy?: string) => {
    try {
      await reviewFlag(flagId, action);
      if (action === 'blocked-flagger' && flaggedBy && eventId) {
        await blockFlagger(eventId, flaggedBy);
        toast.success('Flagger blocked from future flagging');
      }
      setFlagsForReview(prev => prev.filter(f => f.id !== flagId));
      // Refresh flag counts
      if (eventId) {
        const counts = await batchGetFlagCounts(eventId);
        setFlagCounts(counts);
      }
    } catch {
      toast.error('Failed to review flag');
    }
  };

  const handleRemoveBlockedFromQueue = async (request: SongRequest) => {
    try {
      await updateRequest(request.id, { status: 'rejected', rejectionReason: 'Song is on blocked list' });
      setRequests(prev => prev.filter(r => r.id !== request.id));
      toast.success(`Removed "${request.song.title}" from queue`);
    } catch (err) {
      toast.error('Failed to remove song from queue');
    }
  };

  const handleIgnoreBlocked = (requestId: string) => {
    setIgnoredBlockedIds(prev => new Set(prev).add(requestId));
  };

  const handleRemoveAllBlocked = async () => {
    const toRemove = [...blockedInQueue];
    let removed = 0;
    for (const request of toRemove) {
      try {
        await updateRequest(request.id, { status: 'rejected', rejectionReason: 'Song is on blocked list' });
        setRequests(prev => prev.filter(r => r.id !== request.id));
        removed++;
      } catch {
        // continue with remaining
      }
    }
    setShowBlockedReviewModal(false);
    toast.success(`Removed ${removed} blocked song${removed !== 1 ? 's' : ''} from queue`);
  };

  // The one place the Queue page moves Now Playing: current → played, target → nowPlaying
  const advanceQueueTo = async (
    target: SongRequest | null,
    options: {
      previousTo?: 'played' | 'queued';
      manualPick?: boolean;
      playerTrack?: { title: string; artist: string } | null;
    } = {},
  ) => {
    const previousTo = options.previousTo ?? 'played';
    const displacedUpdate = (request: SongRequest) =>
      previousTo === 'queued' && request.id === nowPlayingRequest?.id
        ? buildBackToQueueUpdate(request)
        : buildPlayedUpdate(request, new Date().toISOString());

    const currentlyPlaying = requests.find(r => r.status === 'nowPlaying');
    if (currentlyPlaying) {
      await updateRequest(currentlyPlaying.id, displacedUpdate(currentlyPlaying));
    }

    // Also check global storage for now playing from other queue views
    const allRequests = await getRequestsByEventId(eventId!);
    const globalNowPlaying = allRequests.find(r => r.status === 'nowPlaying' && r.id !== target?.id);
    if (globalNowPlaying) {
      await updateRequest(globalNowPlaying.id, displacedUpdate(globalNowPlaying));
    }

    if (nowPlayingRequest && previousTo === 'played' && eventData?.settings?.triviaEnabled) {
      setCurationRequest(nowPlayingRequest);
    }

    if (target) {
      // Mark the target as now playing (and clear queuedInPlayer + lockedNext + vipPriority)
      await updateRequest(
        target.id,
        buildNowPlayingUpdate(target, new Date().toISOString(), {
          manualPick: options.manualPick,
          playerTrack: options.playerTrack,
        }),
      );
      // Starting a song by hand ends a stopped show and drops an old Last Song mark (ITER-175)
      if (eventData?.showStop || eventData?.lastSongRequestId) {
        await updateEvent(eventId!, buildClearShowStopUpdate());
      }
    }
    if (target || playerMismatch?.eventId === eventId) {
      clearPlayerMismatch(eventId!).catch((error) => {
        console.error('Could not clear the player note after a manual advance:', error);
      });
    }

    // Refresh the queue, played list, and now playing
    const updatedRequests = await getRequestsByEventId(eventId!);
    setRequests(sortRequests(updatedRequests.filter(r => r.status === 'queued')));
    setPlayedRequests(updatedRequests.filter(r => r.status === 'played'));

    const newNowPlaying = await getNowPlaying(eventId!);
    setNowPlayingRequest(newNowPlaying);
  };

  const advanceQueueOrToast = async (target: SongRequest | null) => {
    try {
      await advanceQueueTo(target);
    } catch (error) {
      console.error('Could not move the queue on:', error);
      toast.error('Failed to update now playing');
    }
  };

  const [lastSongBusy, setLastSongBusy] = useState(false);

  const handleToggleLastSong = async (request: SongRequest) => {
    if (!eventId) return;
    const turningOn = !lastSongActive;
    setLastSongBusy(true);
    try {
      await setLastSong(eventId, turningOn ? request.id : null);
      toast(turningOn
        ? `Last Song ON — Bridge will pause ${playerLabel} when this song ends`
        : 'Last Song off — the queue keeps going',
        { icon: turningOn ? '⏹️' : '▶️', duration: 5000 },
      );
    } catch (error) {
      console.error('Could not save Last Song:', error);
      toast.error(error instanceof Error ? error.message : 'Could not save Last Song');
    } finally {
      setLastSongBusy(false);
    }
  };

  // Clears the stop; at the Bridge level the player plays and Bridge advances as usual.
  // A refusal is not followed by setEventData: the event listener already delivers the fresh copy.
  const handleResumeShow = async () => {
    if (!eventId) return;
    try {
      await resumeShow(eventId);
      if (level === 'bridge') await sendRemoteCommand('play');
      toast.success('Resumed');
    } catch (error) {
      console.error('Could not resume the show:', error);
      toast.error(error instanceof Error ? error.message : 'Could not resume');
    }
  };

  const [endAfterBusy, setEndAfterBusy] = useState(false);

  const handleToggleEndAfterLastSong = async (checked: boolean) => {
    if (!eventId) return;
    setEndAfterBusy(true);
    try {
      await setEndAfterLastSong(eventId, checked);
    } catch (error) {
      console.error('Could not save the auto-end choice:', error);
      toast.error('Could not save the auto-end choice');
    } finally {
      setEndAfterBusy(false);
    }
  };

  // End Event / Reopen (ITER-199): both ask first; attendees get the wrap-up from the status change
  const handleToggleEventEnded = async () => {
    if (!eventId || !eventData) return;
    const isEnding = eventData.status !== 'completed';
    if (!window.confirm(isEnding ? END_EVENT_CONFIRM_TEXT : REOPEN_EVENT_CONFIRM_TEXT)) return;
    try {
      if (isEnding) {
        await endEvent(eventId);
      } else {
        await reopenEvent(eventId);
      }
      setEventData(current => (current ? { ...current, status: isEnding ? 'completed' : 'active' } : current));
      toast.success(isEnding ? 'Event ended. Attendees got the thank-you.' : 'Event reopened');
    } catch (error) {
      console.error(isEnding ? 'Could not end the event:' : 'Could not reopen the event:', error);
      toast.error(isEnding ? 'Could not end the event' : 'Could not reopen the event');
    }
  };

  const handleReopenAndResume = async () => {
    if (!eventId) return;
    if (!window.confirm(REOPEN_EVENT_CONFIRM_TEXT)) return;
    try {
      await reopenEvent(eventId);
      setEventData(current => (current ? { ...current, status: 'active' } : current));
    } catch (error) {
      console.error('Could not reopen the event:', error);
      toast.error('Could not reopen the event');
      return;
    }
    await handleResumeShow();
  };

  const handleChecklistPlayed = () => {
    advanceQueueOrToast(nextUp);
  };

  const isDragLocked = (request: SongRequest) =>
    Boolean(
      request.lockedNext ||
      (level === 'checklist' && isQueuedInPlayer(request))
    );

  const executeMarkNowPlaying = async (requestId: string, songTitle: string) => {
    try {
      const request = requests.find(r => r.id === requestId);
      if (!request) {
        toast.error(`"${songTitle}" is no longer in the queue`);
        return;
      }

      // Bridge level, DJ starts a local file by hand: park the player first
      if (request.localFile && level === 'bridge' && nowPlayingRequest && !nowPlayingRequest.localFile) {
        try {
          await sendRemoteCommand('pause');
        } catch (error) {
          console.error('Could not pause the player before a local file:', error);
          toast.error(`Could not pause ${playerLabel} — pause it by hand`);
        }
      }

      await advanceQueueTo(request);

      toast.success(`"${songTitle}" is now playing!`);
    } catch (error) {
      console.error('Could not mark the song as now playing:', error);
      toast.error('Failed to update now playing');
    }
  };

  const handleMarkNowPlaying = (requestId: string, songTitle: string) => {
    // If listening to another event, prompt to switch first
    if (isLockedToOtherEvent(eventId!)) {
      setSwitchEventConfirm({
        action: async () => {
          lockPlayback(eventId!, eventData?.name || 'Unknown Event');
          executeMarkNowPlaying(requestId, songTitle);
        },
        lockedEventName: activePlayback?.eventName || 'another event',
      });
      return;
    }
    // Auto-start listening if not already active
    if (!activePlayback) {
      lockPlayback(eventId!, eventData?.name || 'Unknown Event');
    }
    executeMarkNowPlaying(requestId, songTitle);
  };


  // Drop by touch, mouse or keyboard; writes the same manual order the old HTML5 drop did
  const refreshQueueFromStorage = async () => {
    const eventRequests = (await getRequestsByEventId(eventId!))
      .filter(r => r.status === 'queued');
    setRequests(sortRequests(eventRequests));
  };

  const handleQueueDragMove = async (activeId: string, overId: string) => {
    const result = manualOrderAfterDrop(filteredRequests, activeId, overId, isDragLocked);
    if ('refused' in result) {
      if (result.refused === 'locked') toast.error('Cannot move songs into a locked position');
      if (result.refused === 'missing') toast('That song just left the queue.');
      return;
    }
    const newOrders = new Map(result.updates.map(update => [update.requestId, update.manualOrder]));
    setRequests(prev => prev.map(r => newOrders.has(r.id) ? { ...r, manualOrder: newOrders.get(r.id) } : r));
    try {
      await updateManualOrder(result.updates);
      await refreshQueueFromStorage();
      toast.success('Queue reordered successfully');
    } catch (err) {
      console.error('[queue] reorder failed', err);
      toast.error('Failed to reorder queue');
      try {
        await refreshQueueFromStorage();
      } catch (refreshErr) {
        console.error('[queue] refresh after failed reorder failed', refreshErr);
      }
    }
  };

  // DJ Notes handlers
  const handleToggleNotes = (requestId: string, currentNotes?: string) => {
    const newExpanded = new Set(expandedNotes);
    if (newExpanded.has(requestId)) {
      newExpanded.delete(requestId);
      // Clear editing state when collapsing
      const newEditing = { ...editingNotes };
      delete newEditing[requestId];
      setEditingNotes(newEditing);
    } else {
      newExpanded.add(requestId);
      // Initialize editing state with current notes
      setEditingNotes(prev => ({
        ...prev,
        [requestId]: currentNotes || '',
      }));
    }
    setExpandedNotes(newExpanded);
  };

  const handleNotesChange = (requestId: string, value: string) => {
    setEditingNotes(prev => ({
      ...prev,
      [requestId]: value,
    }));
  };

  const handleSaveNotes = async (requestId: string) => {
    const notes = editingNotes[requestId] || '';
    setSavingNotes(prev => new Set(prev).add(requestId));

    try {
      await updateRequest(requestId, { djNotes: notes.trim() || undefined });

      // Update local state
      setRequests(prev => prev.map(r =>
        r.id === requestId ? { ...r, djNotes: notes.trim() || undefined } : r
      ));

      toast.success('Note saved');
    } catch {
      toast.error('Failed to save note');
    } finally {
      setSavingNotes(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  // Reset to sort-mode-based order
  const handleResetToSortOrder = async () => {
    const sortLabel = djSortMode === 'copeland' ? 'Copeland' : djSortMode === 'fifo' ? 'FIFO' : 'Votes';
    try {
      // Update each request to clear manual order
      for (const req of requests) {
        await updateRequest(req.id, { manualOrder: undefined });
      }

      // Refresh and sort by score (voted-out to bottom)
      const eventRequests = (await getRequestsByEventId(eventId!))
        .filter(r => r.status === 'queued')
        .sort((a, b) => {
          const byCrowd = compareCrowdTier(a, b);
          if (byCrowd !== 0) return byCrowd;
          return sortDirection === 'desc'
            ? getSortValue(b) - getSortValue(a)
            : getSortValue(a) - getSortValue(b);
        });
      setRequests(eventRequests);
      setUseManualOrder(false);

      toast.success(`Queue reset to ${sortLabel} order`);
    } catch {
      toast.error('Failed to reset order');
    }
  };

  // Real-time listener for all request changes (replaces 10-second poll)
  // After the initial snapshot, only changed documents are transferred (delta reads)
  useEffect(() => {
    if (!eventId) return;
    const unsub = subscribeToRequestsByEventId(eventId, (allRequests) => {
      setPendingRequests(allRequests.filter(isAwaitingDjApproval));
      setPlayedRequests(allRequests.filter(r => r.status === 'played'));
      setOverflowRequests(allRequests.filter(r => r.status === 'overflow'));

      // Live attendee stats (computed from snapshot data — no extra reads)
      const uniqueRequesters = new Set(allRequests.map(r => r.requestedBy.oderId)).size;
      setAttendeeStats(prev => ({ ...prev, requesters: uniqueRequesters }));

      // Update queued requests from snapshot
      const queued = allRequests.filter(r => r.status === 'queued');
      if (useManualOrderRef.current) {
        setRequests(prev => refreshKeepingListOrder(prev, queued));
      } else {
        setRequests(sortRequestsRef.current(queued));
      }

      // People's Pick nomination tracking (Feature 16) — read from ref to avoid stale closure
      const currentEventData = eventDataRef.current;
      if (currentEventData?.settings?.superRequestEnabled) {
        const superReqs = allRequests.filter(r => r.superRequest && r.status === 'pending' && r.poolApproved);
        // Group by fuzzy-matched song
        const groups: Array<{ title: string; artist: string; albumArtUrl?: string; oderIds: Set<string>; requestIds: string[] }> = [];
        for (const req of superReqs) {
          const existingGroup = groups.find(g => isFuzzySongMatch(g.title, g.artist, req.song.title, req.song.artist));
          if (existingGroup) {
            existingGroup.oderIds.add(req.requestedBy.oderId);
            existingGroup.requestIds.push(req.id);
          } else {
            groups.push({ title: req.song.title, artist: req.song.artist, albumArtUrl: req.song.albumArtUrl, oderIds: new Set([req.requestedBy.oderId]), requestIds: [req.id] });
          }
        }
        const totalAttendees = new Set(allRequests.map(r => r.requestedBy.oderId)).size;
        const thresholdType = currentEventData.settings.superRequestThresholdType ?? 'count';
        const rawThreshold = thresholdType === 'percentage'
          ? (currentEventData.settings.superRequestThresholdPercent ?? 10)
          : (currentEventData.settings.superRequestThresholdCount ?? 5);
        const effectiveThreshold = thresholdType === 'percentage'
          ? Math.max(2, Math.ceil((rawThreshold / 100) * totalAttendees))
          : rawThreshold;

        const nomGroups = groups.map(g => ({ title: g.title, artist: g.artist, albumArtUrl: g.albumArtUrl, count: g.oderIds.size, threshold: effectiveThreshold, requestIds: g.requestIds, primaryRequestId: g.requestIds[0] }));
        setNominationGroups(nomGroups);

        // Toast for songs one away from threshold
        for (const g of nomGroups) {
          const key = `${g.title.toLowerCase()}-${g.artist.toLowerCase()}`;
          if (g.count === effectiveThreshold - 1 && !notifiedNominationsRef.current.has(key)) {
            notifiedNominationsRef.current.add(key);
            toast(`"${g.title}" is one nomination away from People's Pick!`, { icon: '👑', duration: 8000 });
          }
        }
      } else {
        setNominationGroups([]);
      }
    });
    return () => unsub();
  }, [eventId]);

  // Real-time listener for People's Pick upvote counts
  useEffect(() => {
    if (!eventId || !eventDataRef.current?.settings?.superRequestEnabled) return;
    const unsub = subscribeToUpvoteCountsForEvent(eventId, setPoolUpvoteCounts);
    return unsub;
  }, [eventId]);

  // Attendee stats: use event-doc cached uniqueVoterCount + session count polling (60s instead of 30s)
  // uniqueVoterCount is updated by Cloud Functions on each vote create/delete
  useEffect(() => {
    if (!eventId) return;
    const fetchVisitors = async () => {
      const visitorCount = await getUniqueVisitorCount(eventId);
      setAttendeeStats(prev => ({ ...prev, visitors: visitorCount }));
    };
    fetchVisitors();
    const interval = setInterval(fetchVisitors, 60000);
    return () => clearInterval(interval);
  }, [eventId]);

  // Read cached voter count from event doc (updated by Cloud Functions, no collection scan)
  useEffect(() => {
    if (!eventData) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cachedVoters = (eventData as any).uniqueVoterCount;
    if (typeof cachedVoters === 'number') {
      setAttendeeStats(prev => ({ ...prev, voters: cachedVoters }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [(eventData as any)?.uniqueVoterCount]);

  const participantCount = eventData?.participantCount ?? 0;
  const voteRules = voteRulesOf(eventData?.settings);

  // Fresh-read co-approval setting from Firestore to avoid stale context
  const readCoApprovalEnabled = async (): Promise<boolean> => {
    const contextValue = eventData?.settings?.coApprovalEnabled ?? false;
    if (!eventId) return contextValue;
    try {
      const freshEvent = await getEventById(eventId);
      return freshEvent ? freshEvent.settings.coApprovalEnabled ?? false : contextValue;
    } catch (err) {
      console.error('[QueuePage] Failed to read co-approval setting, using cached event settings:', err);
      return contextValue;
    }
  };

  // Handle DJ manual approval of a nomination group
  const handleApproveNomination = async (title: string, artist: string) => {
    if (!eventId) return;
    try {
      const freshCoApprovalOn = await readCoApprovalEnabled();
      const { mergedCount } = await approveSuperRequestManually(eventId, title, artist, freshCoApprovalOn);
      toast.success(`"${title}" approved and added to queue!${mergedCount > 0 ? ` (${mergedCount} duplicate${mergedCount > 1 ? 's' : ''} merged)` : ''}`, { icon: '👑' });
      // Real-time listener will automatically update requests/pending state
      // Just remove from nomination groups optimistically
      setNominationGroups(prev => prev.filter(g => !isFuzzySongMatch(g.title, g.artist, title, artist)));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to approve nomination');
    }
  };

  // Real-time listener for cached Copeland scores (replaces 30-second polling)
  // Server recomputes scores whenever a ranking is created/updated/deleted
  useEffect(() => {
    if (!eventId) return;
    const unsub = subscribeToCachedCopelandScores(eventId, (scores) => {
      if (!scores) return; // No cached scores yet — initial load already computed them
      cachedScoresArrivedRef.current = true;
      setConsensusScores(scores.consensus);
      setDiscoveryScores(scores.discovery);
      setStaleCount(scores.staleCount);
      setRankingStats({
        totalRankings: scores.totalRankings,
        uniqueVoters: scores.uniqueVoters,
        averageRankedItems: scores.averageRankedItems,
      });
      setAttendeeStats(prev => ({ ...prev, rankers: scores.uniqueVoters }));
      setThresholdMet(scores.totalRankings >= minParticipants);
    });
    return () => unsub();
  }, [eventId, minParticipants]);

  // Manual queue refresh
  const refreshQueue = useCallback(async () => {
    if (!eventId || refreshing) return;
    setRefreshing(true);
    try {
      const allRequests = await getRequestsByEventId(eventId);
      const queued = allRequests.filter(r => r.status === 'queued');
      const pending = allRequests.filter(isAwaitingDjApproval);
      const played = allRequests.filter(r => r.status === 'played');

      if (useManualOrder) {
        setRequests(prev => refreshKeepingListOrder(prev, queued));
      } else {
        setRequests(sortRequests(queued));
      }
      setPendingRequests(pending);
      setPlayedRequests(played);

      const nowPlaying = await getNowPlaying(eventId);
      setNowPlayingRequest(nowPlaying);

      toast.success('Queue refreshed');
    } catch {
      toast.error('Failed to refresh queue');
    } finally {
      setRefreshing(false);
    }
  }, [eventId, refreshing, useManualOrder]);

  // Practice Mode: close dropdown on outside click
  const practiceMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!showPracticeMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (practiceMenuRef.current && !practiceMenuRef.current.contains(e.target as Node)) {
        setShowPracticeMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showPracticeMenu]);

  // Practice Mode: check for existing practice data on load
  useEffect(() => {
    if (!eventId) return;
    hasPracticeData(eventId).then(setHasPracticeRequests).catch(() => {});
  }, [eventId]);

  // Practice Mode: populate handler
  const handlePopulatePractice = async () => {
    if (!eventId || practiceLoading) return;
    setPracticeLoading(true);
    try {
      const result = await populatePracticeQueue(eventId);
      toast.success(`Added ${result.requestsCreated} test requests from ${result.attendeesSimulated} attendees`);
      setHasPracticeRequests(true);
      setShowPracticeMenu(false);
      await refreshQueue();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to populate practice data';
      if (msg.includes('already-exists') || msg.includes('already exists')) {
        toast.error('Practice data already exists. Clear it first.');
      } else {
        toast.error(msg);
      }
    } finally {
      setPracticeLoading(false);
    }
  };

  // Practice Mode: clear handler
  const handleClearPractice = async () => {
    if (!eventId || practiceLoading) return;
    if (!window.confirm('Clear all practice/test data from this event?')) return;
    setPracticeLoading(true);
    try {
      const result = await clearPracticeData(eventId);
      toast.success(`Cleared ${result.requestsDeleted} practice requests`);
      setHasPracticeRequests(false);
      setShowPracticeMenu(false);
      await refreshQueue();
    } catch {
      toast.error('Failed to clear practice data');
    } finally {
      setPracticeLoading(false);
    }
  };

  // Preview Device: derived state
  const isEventActive = eventData?.status === 'active';

  // Preview Device: connect handler
  const handleConnectPreviewDevice = async () => {
    if (!eventId || !user?.uid) return;
    try {
      await createPreviewSession(eventId, user.uid);
      const session = await getPreviewSession(eventId, user.uid);
      if (session) {
        setPreviewSession(session);
        setShowPreviewQr(true);
      }
    } catch {
      toast.error('Failed to create preview session');
    }
  };

  // Preview Device: disconnect handler
  const handleDisconnectPreview = async () => {
    if (!previewSession?.id) return;
    try {
      await deletePreviewSession(previewSession.id);
      setPreviewSession(null);
      toast.success('Preview device disconnected');
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  // Preview Device: send a track to the preview phone for matching
  const handleSendToPreview = useCallback(async (request: SongRequest) => {
    if (!previewSession?.id) return;
    try {
      await sendTrackToPreview(previewSession.id, buildPreviewTrack(request, playMode));
    } catch (sendError) {
      console.error('Send to preview device failed', sendError);
      toast.error("Couldn't send the song to the preview device");
    }
  }, [previewSession?.id, playMode]);

  // Approve a pending request — always opens modal so DJ can verify and enter duration
  const handleApproveRequest = async (requestId: string) => {
    if (processingIds.has(requestId)) return;

    const request = pendingRequests.find(r => r.id === requestId);
    if (!request) return;

    // Always open the match modal — DJ needs to verify song and enter duration
    openMatchModal(request, 0, 0);
  };

  // Direct approve without Spotify match (manual mode)
  const approveDirectly = async (requestId: string) => {
    if (processingIds.has(requestId)) return;
    setProcessingIds(prev => new Set(prev).add(requestId));
    try {
      const existing = pendingRequests.find(r => r.id === requestId);
      const routeToCoApprover = shouldRouteToCoApprover(await readCoApprovalEnabled(), existing?.coApprovalStatus);
      const targetStatus = existing?.isOverflow ? 'overflow' : 'queued';
      const updateData: Record<string, unknown> = {
        ...(routeToCoApprover ? { coApprovalStatus: 'pending' } : { status: targetStatus }),
        ...staleCoApprovalFields(routeToCoApprover, existing?.coApprovalStatus),
        timestamps: {
          requested: existing?.timestamps?.requested || new Date().toISOString(),
          ...existing?.timestamps,
          approved: new Date().toISOString(),
        },
      };
      await updateRequest(requestId, updateData);
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      const updated = await getRequestsByEventId(eventId!);
      setRequests(sortRequests(updated.filter(r => r.status === 'queued')));
      const approvalMessage = routeToCoApprover
        ? 'Sent to co-approver for review'
        : targetStatus === 'overflow'
          ? 'Approved to overflow pool'
          : 'Request approved and added to queue';
      toast.success(approvalMessage);
    } catch {
      toast.error('Failed to approve request');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(requestId);
        return newSet;
      });
    }
  };

  // Paste a Spotify, TIDAL or Apple Music track link on a queue item; stored as its canonical track URL
  const handlePasteUrlOnSong = async (requestId: string) => {
    const pasted = pasteUrlValue.trim();
    if (!pasted) return;

    setPasteUrlLoading(true);
    try {
      const cleaned = await cleanPastedLink(pasted, resolveSpotifyLink);
      if (!cleaned.ok) {
        toast.error(cleaned.message);
        return;
      }
      const url = cleaned.url;

      // Duplicate track check — warn before proceeding
      const isDuplicate = requests.some(r => [r.platformLink, r.spotifyUri].some(link => {
        if (!link) return false;
        const existing = parseMusicLink(link);
        return existing.kind === 'track' && existing.provider === cleaned.provider && existing.trackId === cleaned.trackId;
      }));
      if (isDuplicate && !window.confirm('This URL is already in the queue or pending list. Add it anyway?')) {
        return;
      }

      let platformName = cleaned.platformName;
      const updateData: Record<string, unknown> = {};

      // Resolve metadata via Odesli
      const resolved = await resolveFromUrl(url).catch(() => null);

      // The stored link must play in this event's player; swap in Odesli's link for it when the paste is for another app
      let platformLink = url;
      if (!linkFitsPlayMode(url, platformName, playMode)) {
        const converted = modeLinkFrom(resolved?.platformLinks, playMode);
        if (!converted) {
          toast.error(`That's a ${platformName} link. This event plays ${playerLabel}.`);
          return;
        }
        platformLink = canonicalLinkOrSame(converted);
        platformName = playerLabel;
      }

      if (resolved) {
        updateData.songLinkUrl = resolved.songLinkUrl;
        // Odesli metadata is the source of truth for title/artist (what the URL actually points to)
        const odesliTitle = resolved.metadata?.title || '';
        const odesliArtist = resolved.metadata?.artistName || '';
        // Try to get rich metadata from iTunes (for album art, duration, preview)
        let itunesResult: iTunesResult | null = null;
        if (resolved.metadata?.apiProvider === 'itunes' && resolved.metadata.id) {
          itunesResult = await getSongByTrackId(Number(resolved.metadata.id), searchCatalog).catch(() => null);
        } else if (resolved.platformLinks.appleMusic) {
          const idMatch = resolved.platformLinks.appleMusic.match(/\/(\d{5,})/);
          if (idMatch) itunesResult = await getSongByTrackId(Number(idMatch[1]), searchCatalog).catch(() => null);
        }
        const request = requests.find(r => r.id === requestId);
        const isRealItunes = itunesResult != null && itunesResult.trackId > 0;
        const resolvedTitle = odesliTitle || itunesResult?.trackName || '';
        const resolvedArtist = odesliArtist || itunesResult?.artistName || '';

        // Check for mismatch — only when Odesli returned real metadata (not iTunes fallback)
        if (request && odesliTitle) {
          const check = quickInlineCheck(
            request.song.title, request.song.artist,
            resolvedTitle, resolvedArtist,
          );
          if (check.isMismatch) {
            toast(`URL points to "${resolvedTitle}" by ${resolvedArtist} — opening match modal for review`, { icon: '⚠️', duration: 4000 });
            setPasteUrlRequestId(null);
            setPasteUrlValue('');
            setPasteUrlLoading(false);
            // Open the match modal with the pasted link pre-filled, so it resolves there with the mismatch warning
            openMatchModal(request, 0, 0, url);
            return;
          }
        }

        // Use Odesli title/artist for the queue (so Desktop Helper can recognize the song)
        // Fall back to iTunes only when Odesli has nothing
        updateData.song = {
          ...request?.song,
          title: resolvedTitle || request?.song?.title,
          artist: resolvedArtist || request?.song?.artist,
          albumArtUrl: itunesResult?.artworkUrl100 || resolved.metadata?.thumbnailUrl || request?.song?.albumArtUrl,
          durationMs: isRealItunes ? itunesResult!.trackTimeMillis : (request?.song?.durationMs || 0),
          explicitFlag: isRealItunes
            ? itunesResult!.trackExplicitness === 'explicit'
            : (request?.song?.explicitFlag || false),
          ...(itunesResult?.trackId
            ? { itunesTrackId: String(itunesResult.trackId), isrc: isrcForMatchedSong(itunesResult, request?.song ?? {}) }
            : {}),
          previewUrl: isRealItunes ? (itunesResult!.previewUrl || request?.song?.previewUrl) : request?.song?.previewUrl,
        };
      }

      const linkedRequest = requests.find(r => r.id === requestId);
      const songForLink = (updateData.song as SongRequest['song'] | undefined) ?? linkedRequest?.song;
      if (songForLink) {
        Object.assign(updateData, matchedLinkFields({
          platformLink,
          platformName,
          songLinkUrl: updateData.songLinkUrl as string | undefined,
        }, songForLink));
      } else {
        Object.assign(updateData, { platformLink, platformName });
      }
      await updateRequest(requestId, updateData);
      toast.success(`Linked to ${platformName}`);
      const updated = await getRequestsByEventId(eventId!);
      setRequests(sortRequests(updated.filter(r => r.status === 'queued')));
      // Save to the shared library under the song as the guest picked it, not Odesli's title (fire-and-forget)
      if (linkedRequest) {
        const songData = (updateData.song as SongRequest['song'] | undefined) ?? linkedRequest.song;
        trySaveSongLink({
          song: librarySongOf(linkedRequest.song),
          link: platformLink,
          songLinkUrl: (updateData.songLinkUrl as string | undefined) || null,
          albumArtUrl: songData.albumArtUrl || null,
          durationMs: songData.durationMs || null,
          eventId: eventId ?? null,
          requestId: linkedRequest.id,
        }).then(outcome => {
          if (outcome.error) toast.error(`Link saved, but saving it to the library failed: ${outcome.error}`);
          else if (outcome.queuedForReview) toast(QUEUED_FOR_REVIEW_MESSAGE, { duration: 6000 });
        });
      }
      setPasteUrlRequestId(null);
      setPasteUrlValue('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save URL');
    } finally {
      setPasteUrlLoading(false);
    }
  };

  // --- Song Match Modal helpers ---
  const openMatchModal = async (request: SongRequest, index = 0, total = 0, initialLinkUrl?: string): Promise<boolean> => {
    if (request.status === 'pending') {
      const outcome = await reviewHold.claim(request.id);
      if (outcome.status === 'held') {
        toast(`${holderLabel(outcome.heldBy)} is reviewing this one`, { icon: '👀' });
        return false;
      }
      if (outcome.status === 'error') toast.error("Couldn't reserve this song — the helper may open it too");
    }
    setMatchingRequest(request);
    setMatchInitialLinkUrl(initialLinkUrl);
    setPendingMatchIndex(index);
    setPendingMatchTotal(total);
    return true;
  };

  // Songs the co-approval DJ holds are skipped, so the run moves on to the next one
  const openNextInRun = async (step: MatchRunStep, stillPendingIds: ReadonlySet<string>) => {
    let current = step;
    while (current.nextId) {
      const nextId = current.nextId;
      const next = actionablePendingRequests.find(r => r.id === nextId);
      if (next) {
        setMatchRun(current.run);
        if (await openMatchModal(next, current.position, current.run.total)) return;
      }
      current = advanceMatchRun(current.run, nextId, stillPendingIds);
    }
    handleCloseMatchModal();
  };

  const advanceToNextPending = () => {
    if (!matchRun || !matchingRequest) {
      handleCloseMatchModal();
      return;
    }
    const stillPendingIds = new Set(actionablePendingRequests.map(r => r.id));
    void openNextInRun(advanceMatchRun(matchRun, matchingRequest.id, stillPendingIds), stillPendingIds);
  };

  // Approve with a platform-specific link (from Odesli resolution or direct paste)
  const approveWithPlatformLink = async (
    requestId: string,
    platformLink: string,
    platformName: string,
    songLinkUrl: string,
    itunesResult: iTunesResult | null,
    durationOverrideMs: number | null = null,
    durationVerified: 'exact' | 'approximate' | null = null,
  ) => {
    if (processingIds.has(requestId)) return;
    setProcessingIds(prev => new Set(prev).add(requestId));
    try {
      const existing = pendingRequests.find(r => r.id === requestId);
      const routeToCoApprover = shouldRouteToCoApprover(await readCoApprovalEnabled(), existing?.coApprovalStatus);
      const targetStatus = existing?.isOverflow ? 'overflow' : 'queued';
      const updateData: Record<string, unknown> = {
        ...(routeToCoApprover ? { coApprovalStatus: 'pending' } : { status: targetStatus }),
        ...staleCoApprovalFields(routeToCoApprover, existing?.coApprovalStatus),
        preppedInSpotify: platformName === 'Spotify',
        durationVerified: durationVerified || null,
        entrySource: 'pending-approved',
        matchVerified: true,
        timestamps: {
          requested: existing?.timestamps?.requested || new Date().toISOString(),
          ...existing?.timestamps,
          approved: new Date().toISOString(),
        },
      };
      if (itunesResult) {
        const isRealItunes = itunesResult.trackId > 0;
        // Use DJ-overridden duration if provided, otherwise iTunes duration, then keep existing
        const finalDurationMs = (durationOverrideMs
          ?? (isRealItunes ? itunesResult.trackTimeMillis : 0))
          || existing?.song?.durationMs
          || 0;
        const songUpdate: Record<string, unknown> = {
          ...existing?.song,
          title: itunesResult.trackName || existing?.song?.title || '',
          artist: itunesResult.artistName || existing?.song?.artist || '',
          albumArtUrl: itunesResult.artworkUrl100 || existing?.song?.albumArtUrl || '',
          durationMs: finalDurationMs,
          explicitFlag: isRealItunes
            ? itunesResult.trackExplicitness === 'explicit'
            : (existing?.song?.explicitFlag || false),
          ...(itunesResult.trackId
            ? { itunesTrackId: String(itunesResult.trackId), isrc: isrcForMatchedSong(itunesResult, existing?.song ?? {}) }
            : {}),
          previewUrl: isRealItunes ? (itunesResult.previewUrl || existing?.song?.previewUrl || null) : (existing?.song?.previewUrl || null),
        };
        // Firestore rejects undefined in nested objects — replace with null
        for (const key of Object.keys(songUpdate)) {
          if (songUpdate[key] === undefined) songUpdate[key] = null;
        }
        updateData.song = songUpdate;
      } else if (durationOverrideMs) {
        // No itunesResult but DJ entered a duration — update just the duration
        updateData.song = {
          ...existing?.song,
          durationMs: durationOverrideMs,
        };
        for (const key of Object.keys(updateData.song as Record<string, unknown>)) {
          if ((updateData.song as Record<string, unknown>)[key] === undefined) (updateData.song as Record<string, unknown>)[key] = null;
        }
      }
      const songForLink = (updateData.song as SongRequest['song'] | undefined) ?? existing?.song;
      if (songForLink) {
        Object.assign(updateData, matchedLinkFields({ platformLink, platformName, songLinkUrl }, songForLink));
      } else {
        Object.assign(updateData, { platformLink: canonicalLinkOrSame(platformLink), platformName, ...(songLinkUrl ? { songLinkUrl } : {}) });
      }
      await updateRequest(requestId, updateData);
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      const updated = await getRequestsByEventId(eventId!);
      setRequests(sortRequests(updated.filter(r => r.status === 'queued')));
      toast.success(`Matched on ${platformName} — ${routeToCoApprover ? 'sent to co-approver for review' : 'added to queue'}`);
    } catch {
      toast.error('Failed to approve request');
    } finally {
      setProcessingIds(prev => { const s = new Set(prev); s.delete(requestId); return s; });
    }
  };

  // --- SongMatchModal callbacks ---
  const handleModalApproveWithPlatformLink = async (
    requestId: string,
    platformLink: string,
    platformName: string,
    songLinkUrl: string,
    itunesResult: iTunesResult | null,
    durationOverrideMs: number | null,
    durationVerified: 'exact' | 'approximate' | null,
  ) => {
    await approveWithPlatformLink(requestId, platformLink, platformName, songLinkUrl, itunesResult, durationOverrideMs, durationVerified);
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleModalReject = async (requestId: string, reason: string) => {
    try {
      const existing = pendingRequests.find(r => r.id === requestId);
      await updateRequest(requestId, {
        status: 'rejected',
        rejectionReason: reason,
        timestamps: { requested: existing?.timestamps?.requested || new Date().toISOString(), ...existing?.timestamps, rejected: new Date().toISOString() },
      });
      setPendingRequests(prev => prev.filter(r => r.id !== requestId));
      toast.success('Request rejected');
    } catch {
      toast.error('Failed to reject');
    }
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleModalDefer = async (requestId: string) => {
    await updateRequest(requestId, { djNotes: 'Researching — needs platform match' });
    toast('Marked as "Researching" — come back to approve later', { icon: '🔍' });
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleModalApproveDirect = async (requestId: string) => {
    await approveDirectly(requestId);
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleModalSkip = () => {
    if (pendingMatchTotal > 0) {
      advanceToNextPending();
    } else {
      handleCloseMatchModal();
    }
  };

  const handleCloseMatchModal = () => {
    reviewHold.release();
    setMatchingRequest(null);
    setPendingMatchIndex(0);
    setPendingMatchTotal(0);
    setMatchRun(null);
    setMatchInitialLinkUrl(undefined);
  };

  // --- Inline duration editor ---
  const parseMmSs = (input: string): number | null => {
    const match = input.trim().match(/^(\d{1,3}):(\d{2})$/);
    if (!match) return null;
    const mins = parseInt(match[1], 10);
    const secs = parseInt(match[2], 10);
    if (secs >= 60) return null;
    return (mins * 60 + secs) * 1000;
  };

  const formatMmSs = (ms: number): string => {
    if (!ms) return '';
    const totalSecs = Math.floor(ms / 1000);
    const mins = Math.floor(totalSecs / 60);
    const secs = totalSecs % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const openDurationEditor = (requestId: string, currentMs?: number) => {
    setEditingDurationId(requestId);
    setDurationEditValue(currentMs ? formatMmSs(currentMs) : '');
  };

  const handleSaveDuration = async (requestId: string, verified: 'exact' | 'approximate' | null) => {
    const parsed = parseMmSs(durationEditValue);
    if (verified === 'exact' && (!parsed || parsed <= 0)) {
      toast.error('Enter duration as m:ss (e.g. 3:17)');
      return;
    }
    setDurationEditSaving(true);
    try {
      const existing = requests.find(r => r.id === requestId);
      const update: Record<string, unknown> = { durationVerified: verified || null };
      if (parsed && parsed > 0) {
        update.song = { ...existing?.song, durationMs: parsed };
      }
      await updateRequest(requestId, update);
      const savedDurationMs = parsed && parsed > 0 ? parsed : existing?.song?.durationMs;
      const writeBack = existing
        ? buildDurationWriteBack({
            request: existing,
            librarySong: existing.linkLibraryKey ? libraryStats.get(existing.linkLibraryKey) : undefined,
            durationMs: savedDurationMs,
            verified,
          })
        : null;
      if (writeBack?.songKey) {
        const songKey = writeBack.songKey;
        trySaveSongLink(writeBack).then(outcome => {
          if (outcome.error) {
            toast.error(`Duration updated, but saving it to the library failed: ${outcome.error}`);
            return;
          }
          if (outcome.queuedForReview) toast(QUEUED_FOR_REVIEW_MESSAGE, { duration: 6000 });
          getLibrarySongsByKeys([songKey])
            .then(fresh => setLibraryStats(prev => new Map([...prev, ...fresh])))
            .catch((err: unknown) => console.error('[QueuePage] Reloading the library song failed:', err));
        });
      }
      const updated = await getRequestsByEventId(eventId!);
      setRequests(sortRequests(updated.filter(r => r.status === 'queued')));
      setEditingDurationId(null);
      if (verified) toast.success('Duration updated');
    } catch {
      toast.error('Failed to update duration');
    } finally {
      setDurationEditSaving(false);
    }
  };

  const handleDurationDismissForEvent = () => {
    sessionStorage.setItem('votebeats_duration_verify_dismissed', 'true');
    setDurationDismissedForEvent(true);
    setEditingDurationId(null);
  };

  // Process Pending (batch with match modal)
  const handleProcessPending = () => {
    const available = actionablePendingRequests.filter(r => !isHeldByOther(r, 'dj', Date.now(), coApprovalDjActive));
    if (available.length === 0) return;
    const run = startMatchRun(available.map(r => r.id));
    void openNextInRun({ run, nextId: run.remainingIds[0], position: 1 }, new Set(available.map(r => r.id)));
  };

  // F3: DJ Add Song — search and add directly to queue
  const handleAddSongSearch = async () => {
    if (!addSongQuery.trim()) return;
    setAddSongLoading(true);
    try {
      const results = await searchSongs(addSongQuery.trim(), 10, 'song', searchCatalog);
      setAddSongResults(results);
    } catch {
      toast.error('Search failed');
      setAddSongResults([]);
    } finally {
      setAddSongLoading(false);
    }
  };

  const handleAddSongSelect = (result: iTunesResult) => {
    if (useManualOrder) {
      setPositionPrompt({ songTitle: result.trackName, place: (position) => addSongFromSearch(result, position) });
      return;
    }
    void addSongFromSearch(result);
  };

  const addSongFromSearch = async (result: iTunesResult, position?: number) => {
    if (!eventId || !user) return;
    setAddingSongId(String(result.trackId));
    try {
      const song: SongRequest['song'] = {
        title: result.trackName,
        artist: result.artistName,
        albumArtUrl: result.artworkUrl100 || undefined,
        durationMs: result.trackTimeMillis,
        explicitFlag: result.trackExplicitness === 'explicit' || result.trackExplicitness === 'cleaned',
        itunesTrackId: String(result.trackId),
        isrc: result.isrc ?? null,
      };
      const placement = position === undefined ? null : placeInManualOrder(displayQueue, position);
      if (placement) await updateManualOrder(placement.otherOrders);
      await createRequest(eventId, {
        song,
        requestedBy: { oderId: user.uid, nickname: 'DJ' },
        initialStatus: 'queued',
        entrySource: 'dj-added',
        ...(placement ? { manualOrder: placement.manualOrder } : {}),
      });
      await reloadQueuedRequests();
      toast.success(`"${song.title}" added to queue`);
      setShowAddSongModal(false);
      setAddSongQuery('');
      setAddSongResults([]);
    } catch {
      toast.error('Failed to add song');
    } finally {
      setAddingSongId(null);
    }
  };

  // Reject handlers
  const handleRejectRequest = (requestId: string) => {
    const request = pendingRequests.find(r => r.id === requestId);
    if (request) {
      setRejectingRequest(request);
      setRejectionReason('');
    }
  };

  const confirmReject = async () => {
    if (!rejectingRequest) return;
    if (processingIds.has(rejectingRequest.id)) return;
    setProcessingIds(prev => new Set(prev).add(rejectingRequest.id));
    try {
      await updateRequest(rejectingRequest.id, {
        status: 'rejected',
        rejectionReason: rejectionReason.trim() || undefined,
        timestamps: {
          ...rejectingRequest.timestamps,
          rejected: new Date().toISOString(),
        },
      });
      setPendingRequests(prev => prev.filter(r => r.id !== rejectingRequest.id));
      setRejectingRequest(null);
      setRejectionReason('');
      toast.success('Request rejected');
    } catch {
      toast.error('Failed to reject request');
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(rejectingRequest.id);
        return newSet;
      });
    }
  };

  const cancelReject = () => {
    setRejectingRequest(null);
    setRejectionReason('');
  };

  // Toggle "queued in player"
  const handleToggleQueuedInPlayer = async (request: SongRequest) => {
    const newStatus = !isQueuedInPlayer(request);
    if (newStatus && !canQueueOrLock(request)) {
      toast.error(VOTED_OUT_QUEUE_REFUSAL);
      return;
    }

    // Warn when un-queuing — song may still be in the actual player
    if (!newStatus) {
      if (!window.confirm(`"${request.song.title}" may still be in your ${playerLabel} queue.\n\nMake sure to remove it from ${playerLabel} as well.`)) {
        return;
      }
    }

    const lockDecision = newStatus
      ? decideQueueNextLock({
        request,
        lockWhenQueued,
        depth: queueDepth,
        queue: requests,
        forceLock: (level === 'bridge' && recognition.flagOf(request.id) !== null && nextSong?.id === request.id)
          || shouldForceLockOnQueue(request, eventData?.settings),
      })
      : 'off';
    const update = newStatus
      ? buildQueueNextUpdate(lockDecision, new Date().toISOString())
      : buildUnqueueUpdate(request);

    try {
      await updateRequest(request.id, update);

      setRequests(prev => prev.map(r =>
        r.id === request.id ? { ...r, ...update } : r
      ));

      // When queuing: open song in player (direct link if matched, search otherwise)
      if (newStatus) {
        const { url } = openUrlFor(request, playMode);
        // App URIs (spotify:, music://) should use location.href to trigger the OS handler
        // Web URLs should use window.open to avoid navigating away
        if (url.startsWith('http://') || url.startsWith('https://')) {
          window.open(url, '_blank', 'noopener');
        } else {
          window.location.href = url;
        }
        // Prompt duration editor if not verified
        if (!request.durationVerified && !durationDismissedForEvent) {
          openDurationEditor(request.id, request.song.durationMs);
        }
      }

      if (!newStatus) {
        toast.success(`"${request.song.title}" removed from player queue`);
      } else if (lockDecision === 'lock') {
        toast.success(`"${request.song.title}" queued in ${playerLabel} and locked`);
      } else if (lockDecision === 'cap-full') {
        const cap = autoLockCap(queueDepth);
        toast(`"${request.song.title}" queued — not locked (${cap} of ${cap} auto-locks in use)`);
      } else {
        toast.success(`"${request.song.title}" queued in ${playerLabel}`);
      }
    } catch {
      toast.error('Failed to update queue status');
    }
  };

  // Toggle position lock on a song — locked songs cannot be moved by votes or drag.
  // Optimistic-update only (no re-sort): the visual lock indicator updates immediately
  // and Firestore confirms in the background. Re-sorting on lock would cause a flash.
  const handleToggleLockedNext = async (request: SongRequest) => {
    const update = buildManualLockToggleUpdate(request);
    const newLocked = Boolean(update.lockedNext);
    if (newLocked && !canQueueOrLock(request)) {
      toast.error(VOTED_OUT_QUEUE_REFUSAL);
      return;
    }
    // Optimistic update — apply immediately without waiting for Firestore
    setRequests(prev => prev.map(r =>
      r.id === request.id ? { ...r, ...update } : r
    ));
    try {
      await updateRequest(request.id, update);
      toast.success(newLocked
        ? `"${request.song.title}" position locked`
        : `"${request.song.title}" position unlocked`
      );
    } catch {
      // Roll back optimistic update on failure
      setRequests(prev => prev.map(r =>
        r.id === request.id ? { ...r, lockedNext: request.lockedNext, queueLockedAt: request.queueLockedAt } : r
      ));
      toast.error('Failed to update lock');
    }
  };

  // Mark a song as un-queued from the player (used in Clean Queue modal). Returns false when the write failed.
  const handleUnqueueFromPlayer = async (request: SongRequest): Promise<boolean> => {
    setCleaningQueueIds(prev => new Set(prev).add(request.id));
    const update = buildUnqueueUpdate(request);
    try {
      await updateRequest(request.id, update);
      setRequests(prev => prev.map(r =>
        r.id === request.id ? { ...r, ...update } : r
      ));
      return true;
    } catch {
      toast.error(`Failed to unqueue "${request.song.title}"`);
      return false;
    } finally {
      setCleaningQueueIds(prev => { const s = new Set(prev); s.delete(request.id); return s; });
    }
  };

  // Mark several songs removed at once (Mark All Removed, Clear all). Returns true when every write succeeded.
  const handleMarkRowsRemoved = async (songs: SongRequest[]): Promise<boolean> => {
    const toRemove = songs.filter(song => !cleaningQueueIds.has(song.id));
    let allSucceeded = true;
    for (const song of toRemove) {
      if (!(await handleUnqueueFromPlayer(song))) allSucceeded = false;
    }
    return allSucceeded;
  };

  const handleClearAllQueued = async (songs: SongRequest[]) => {
    if (!window.confirm(`Unmark all ${songs.length} songs queued in ${playerLabel}?\n\nClear your ${playerLabel} queue too, so the two match.`)) return;
    const allCleared = await handleMarkRowsRemoved(songs);
    if (!allCleared) return;
    setShowCleanQueueModal(false);
    toast.success(`Cleared ${songs.length} songs — re-queue the top songs in ${playerLabel}`);
  };

  // Save queue depth — show 3-option confirmation modal
  const handleSaveQueueDepth = (newDepth: number) => {
    if (!eventId || !eventData) return;
    setEditingQueueDepth(false);
    if (newDepth === queueDepth) return; // no change
    setPendingQueueSetting({ key: 'queueDepth', value: newDepth });
  };

  const handleQueueSettingConfirm = async (scope: 'all' | 'event' | 'cancel') => {
    const pending = pendingQueueSetting;
    setPendingQueueSetting(null);
    if (scope === 'cancel' || pending === null || !eventId || !eventData) return;
    const fields = pending.key === 'queueDepth' ? { queueDepth: pending.value } : { lockWhenQueued: pending.value };
    try {
      await updateEventSettingFields(eventId, fields);
      setEventData(prev => prev ? { ...prev, settings: { ...prev.settings, ...fields } } : prev);
      if (scope === 'all' && user?.uid) {
        await saveDJDefaults(user.uid, fields);
      }
      const savedNote = scope === 'all' ? ' (saved as default)' : '';
      toast.success(pending.key === 'queueDepth'
        ? `Queue depth set to ${pending.value}${savedNote}`
        : `Lock songs when queued turned ${pending.value ? 'on' : 'off'}${savedNote}`);
    } catch {
      toast.error('Failed to update queue settings');
    }
  };

  // Detect blocked songs sitting in the queue
  const blockedInQueue = useMemo(() => {
    if (songVerdicts.size === 0) return [];
    return requests.filter(r => {
      if (ignoredBlockedIds.has(r.id)) return false;
      const key = `${r.song.title.toLowerCase().trim()}|${r.song.artist.toLowerCase().trim()}`;
      return songVerdicts.get(key) === 'blocked';
    });
  }, [requests, songVerdicts, ignoredBlockedIds]);

  // Trim search query for filtering (whitespace-only is treated as empty)
  const trimmedSearchQuery = searchQuery.trim();

  // Check if any filters are active (using trimmed search)
  const hasActiveFilters = trimmedSearchQuery !== '' || voteFilter !== 'all';

  // Compute which request IDs match the search query (for highlight mode — queue is never filtered)
  const computedMatchIds = useMemo(() => {
    if (!trimmedSearchQuery) return [];
    const query = trimmedSearchQuery.toLowerCase();
    return requests
      .filter(r =>
        r.song.title.toLowerCase().includes(query) ||
        r.song.artist.toLowerCase().includes(query)
      )
      .map(r => r.id);
  }, [requests, trimmedSearchQuery]);

  // Sync computed match IDs into state and reset index when query changes
  useEffect(() => {
    setSearchMatchIds(computedMatchIds);
    setSearchMatchIndex(0);
  }, [computedMatchIds]);

  // Scroll to the focused match when searchMatchIndex changes
  useEffect(() => {
    if (searchMatchIds.length === 0) return;
    const focusedId = searchMatchIds[searchMatchIndex];
    if (!focusedId) return;
    document.getElementById(`queue-card-${focusedId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [searchMatchIndex, searchMatchIds]);

  const compareQueueDisplayOrder = (a: SongRequest, b: SongRequest): number => {
    // VIP priority songs go to the top
    const aVip = a.vipPriority ? 1 : 0;
    const bVip = b.vipPriority ? 1 : 0;
    if (aVip !== bVip) return bVip - aVip;
    // Locked songs come next — the desktop helper treats the top of this order as "next"
    const aLocked = isPinnedNext(a) ? 1 : 0;
    const bLocked = isPinnedNext(b) ? 1 : 0;
    if (aLocked !== bLocked) return bLocked - aLocked;
    // Songs locked by Queue Next keep the order they were queued in
    const queueLockOrder = compareQueueLocks(a, b);
    if (queueLockOrder !== 0) return queueLockOrder;
    // Crowd favorites come right after locked songs; voted-out songs go to the bottom
    const byCrowd = compareCrowdTier(a, b);
    if (byCrowd !== 0) return byCrowd;
    // If using manual order, sort by that
    if (useManualOrder) {
      const byPosition = compareManualPositions(a, b);
      if (byPosition !== 0) return byPosition;
    }
    // Otherwise, sort by score (Copeland in ranked mode, votes otherwise)
    return sortDirection === 'desc'
      ? getSortValue(b) - getSortValue(a)
      : getSortValue(a) - getSortValue(b);
  };

  const queueOrder = level === 'checklist' ? compareChecklistOrder(compareQueueDisplayOrder) : compareQueueDisplayOrder;

  // Apply filters to requests
  const filteredRequests = requests
    .filter(request => {
      // Vote count filter
      if (voteFilter !== 'all') {
        const minVotes = parseInt(voteFilter.replace('+', ''));
        if (request.voteCount < minVotes) return false;
      }

      return true;
    })
    .sort(queueOrder);

  // The order the DJ sees, ignoring the vote filter — top-X, Clean Queue and the banners all read from this
  const displayQueue = [...requests].sort(queueOrder);
  const topStatus = isManualPlayMode(playMode.id) ? topQueueStatus(displayQueue, queueDepth) : null;
  const nextSong = nextPlayerSong(displayQueue);
  const queuedInPlayerCount = allQueuedRows(displayQueue).length;
  // Late to queue the next song while others are already in the player (ITER-183): a card warning, not the takeover
  const lateQueue = !nextSongWarningsQuiet && lateQueueApplies({ level, manual: isManualPlayMode(playMode.id), nextSong, queuedInPlayerCount });
  const lateStage = lateQueue ? lateQueueStage(songEndCountdown) : null;
  const lateOutlineFor = (request: SongRequest) =>
    (request.id === nextSong?.id && !request.localFile ? lateQueueCardOutline(lateStage) : null);
  const songEndBannerShown = songEndBanner({
    countdownMs: songEndCountdown,
    queueEmpty: filteredRequests.length === 0,
    hasNextSong: nextSong !== null,
    nextQueued: nextSong !== null && isQueuedInPlayer(nextSong),
    lateQueue,
    warn45Dismissed,
    warn30Dismissed: warn30Dismissed || warn30DismissedForCurrentSong,
    lastSongActive: nextSongWarningsQuiet,
  });

  // Publish the top of the queue (ignoring the vote filter) so the desktop helper advances to the same song
  const nextUp = displayQueue[0] ?? null;
  const nextUpId = nextUp?.id ?? null;
  const lastPublishedNextUpRef = useRef<string | null>(null);
  useEffect(() => {
    if (!eventId || activePlayback?.eventId !== eventId) return;
    const publishKey = `${eventId}|${nextUpId ?? ''}`;
    if (lastPublishedNextUpRef.current === publishKey) return;
    lastPublishedNextUpRef.current = publishKey;
    publishQueueNextUp(eventId, nextUpId);
  }, [eventId, activePlayback?.eventId, nextUpId, publishQueueNextUp]);

  // "Bridge didn't recognize" flags for Now Playing and the top of the queue (ITER-172)
  const flaggableSongs = [
    ...(nowPlayingRequest && !nowPlayingRequest.localFile ? [nowPlayingRequest] : []),
    ...playerSongsInOrder(displayQueue).slice(0, RECOGNITION_WARNING_TOP),
  ];
  const recognition = useRecognitionFlags({ requests: flaggableSongs, provider: playMode.provider, enabled: level === 'bridge' });
  const playerMatchesMode = playerNameMatchesMode(detectedPlayerName, playMode);
  const canFlag = canFlagPick({ level, playerMatchesMode });
  const nowPlayingFlag = nowPlayingRequest && level === 'bridge' ? recognition.flagOf(nowPlayingRequest.id) : null;
  const showRemoveFlagPrompt = shouldPromptRemoveFlag(nowPlayingRequest, nowPlayingFlag, dismissedRemovePrompts);
  const playerMismatchHere = playerMismatch?.eventId === eventId ? playerMismatch : null;
  const detectedTrack = detectedTrackFor({ level, mismatch: playerMismatchHere, position: activePlayback?.playerPosition ?? null });

  const errorMessage = (error: unknown) => (error instanceof Error && error.message ? error.message : 'unknown error');

  const handleSetRecognitionFlag = async (request: SongRequest, flagged: boolean, source: 'manual' | 'prompt') => {
    setFlagBusy(true);
    try {
      await setBridgeRecognitionFlag({ requestId: request.id, flagged, source });
      recognition.refresh();
      toast.success(flagged ? `"${request.song.title}" flagged for ${playerLabel}` : 'Flag removed');
    } catch (error) {
      console.error('Could not change the Bridge recognition flag:', error);
      toast.error(`Couldn't ${flagged ? 'flag the song' : 'remove the flag'}: ${errorMessage(error)}`);
    } finally {
      setFlagBusy(false);
    }
  };

  // Off-track recovery (ITER-173): the prompt's two buttons and the Fix Now Playing dialog end here
  const handleRecoveryOutcome = async (outcome: RecoveryOutcome) => {
    try {
      if (outcome.kind === 'pickFromQueue') {
        const picked = displayQueue.find(request => request.id === outcome.requestId);
        if (!picked) {
          toast.error('That song is no longer in the queue');
          return;
        }
        const heard = detectedTrack ? { title: detectedTrack.title, artist: detectedTrack.artist } : null;
        await advanceQueueTo(picked, { previousTo: outcome.previousTo, manualPick: true, playerTrack: heard });
        setFixDialog(null);
        if (!outcome.flagPicked) {
          toast.success('Now Playing fixed');
          return;
        }
        try {
          const result = await setBridgeRecognitionFlag({
            requestId: picked.id,
            flagged: true,
            source: 'off_track',
            ...(heard ? { heard } : {}),
          });
          recognition.refresh();
          toast.success(result.learned
            ? `Now Playing fixed. "${picked.song.title}" is flagged for ${playerLabel}, and Bridge will recognize this version next time`
            : `Now Playing fixed. "${picked.song.title}" is flagged for ${playerLabel}`);
        } catch (error) {
          console.error('Could not flag the picked song:', error);
          toast.error(`Now Playing fixed, but the flag didn't save: ${errorMessage(error)}`);
        }
      } else if (outcome.kind === 'letItPlay') {
        if (!user || !eventId) {
          toast.error('Sign in again to fix Now Playing');
          return;
        }
        const { title, artist, durationMs } = outcome.track;
        const created = await createRequest(eventId, {
          song: { title, artist, explicitFlag: false, ...(durationMs ? { durationMs } : {}) },
          requestedBy: { oderId: user.uid, nickname: 'DJ' },
          initialStatus: 'queued',
          entrySource: 'dj-added',
          unplannedPlayAt: new Date().toISOString(),
        });
        await advanceQueueTo(created, { previousTo: outcome.previousTo, playerTrack: { title, artist } });
        setFixDialog(null);
        toast.success(`Letting "${title}" finish`);
      } else {
        await advanceQueueTo(null, { previousTo: outcome.previousTo });
        setFixDialog(null);
        toast.success('Now Playing cleared');
      }
    } catch (error) {
      console.error('Could not fix Now Playing:', error);
      toast.error(`Couldn't fix Now Playing: ${errorMessage(error)}`);
    }
  };

  // Bridge connection lost: tell the DJ once when the heartbeat goes stale
  const previousBridgeKindRef = useRef(bridgeStatus.kind);
  useEffect(() => {
    if (previousBridgeKindRef.current === 'live-here' && bridgeStatus.kind === 'lost') {
      toast.error('Bridge connection lost — switched to checklist mode');
    }
    previousBridgeKindRef.current = bridgeStatus.kind;
  }, [bridgeStatus.kind]);

  // Local files: which device plays them, and the deck that plays them
  const localPlayerClaim = localPlayerClaimStatus(localFilePlayer, deviceId, eventId ?? '', Date.now());
  const isLocalPlayerDevice = localPlayerClaim === 'this-device';
  const localFadeMs = crossfadeSecondsFor(eventData?.settings) * 1000;

  useEffect(() => {
    if (!eventId || !isLocalPlayerDevice) return;
    const timer = setInterval(() => beatLocalFilePlayer(eventId), HEARTBEAT_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [eventId, isLocalPlayerDevice, beatLocalFilePlayer]);

  useEffect(() => {
    if (!eventId) return;
    return () => {
      releaseLocalFilePlayer(eventId).catch((error) => {
        console.error('Could not release the local file player when leaving the Queue page:', error);
      });
    };
  }, [eventId, releaseLocalFilePlayer]);

  const localDeck = useLocalFileDeck({
    eventId,
    nowPlaying: nowPlayingRequest,
    nextUp,
    isPlayerDevice: isLocalPlayerDevice,
    level,
    crossfadeMs: localFadeMs,
    onCrossfadeToNext: (nextId) => {
      advanceQueueOrToast(requests.find(r => r.id === nextId) ?? null);
    },
    onTellBridgePlay: () => {
      sendRemoteCommand('play').catch((error) => {
        console.error('Could not tell Bridge to press play after the local file:', error);
        toast.error(`Could not start ${playerLabel} — press play now`);
      });
    },
    onFileEndedChecklist: () => {
      advanceQueueOrToast(nextUp);
    },
    onError: (message) => toast.error(message),
  });

  const handleClaimLocalPlayer = async () => {
    if (!eventId) return;
    try {
      await claimLocalFilePlayer(eventId);
      localDeck.unblock();
    } catch {
      toast.error('Could not set this device to play local files');
    }
  };

  const nowPlayingIsLocalFile = Boolean(nowPlayingRequest?.localFile?.storageUrl);
  const localFilePlaysHere = nowPlayingIsLocalFile && isLocalPlayerDevice;
  const hasLocalFiles = Boolean(eventData?.settings?.localFilesEnabled || nowPlayingIsLocalFile || requests.some(r => r.localFile));
  const playerSongAfterFiles = playerSongToPrepare(nowPlayingRequest, nextUp);

  // Claim this device by itself when no device holds a claim, so the DJ doesn't click every show
  const autoClaimedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!eventId || !localFilePlayerLoaded || !hasLocalFiles) return;
    if (autoClaimedForRef.current === eventId) return;
    if (!shouldAutoClaim(localFilePlayer, eventId, Date.now())) return;
    autoClaimedForRef.current = eventId;
    claimLocalFilePlayer(eventId).catch(() => {
      toast.error('Could not set this device to play local files');
    });
  }, [eventId, localFilePlayerLoaded, hasLocalFiles, localFilePlayer, claimLocalFilePlayer]);

  // During the last local file in a row, lock the player song that follows so votes can't move it.
  // Only the device playing the files writes this, so two open Queue pages never race.
  const autoLockedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (!localFilePlaysHere || !nowPlayingRequest || !playerSongAfterFiles || playerSongAfterFiles.lockedNext) return;
    if (autoLockedForRef.current === nowPlayingRequest.id) return;
    autoLockedForRef.current = nowPlayingRequest.id;
    const songId = playerSongAfterFiles.id;
    updateRequest(songId, { lockedNext: true })
      .then(() => {
        setRequests(prev => prev.map(r => (r.id === songId ? { ...r, lockedNext: true } : r)));
      })
      .catch((error) => {
        console.error('Could not lock the song after the local file:', error);
        toast.error('Could not lock the next song');
      });
  }, [localFilePlaysHere, nowPlayingRequest?.id, playerSongAfterFiles?.id, playerSongAfterFiles?.lockedNext]);

  const handleRemotePlayerPrevious = async () => {
    try {
      await sendRemoteCommand('previous');
      toast.success('Went back');
    } catch {
      toast.error('Failed to send previous command');
    }
  };

  const handleRemotePlayerPause = async () => {
    try {
      await sendRemoteCommand('pause');
      // Freeze countdown at current wall-clock time
      if (countdownPausedAt === null) {
        setCountdownPausedAt(Date.now());
      }
      toast.success('Paused');
    } catch {
      toast.error('Failed to send pause command');
    }
  };

  const handleRemotePlayerPlay = async () => {
    try {
      await sendRemoteCommand('play');
      // Resume countdown — accumulate paused duration
      if (countdownPausedAt !== null) {
        pauseOffsetRef.current += Date.now() - countdownPausedAt;
        setCountdownPausedAt(null);
      }
      toast.success('Playing');
    } catch {
      toast.error('Failed to send play command');
    }
  };

  const handleRemotePlayerSkip = async () => {
    try {
      await sendRemoteCommand('skip');
      toast.success('Skipped to next');
    } catch {
      toast.error('Failed to send skip command');
    }
  };

  const handleAcknowledgePlayerMismatch = (detectedAt: string) => {
    acknowledgePlayerMismatch(detectedAt).catch((error) => {
      console.error('Could not acknowledge the player note:', error);
      toast.error('Could not update the player note');
    });
  };

  // F17: Queue time cutoff calculation
  // Crossfade from event settings (seconds)
  const crossfadeSec = eventData?.settings?.crossfadeSeconds ?? 0;
  const crossfadeMs = crossfadeSec * 1000;

  // Baseline time for queue calculations: use event startTime if event hasn't started yet
  const baselineMs = (() => {
    if (!eventData?.startTime || !eventData?.date) return Date.now();
    const [yr, mo, dy] = eventData.date.split('-').map(Number);
    const [startH, startM] = eventData.startTime.split(':').map(Number);
    const startMs = new Date(yr, mo - 1, dy, startH, startM, 0, 0).getTime();
    return Date.now() < startMs ? startMs : Date.now();
  })();
  const eventNotStarted = baselineMs > Date.now();

  // Total queue playback time (including now-playing remaining)
  const totalQueueTimeMs = (() => {
    let totalMs = 0;
    let transitionCount = 0;

    // Add remaining time for now-playing song (only when event has started)
    if (!eventNotStarted) {
      if (nowPlayingRequest?.song?.durationMs && nowPlayingRequest.timestamps?.startedPlaying) {
        const elapsed = Date.now() - new Date(nowPlayingRequest.timestamps.startedPlaying).getTime();
        const remaining = Math.max(0, nowPlayingRequest.song.durationMs - elapsed);
        totalMs += remaining;
        if (filteredRequests.length > 0) transitionCount++;
      }
    }

    // Add all queued song durations
    for (let i = 0; i < filteredRequests.length; i++) {
      totalMs += filteredRequests[i].song?.durationMs || 210000;
      if (i < filteredRequests.length - 1) transitionCount++; // between queued songs
    }

    // Subtract crossfade overlap for each transition
    totalMs -= crossfadeMs * transitionCount;
    return Math.max(0, totalMs);
  })();

  // Helper: format milliseconds as "Xh Xm Xs" (skip zero parts)
  const formatDurationHms = (ms: number): string => {
    const totalSec = Math.floor(Math.abs(ms) / 1000);
    const hrs = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    const parts: string[] = [];
    if (hrs > 0) parts.push(`${hrs}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 || parts.length === 0) parts.push(`${secs}s`);
    return parts.join(' ');
  };

  // Metric 3: Total queue value
  const queueValueDisplay = formatDurationHms(totalQueueTimeMs);

  // Metric 2: Queue vs end time gap (only when end time defined)
  const queueGapDisplay = (() => {
    if (!eventData?.endTime) return null;
    const [endH, endM] = eventData.endTime.split(':').map(Number);
    const edStr = eventData.endDate || eventData.date;
    const [eyr, emo, edy] = edStr.split('-').map(Number);
    const endDate = new Date(eyr, emo - 1, edy, endH, endM, 0, 0);
    const eventRemainingMs = endDate.getTime() - baselineMs;
    const diffMs = totalQueueTimeMs - eventRemainingMs;
    const timeStr = formatDurationHms(diffMs);
    if (diffMs > 60000) return { text: `${timeStr} over`, color: 'amber' as const };
    if (diffMs < -60000) return { text: `${timeStr} short`, color: 'red' as const };
    return { text: 'On track', color: 'green' as const };
  })();

  // Estimated queue end time (when will the last song finish)
  const queueEndMs = baselineMs + totalQueueTimeMs;

  // Metric 1: Estimated actual end time (factoring in scheduled end time + overlap setting)
  const actualEndTimeDisplay = (() => {
    if (totalQueueTimeMs <= 0 && !eventData?.endTime) return null;

    let actualEndMs: number;

    if (!eventData?.endTime) {
      // No scheduled end — music ends when queue runs out
      actualEndMs = queueEndMs;
    } else {
      // Parse scheduled end time using explicit endDate
      const [endH, endM] = eventData.endTime.split(':').map(Number);
      const edStr = eventData.endDate || eventData.date;
      const [eyr, emo, edy] = edStr.split('-').map(Number);
      const scheduledEndMs = new Date(eyr, emo - 1, edy, endH, endM, 0, 0).getTime();

      if (eventData.settings?.allowOverlap) {
        // Overlap ON: last song plays through past end time, but if queue is short
        // music stops when queue runs out (not at scheduled end)
        actualEndMs = totalQueueTimeMs > 0 ? queueEndMs : scheduledEndMs;
      } else {
        // Overlap OFF controls playback only — display always shows real queue end
        actualEndMs = totalQueueTimeMs > 0 ? queueEndMs : scheduledEndMs;
      }
    }

    const endD = new Date(actualEndMs);
    const h = endD.getHours();
    const m = endD.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    const timeStr = `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;

    // Color: green if at/before scheduled end, amber if after
    if (eventData?.endTime) {
      const [eH, eM] = eventData.endTime.split(':').map(Number);
      const edStr2 = eventData.endDate || eventData.date;
      const [eyr2, emo2, edy2] = edStr2.split('-').map(Number);
      const schedMs = new Date(eyr2, emo2 - 1, edy2, eH, eM, 0, 0).getTime();
      const isAfter = actualEndMs > schedMs + 60000;
      const isBefore = actualEndMs < schedMs - 60000;
      return { time: timeStr, isAfterScheduled: isAfter, isBeforeScheduled: isBefore };
    }
    return { time: timeStr, isAfterScheduled: false, isBeforeScheduled: false };
  })();

  // Format current time as 12h display
  const currentTimeDisplay = (() => {
    const h = currentTime.getHours();
    const m = currentTime.getMinutes();
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  })();

  // Cutoff index (which songs won't make the cut)
  const cutoffIndex = (() => {
    if (!eventData?.endTime) return -1;
    const [endH, endM] = eventData.endTime.split(':').map(Number);
    const edStr = eventData.endDate || eventData.date;
    const [eyr, emo, edy] = edStr.split('-').map(Number);
    const endDate = new Date(eyr, emo - 1, edy, endH, endM, 0, 0);
    const remainingMs = endDate.getTime() - baselineMs;
    if (remainingMs <= 0) return 0; // Past end time

    let cumulativeMs = 0;
    // If there's a now-playing song (only when event has started)
    if (!eventNotStarted) {
      if (nowPlayingRequest?.song?.durationMs && nowPlayingRequest.timestamps?.startedPlaying) {
        const elapsed = Date.now() - new Date(nowPlayingRequest.timestamps.startedPlaying).getTime();
        cumulativeMs += Math.max(0, nowPlayingRequest.song.durationMs - elapsed);
      }
    }

    for (let i = 0; i < filteredRequests.length; i++) {
      // Subtract crossfade for each transition (current→first, or song→next)
      if (i > 0 || cumulativeMs > 0) cumulativeMs -= crossfadeMs;
      const songDuration = filteredRequests[i].song?.durationMs || 210000; // default 3:30
      cumulativeMs += songDuration;
      if (cumulativeMs > remainingMs) {
        return eventData.settings?.allowOverlap ? i + 1 : i;
      }
    }
    return -1; // All songs fit
  })();

  // Infinite scroll calculations — if only a few items remain beyond the window, show them all
  const effectiveVisibleCount = (filteredRequests.length - visibleCount) <= LOAD_MORE_COUNT
    ? filteredRequests.length
    : visibleCount;
  const visibleRequests = filteredRequests.slice(0, effectiveVisibleCount);
  const dragDisplayRequests = orderByFrozenIds(visibleRequests, frozenQueueIds);
  const dragBlockedByFilter = voteFilter !== 'all';
  const hasMore = effectiveVisibleCount < filteredRequests.length;

  // Load more function for infinite scroll
  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    // Small delay to show loading indicator and prevent rapid firing
    setTimeout(() => {
      setVisibleCount(prev => Math.min(prev + LOAD_MORE_COUNT, filteredRequests.length));
      setIsLoadingMore(false);
    }, 100);
  }, [isLoadingMore, hasMore, filteredRequests.length]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    // Clean up previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Create new observer
    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { rootMargin: '200px' } // Start loading 200px before reaching the bottom
    );

    // Observe the load more trigger element
    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, isLoadingMore, loadMore]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(INITIAL_ITEMS_COUNT);
  }, [searchQuery, voteFilter, sortDirection, scoringMode, djSortMode]);

  // Persist filter state to localStorage when filters change
  useEffect(() => {
    if (eventId) {
      persistFilters(eventId, { searchQuery, voteFilter, sortDirection });
    }
  }, [eventId, searchQuery, voteFilter, sortDirection]);

  // Sync filter state to URL query params
  useEffect(() => {
    const newParams = new URLSearchParams();

    // Only add params if they differ from defaults
    if (searchQuery.trim()) {
      newParams.set('search', searchQuery.trim());
    }
    if (voteFilter !== 'all') {
      newParams.set('votes', voteFilter);
    }
    if (sortDirection !== 'desc') {
      newParams.set('sort', sortDirection);
    }

    // Update URL without causing navigation
    setSearchParams(newParams, { replace: true });
  }, [searchQuery, voteFilter, sortDirection, setSearchParams]);


  // NFC tag writing
  const handleWriteNfc = async () => {
    if (!eventData?.eventUrl) return;
    const url = `${window.location.origin}/e/${eventData.eventUrl}`;
    setNfcWriting(true);
    const controller = new AbortController();
    nfcAbortRef.current = controller;
    try {
      const ndef = new NDEFReader();
      await ndef.write(
        { records: [{ recordType: 'url', data: url }] },
        { signal: controller.signal }
      );
      if (nfcLockTag) {
        await ndef.makeReadOnly({ signal: controller.signal });
        toast.success('NFC tag written and permanently locked!');
      } else {
        toast.success('NFC tag written! Attendees can tap to join.');
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        toast('NFC write cancelled', { icon: '✋' });
      } else {
        toast.error('NFC write failed. Make sure NFC is enabled.');
      }
    } finally {
      setNfcWriting(false);
      nfcAbortRef.current = null;
    }
  };

  const handleCancelNfc = () => {
    nfcAbortRef.current?.abort();
    nfcAbortRef.current = null;
    setNfcWriting(false);
  };

  if (loading) {
    return <div className="animate-pulse">Loading queue...</div>;
  }

  return (
    <div className="space-y-6">

      {/* Clean Queue Modal */}
      {showCleanQueueModal && (() => {
        const rows = cleanQueueRows(displayQueue, queueDepth);
        const allQueued = allQueuedRows(displayQueue);
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-md">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900 dark:text-white">Clean Up Player Queue</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {allQueued.length} songs queued in {playerLabel} — depth is set to {queueDepth}.
                      Starting from the top of this list, remove each song from {playerLabel}, then mark it removed here.
                    </p>
                  </div>
                  <button onClick={() => setShowCleanQueueModal(false)} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="p-4 space-y-2 max-h-72 overflow-y-auto">
                {rows.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">Queue is within depth limit.</p>
                ) : rows.map(({ request: song, position }) => (
                  <div key={song.id} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                    <span className="text-xs font-mono text-gray-400 w-6">#{position}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{song.song.title}</p>
                      <p className="text-xs text-gray-500 truncate">{song.song.artist}</p>
                    </div>
                    <button
                      onClick={() => handleUnqueueFromPlayer(song)}
                      disabled={cleaningQueueIds.has(song.id)}
                      className="flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50 transition-colors"
                    >
                      {cleaningQueueIds.has(song.id) ? 'Removing…' : 'Mark Removed'}
                    </button>
                  </div>
                ))}
              </div>
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-wrap items-center gap-2">
                {rows.length > 0 && (
                  <button
                    onClick={() => handleMarkRowsRemoved(rows.map(row => row.request))}
                    disabled={cleaningQueueIds.size > 0}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 disabled:opacity-50 transition-colors"
                  >
                    {cleaningQueueIds.size > 0 ? 'Removing…' : `Mark All Removed (${rows.length})`}
                  </button>
                )}
                {allQueued.length > 0 && (
                  <button
                    onClick={() => handleClearAllQueued(allQueued.map(row => row.request))}
                    disabled={cleaningQueueIds.size > 0}
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
                  >
                    {`Clear all (${allQueued.length})`}
                  </button>
                )}
                <button onClick={() => setShowCleanQueueModal(false)} className="btn btn-secondary text-sm ml-auto">Done</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Queue Depth Confirmation Modal */}
      {pendingQueueSetting !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-5 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {pendingQueueSetting.key === 'queueDepth' ? 'Change Queue Depth' : 'Lock Songs When Queued'}
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {pendingQueueSetting.key === 'queueDepth'
                  ? `Change player queue depth from ${queueDepth} to ${pendingQueueSetting.value}?`
                  : `Turn ${pendingQueueSetting.value ? 'on' : 'off'} Lock songs when queued?`}
              </p>
            </div>
            <div className="p-4 space-y-2">
              <button
                onClick={() => handleQueueSettingConfirm('all')}
                className="w-full px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors"
              >
                Change for all future events
              </button>
              <button
                onClick={() => handleQueueSettingConfirm('event')}
                className="w-full px-4 py-2.5 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Change for this event only
              </button>
              <button
                onClick={() => handleQueueSettingConfirm('cancel')}
                className="w-full px-4 py-2.5 text-sm font-medium rounded-lg text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Desktop Helper status indicator */}
      <div className="mb-3">
        {bridgeStatus.kind === 'live-here' ? (
          <div className="flex flex-col gap-1.5 px-3 py-2 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <div className="flex items-center gap-2">
            <Monitor className="w-4 h-4 text-green-500 animate-pulse flex-shrink-0" />
            <span className="text-xs font-medium text-green-700 dark:text-green-400">
              Bridge connected{detectedPlayerName ? ` (${detectedPlayerName})` : ''}
            </span>
            {!playerNameMatchesMode(detectedPlayerName, playMode) && (
              <span className="text-xs text-gray-500 dark:text-gray-400">
                Bridge sees {detectedPlayerName}; this event plays {playerLabel}. Bridge can&rsquo;t follow this player, so song flags are off.
              </span>
            )}
            <button
              onClick={unlockPlayback}
              className="ml-auto text-xs text-green-600 dark:text-green-400 hover:text-red-500 dark:hover:text-red-400 underline"
            >
              Stop
            </button>
          </div>
          {isBridgeOutdated(activePlayback?.bridgeVersion) && (
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Bridge {activePlayback?.bridgeVersion ?? '(older version)'} — update to {MIN_BRIDGE_VERSION}+ for the live countdown and off-track protection.{' '}
              <a
                href={BRIDGE_LATEST_RELEASE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80 underline font-medium"
              >
                Download Latest
              </a>
            </p>
          )}
          </div>
        ) : bridgeStatus.kind === 'live-elsewhere' ? (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
            <Monitor className="w-4 h-4 text-amber-500 flex-shrink-0" />
            <span className="text-xs font-medium text-amber-700 dark:text-amber-400 truncate">
              Bridge on: {bridgeStatus.eventName}
            </span>
            <button
              onClick={() => sendSwitchEventCommand(eventId!, eventData?.name || 'Event')}
              className="ml-auto text-xs text-amber-600 dark:text-amber-400 hover:text-primary underline whitespace-nowrap"
            >
              Switch here
            </button>
          </div>
        ) : (
          <div className={`flex flex-col gap-1.5 px-3 py-2 rounded-lg border ${
            bridgeStatus.kind === 'lost'
              ? 'bg-red-50 dark:bg-red-900/20 border-red-300 dark:border-red-800'
              : 'bg-gray-50 dark:bg-gray-800/50 border-gray-200 dark:border-gray-700'
          }`}>
            <div className="flex items-center gap-2">
              <Monitor className={`w-4 h-4 flex-shrink-0 ${bridgeStatus.kind === 'lost' ? 'text-red-500' : 'text-gray-400'}`} />
              {bridgeStatus.kind === 'lost' ? (
                <span className="text-xs font-medium text-red-700 dark:text-red-400">
                  Bridge connection lost — the queue is a checklist until Bridge reconnects.
                </span>
              ) : (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Bridge not connected
                </span>
              )}
              <button
                onClick={launchBridge}
                className="ml-auto text-xs text-primary hover:text-primary/80 underline whitespace-nowrap"
              >
                Launch
              </button>
            </div>
            <BridgeLaunchNotice state={bridgeLaunchState} onDismiss={dismissBridgeLaunch} onRecheck={recheckBridge} compact />
          </div>
        )}
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Song Queue</h2>
          <p className="text-gray-600 dark:text-gray-400">
            Approved songs ready to play
          </p>
        </div>
        <button
          onClick={() => setShowLibraryQueueModal(true)}
          className="btn btn-secondary text-sm"
        >
          Add from Library
        </button>
        <div className="text-right">
          {/* Clean Queue button — shown when queuedInPlayer count exceeds depth */}
          {queuedInPlayerCount > queueDepth && (
            <button
              onClick={() => setShowCleanQueueModal(true)}
              className="mb-1 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50 transition-colors border border-amber-300 dark:border-amber-700"
            >
              <Trash className="w-3 h-3" />
              Clean Queue ({queuedInPlayerCount}/{queueDepth})
            </button>
          )}
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {filteredRequests.length} songs in queue
          </span>
          {/* Queue depth inline editor */}
          <div className="flex items-center gap-1 mt-1">
            <span className="text-xs text-gray-400 dark:text-gray-500">Player Queue Depth:</span>
            {editingQueueDepth ? (
              <input
                type="number"
                min={0}
                max={10}
                value={queueDepthDraft}
                onChange={e => setQueueDepthDraft(clampQueueDepth(Number(e.target.value)))}
                onBlur={() => handleSaveQueueDepth(queueDepthDraft)}
                onKeyDown={e => { if (e.key === 'Enter') handleSaveQueueDepth(queueDepthDraft); if (e.key === 'Escape') setEditingQueueDepth(false); }}
                className="w-12 text-xs text-center border border-primary rounded px-1 py-0.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-primary"
                autoFocus
              />
            ) : (
              <button
                onClick={() => { setQueueDepthDraft(queueDepth); setEditingQueueDepth(true); }}
                className="text-xs font-medium text-gray-600 dark:text-gray-300 hover:text-primary dark:hover:text-primary underline decoration-dashed"
                title="Edit queue depth — how many songs to keep queued in the player at once (0 = queue each song when prompted)"
              >
                {queueDepth}
              </button>
            )}
          </div>
          {isManualPlayMode(playMode.id) && (queueDepth === 0 ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Queue each song when prompted</p>
          ) : topStatus && (
            <p className={`text-xs font-medium mt-0.5 ${topStatus.queuedCount === topStatus.depth ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
              Top {topStatus.depth}: {topStatus.queuedCount} of {topStatus.depth} queued
            </p>
          ))}
          {rankingStats && rankingStats.totalRankings > 0 && (
            <div className="text-xs text-amber-600 dark:text-amber-400">
              <p>{isRankedMode ? 'Ranked Mode • ' : ''}{rankingStats.uniqueVoters} voters{staleCount > 0 ? ` (${staleCount} stale)` : ''}</p>
              {hiddenGems.length > 0 && thresholdMet && (
                <p className="text-emerald-600 dark:text-emerald-400">
                  💎 {hiddenGems.length} hidden gem{hiddenGems.length !== 1 ? 's' : ''} found
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Live Attendee Stats Strip */}
      {(attendeeStats.visitors > 0 || attendeeStats.requesters > 0) && (
        <div className="flex items-center gap-4 px-3 py-2 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
          <div className="flex items-center gap-1.5" title="Total unique page visitors">
            <Users className="w-3.5 h-3.5 text-gray-400" />
            <span className="font-medium text-gray-700 dark:text-gray-300">{attendeeStats.visitors}</span>
            <span className="text-gray-400">visitors</span>
          </div>
          <div className="flex items-center gap-1.5" title="Attendees who submitted at least 1 request">
            <Music className="w-3.5 h-3.5 text-blue-400" />
            <span className="font-medium text-gray-700 dark:text-gray-300">{attendeeStats.requesters}</span>
            <span className="text-gray-400">requesters</span>
          </div>
          <div className="flex items-center gap-1.5" title="Attendees who cast at least 1 vote">
            <ArrowUp className="w-3.5 h-3.5 text-green-400" />
            <span className="font-medium text-gray-700 dark:text-gray-300">{attendeeStats.voters}</span>
            <span className="text-gray-400">voters</span>
          </div>
          {attendeeStats.rankers > 0 && (
            <div className="flex items-center gap-1.5" title="Attendees who submitted rankings">
              <BarChart3 className="w-3.5 h-3.5 text-amber-400" />
              <span className="font-medium text-gray-700 dark:text-gray-300">{attendeeStats.rankers}</span>
              <span className="text-gray-400">rankers</span>
            </div>
          )}
        </div>
      )}

      {/* Overflow: Raise Limit Button */}
      {OVERFLOW_POOL_ENABLED && eventData?.settings?.overflowEnabled && (() => {
        if (overflowRequests.length === 0) return null;
        return (
          <button
            onClick={() => setShowLimitRaiseModal(true)}
            className="flex items-center gap-2 px-3 py-2 bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg text-sm font-medium text-indigo-700 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors"
          >
            <ArrowUp className="w-4 h-4" />
            Raise Limit
            <span className="px-1.5 py-0.5 bg-indigo-200 dark:bg-indigo-800 rounded-full text-xs">{overflowRequests.length}</span>
          </button>
        );
      })()}

      {/* Requests/Voting Mode Control */}
      <div className="flex bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
        {([
          { mode: 'all-open' as OpenCloseMode, label: 'All Open', icon: ToggleRight, color: 'bg-green-500 text-white' },
          { mode: 'requests-closed' as OpenCloseMode, label: 'Req. Closed', icon: StopCircle, color: 'bg-amber-500 text-white' },
          { mode: 'all-closed' as OpenCloseMode, label: 'All Closed', icon: Lock, color: 'bg-red-500 text-white' },
        ]).map(({ mode, label, icon: Icon, color }) => (
          <button
            key={mode}
            onClick={() => handleSetMode(mode)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium transition-colors ${
              getCurrentMode() === mode
                ? color
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Close requests at show start (ITER-137): status only; the mode control above does the switching */}
      {requestCloseStatus && (
        <span
          className={`flex items-center gap-1.5 text-xs ${
            requestCloseStatus.state === 'countdown' || requestCloseStatus.state === 'closed'
              ? 'font-medium text-amber-600 dark:text-amber-400'
              : 'text-gray-500 dark:text-gray-400'
          }`}
        >
          <Clock className="w-3.5 h-3.5" />
          {requestCloseStatus.text}
        </span>
      )}

      {/* Preview Device Indicator */}
      {(
        <div className="flex items-center gap-2">
          {previewSession?.status === 'connected' ? (
            <div className="flex items-center gap-1.5">
              <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-full">
                <Smartphone className="w-3 h-3" />
                Preview Connected
              </span>
              <button
                onClick={handleDisconnectPreview}
                className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                title="Disconnect preview device"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={handleConnectPreviewDevice}
              className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400 hover:text-green-600 dark:hover:text-green-400 bg-gray-100 dark:bg-gray-800 px-2.5 py-1.5 rounded-lg transition-colors"
              title="Connect a second device to listen to requests and send links back"
            >
              <Smartphone className="w-3.5 h-3.5" />
              Connect Preview Device
            </button>
          )}
        </div>
      )}

      {/* Incoming Requests Section */}
      {actionablePendingRequests.length > 0 && (
        <div className="border border-amber-300 dark:border-amber-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setPendingCollapsed(!pendingCollapsed)}
            className="w-full flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
          >
            <div className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              <span className="font-medium text-amber-800 dark:text-amber-300">
                Incoming Requests
              </span>
              <span className="px-2 py-0.5 rounded-full bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 text-xs font-bold">
                {actionablePendingRequests.length}
              </span>
            </div>
            {pendingCollapsed ? (
              <ChevronDown className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            ) : (
              <ChevronUp className="w-4 h-4 text-amber-600 dark:text-amber-400" />
            )}
          </button>
          {!pendingCollapsed && (
            <div className="divide-y divide-amber-200 dark:divide-amber-800">
              {actionablePendingRequests.map((request) => (
                <div key={request.id} className={`flex items-center gap-4 p-3 ${
                  request.song.explicitFlag
                    ? 'bg-red-50 dark:bg-red-900/20 border-l-4 border-l-red-500'
                    : 'bg-white dark:bg-gray-800/50'
                }`}>
                  {request.song.albumArtUrl ? (
                    <img
                      src={request.song.albumArtUrl}
                      alt={`${request.song.title} album art`}
                      className="w-10 h-10 rounded flex-shrink-0 object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 flex-shrink-0 bg-gray-200 dark:bg-gray-700 rounded flex items-center justify-center">
                      <Music className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate flex items-center gap-1.5">
                      <span className="truncate">{request.song.title}</span>
                      {request.practiceMode && (
                        <span className="flex-shrink-0 px-1.5 py-0.5 rounded border border-dashed border-purple-400 dark:border-purple-500 text-purple-600 dark:text-purple-400 text-[10px] font-semibold leading-none" title="Practice/test data">TEST</span>
                      )}
                      {request.song.explicitFlag && (
                        <span className="explicit-badge flex-shrink-0" title="Explicit content">E</span>
                      )}
                      {!PEOPLES_PICK_HIDDEN && request.superRequest && (
                        <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-xs font-medium" title={`The People's Pick — ${request.superRequestCount || '?'} nominations`}>
                          👑 Pick
                        </span>
                      )}
                      {request.coApprovalStatus === 'rejected' && (
                        <span className="flex-shrink-0 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 text-xs font-medium" title={request.coApprovalReason ? `Co-approver: "${request.coApprovalReason}" — approving overrides this` : 'Approving overrides the co-approver'}>
                          <ShieldX className="w-3 h-3" /> Rejected by co-approver
                        </span>
                      )}
                      {songVerdicts.get(`${request.song.title.toLowerCase().trim()}|${request.song.artist.toLowerCase().trim()}`) === 'passed' && (
                        <span title="Previously passed"><ShieldCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /></span>
                      )}
                      {songVerdicts.get(`${request.song.title.toLowerCase().trim()}|${request.song.artist.toLowerCase().trim()}`) === 'blocked' && (
                        <span title="Previously blocked"><ShieldX className="w-3.5 h-3.5 text-red-500 flex-shrink-0" /></span>
                      )}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{request.song.artist}</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {attendeeDisplayName(request.requestedBy.nickname, request.requestedBy.oderId)}
                      {request.message && (
                        <span className="ml-1.5 inline-flex items-center gap-0.5 text-primary">
                          <MessageSquare className="w-3 h-3" />
                          <span className="italic">"{request.message}"</span>
                        </span>
                      )}
                    </p>
                    {request.song.explicitFlag && (
                      <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 font-medium flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Explicit content — use AI analysis to review
                      </p>
                    )}
                    {isHeldByOther(request, 'dj', Date.now(), coApprovalDjActive) && (
                      <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-0.5 font-medium">
                        {holderLabel(request.reviewHold)} is reviewing
                      </p>
                    )}
                    <ReviewedByBadge reviewedBy={request.reviewedBy} />
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleAnalyzeSong(request.song.title, request.song.artist)}
                      className={`p-2 rounded-lg transition-colors ${
                        request.song.explicitFlag
                          ? 'text-purple-600 bg-purple-100 dark:bg-purple-900/30 hover:bg-purple-200 dark:hover:bg-purple-900/50'
                          : 'text-purple-500 hover:bg-purple-500/10'
                      }`}
                      title="AI Song Analysis"
                    >
                      <Sparkles className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleApproveRequest(request.id)}
                      disabled={processingIds.has(request.id) || isHeldByOther(request, 'dj', Date.now(), coApprovalDjActive)}
                      className={`p-2 text-success hover:bg-success/10 rounded-lg transition-colors ${
                        processingIds.has(request.id) || isHeldByOther(request, 'dj', Date.now(), coApprovalDjActive) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title={isHeldByOther(request, 'dj', Date.now(), coApprovalDjActive) ? `${holderLabel(request.reviewHold)} is reviewing this song` : 'Approve'}
                    >
                      <Check className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleRejectRequest(request.id)}
                      disabled={processingIds.has(request.id) || isHeldByOther(request, 'dj', Date.now(), coApprovalDjActive)}
                      className={`p-2 text-danger hover:bg-danger/10 rounded-lg transition-colors ${
                        processingIds.has(request.id) || isHeldByOther(request, 'dj', Date.now(), coApprovalDjActive) ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      title={isHeldByOther(request, 'dj', Date.now(), coApprovalDjActive) ? `${holderLabel(request.reviewHold)} is reviewing this song` : 'Reject'}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Rejection Confirmation Dialog */}
      {rejectingRequest && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Reject Request?
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-2">
              Reject "{rejectingRequest.song.title}" by {rejectingRequest.song.artist}?
            </p>
            <div className="mb-4">
              <label htmlFor="rejection-reason" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Reason (optional)
              </label>
              <textarea
                id="rejection-reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="e.g., Already played tonight, Not suitable for this venue..."
                className="input w-full h-20 resize-none"
                maxLength={200}
              />
              <p className="text-xs text-gray-500 mt-1">{rejectionReason.length}/200 characters</p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelReject}
                disabled={processingIds.has(rejectingRequest.id)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={processingIds.has(rejectingRequest.id)}
                className={`btn bg-danger text-white hover:bg-danger/90 ${
                  processingIds.has(rejectingRequest.id) ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {processingIds.has(rejectingRequest.id) ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Queue Toolbar: Refresh, Event Link, QR Code */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Queue</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={refreshQueue}
            disabled={refreshing}
            className="btn btn-ghost btn-sm flex items-center gap-1.5"
            title="Refresh queue"
          >
            <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
          {eventData?.eventUrl && (
            <a
              href={`${window.location.origin}/e/${eventData.eventUrl}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost btn-sm flex items-center gap-1.5"
              title="Open event page"
            >
              <ExternalLink className="w-4 h-4" />
              Event Page
            </a>
          )}
          {eventData?.eventUrl && (
            <button
              onClick={() => setShowQrModal(true)}
              className="btn btn-ghost btn-sm flex items-center gap-1.5"
              title="Show QR code"
            >
              <QrCode className="w-4 h-4" />
              QR
            </button>
          )}
          {!isLocked && (
            <button
              onClick={() => setShowAddSongModal(true)}
              className="btn btn-primary btn-sm flex items-center gap-1.5"
              title="Add a song to the queue"
            >
              <Plus className="w-4 h-4" />
              Add Song
            </button>
          )}
          {/* Practice Mode dropdown */}
          {!isLocked && <div className="relative" ref={practiceMenuRef}>
            <button
              onClick={() => setShowPracticeMenu(prev => !prev)}
              className="btn btn-ghost btn-sm flex items-center gap-1.5"
              title="Practice mode — add test data"
            >
              <FlaskConical className="w-4 h-4" />
              Practice
            </button>
            {showPracticeMenu && (
              <div className="absolute right-0 top-full mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50 p-3 space-y-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Add realistic test requests from simulated attendees to learn the queue tools.
                </p>
                <button
                  onClick={handlePopulatePractice}
                  disabled={practiceLoading || hasPracticeRequests}
                  className="w-full btn btn-sm btn-primary flex items-center gap-1.5 justify-center"
                >
                  {practiceLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Plus className="w-3 h-3" />}
                  Populate Test Requests
                </button>
                {hasPracticeRequests && (
                  <button
                    onClick={handleClearPractice}
                    disabled={practiceLoading}
                    className="w-full btn btn-sm btn-ghost text-red-600 dark:text-red-400 flex items-center gap-1.5 justify-center"
                  >
                    {practiceLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash className="w-3 h-3" />}
                    Clear Practice Data
                  </button>
                )}
                {eventData?.eventUrl && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${window.location.origin}/e/${eventData.eventUrl}`);
                      toast.success('Event URL copied — open in incognito to test as attendee');
                      setShowPracticeMenu(false);
                    }}
                    className="w-full btn btn-sm btn-ghost flex items-center gap-1.5 justify-center"
                  >
                    <Copy className="w-3 h-3" />
                    Copy Event URL
                  </button>
                )}
                {eventData?.eventUrl && (
                  <div className="pt-1 border-t border-gray-200 dark:border-gray-700 space-y-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Open multiple attendee windows to simulate real users.
                    </p>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-600 dark:text-gray-300 whitespace-nowrap">Windows:</label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={simulateCount}
                        onChange={e => setSimulateCount(Math.min(10, Math.max(1, parseInt(e.target.value) || 1)))}
                        className="w-16 text-xs border border-gray-300 dark:border-gray-600 rounded px-1.5 py-0.5 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <button
                      onClick={() => {
                        const base = `${window.location.origin}/e/${eventData.eventUrl}`;
                        for (let i = 1; i <= simulateCount; i++) {
                          window.open(`${base}?testUser=${i}`, `_blank`);
                        }
                        setShowPracticeMenu(false);
                      }}
                      className="w-full btn btn-sm btn-ghost flex items-center gap-1.5 justify-center"
                    >
                      <Users className="w-3 h-3" />
                      Simulate Attendees
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>}
          {!isLocked && actionablePendingRequests.length > 0 && (
            <button
              onClick={handleProcessPending}
              className="btn bg-green-500 text-white hover:bg-green-600 btn-sm flex items-center gap-1.5"
              title="Process all pending requests"
            >
              <Inbox className="w-4 h-4" />
              Process Pending ({actionablePendingRequests.length})
            </button>
          )}
        </div>
      </div>

      {/* Queue Health Section — Row 1: Time References */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {/* Current Time — only when event is active */}
        {isEventActive && (
          <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
            <Clock className="w-3 h-3 inline mr-1" />
            Now: {currentTimeDisplay}
          </span>
        )}
        {/* Start Time (editable) */}
        <button
          onClick={handleOpenStartTimeEditor}
          className="btn btn-ghost btn-sm flex items-center gap-1.5"
          title="Click to edit start time"
        >
          <Play className="w-3.5 h-3.5" />
          Start: {eventData?.startTime ? formatDateTime(eventData.startDate || eventData.date, eventData.startTime) : 'Not set'}
        </button>
        {/* End Time (editable) */}
        <button
          onClick={handleOpenEndTimeEditor}
          className="btn btn-ghost btn-sm flex items-center gap-1.5"
          title="Set or edit event end time"
        >
          <Timer className="w-4 h-4" />
          {eventData?.endTime ? `End: ${formatDateTime(eventData.endDate || eventData.date, eventData.endTime)}` : 'No End Time'}
        </button>
        {/* Event Countdown — only when event is active and end time defined */}
        {isEventActive && eventData?.endTime && eventCountdown && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
            eventCountdown === 'Event ended'
              ? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
              : eventCountdown.includes(':')
                ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 animate-pulse'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
          }`}>
            <Clock className="w-3 h-3 inline mr-1" />
            {eventCountdown}
          </span>
        )}
      </div>

      {/* Queue Health Section — Row 2: Queue Metrics & Controls */}
      <div className="flex flex-wrap items-center gap-3 text-sm">
        {/* Metric 3: Total Queue Value */}
        <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
          <ListMusic className="w-3 h-3 inline mr-1" />
          Queue: {queueValueDisplay}
        </span>
        {/* Metric 1: Estimated Actual End Time — always shown */}
        {actualEndTimeDisplay && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
            actualEndTimeDisplay.isAfterScheduled
              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
              : actualEndTimeDisplay.isBeforeScheduled
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400'
              : 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400'
          }`} title={eventData?.endTime
            ? 'Estimated end — amber: running over, blue: ending early, purple: on track'
            : 'Estimated end based on queue duration'}>
            Est. End: {actualEndTimeDisplay.time}
          </span>
        )}
        {/* Metric 2: Queue vs End Time Gap — only when end time defined */}
        {queueGapDisplay && (
          <span className={`text-xs font-medium px-2 py-1 rounded-full ${
            queueGapDisplay.color === 'amber'
              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
              : queueGapDisplay.color === 'red'
                ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
          }`}>
            {queueGapDisplay.text}
          </span>
        )}
        {/* Overlap Toggle — only when end time defined */}
        {eventData?.endTime && (
          <button
            onClick={async () => {
              if (!eventId || !eventData) return;
              const newOverlap = !(eventData.settings?.allowOverlap ?? false);
              const updated = await updateEvent(eventId, {
                settings: { ...eventData.settings, allowOverlap: newOverlap },
              });
              setEventData(updated);
              toast.success(newOverlap ? 'Last song can exceed end time' : 'Strict end time enforced');
            }}
            className={`btn btn-ghost btn-sm flex items-center gap-1.5 ${eventData.settings?.allowOverlap ? 'text-green-600 dark:text-green-400' : 'text-gray-500'}`}
            title={eventData.settings?.allowOverlap ? 'Allow overlap is ON — last song can exceed end time' : 'Allow overlap is OFF — strict end time'}
          >
            {eventData.settings?.allowOverlap ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            Overlap {eventData.settings?.allowOverlap ? 'On' : 'Off'}
          </button>
        )}
        {/* Crossfade Badge — click to edit */}
        <button
          onClick={async () => {
            const input = prompt('Crossfade seconds (0-12):', String(crossfadeSec));
            if (input === null) return;
            const val = Math.min(12, Math.max(0, Number(input) || 0));
            if (!eventId || !eventData) return;
            const updated = await updateEvent(eventId, {
              settings: { ...eventData.settings, crossfadeSeconds: val },
            });
            setEventData(updated);
            toast.success(`Crossfade set to ${val}s`);
          }}
          className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors cursor-pointer"
          title="Click to change crossfade (seconds)"
        >
          CF: {crossfadeSec}s
        </button>
      </div>

      {/* Start Time Editor Inline Panel */}
      {showStartTimeEditor && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Play className="w-4 h-4 text-primary" />
              Event Start
            </h3>
            <button onClick={() => setShowStartTimeEditor(false)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={editStartDate}
              onChange={(e) => setEditStartDate(e.target.value)}
              className="input text-sm"
            />
            <input
              type="time"
              value={editStartTime}
              onChange={(e) => setEditStartTime(e.target.value)}
              className="input text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button onClick={handleSaveStartTime} className="btn btn-primary btn-sm">
              Save
            </button>
            <button onClick={() => setShowStartTimeEditor(false)} className="btn btn-secondary btn-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* End Time Editor Inline Panel */}
      {showEndTimeEditor && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <Timer className="w-4 h-4 text-primary" />
              Event End
            </h3>
            <button onClick={() => setShowEndTimeEditor(false)} className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="date"
              value={editEndDate}
              onChange={(e) => setEditEndDate(e.target.value)}
              className="input text-sm"
            />
            <input
              type="time"
              value={editEndTime}
              onChange={(e) => setEditEndTime(e.target.value)}
              className="input text-sm"
            />
            <button
              onClick={() => { setEditEndTime(''); setEditEndDate(''); }}
              className="text-xs text-gray-500 hover:text-red-500"
            >
              Clear
            </button>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={editAllowOverlap}
              onChange={(e) => setEditAllowOverlap(e.target.checked)}
              className="rounded border-gray-300"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">Allow last song to go past end time</span>
          </label>
          <div className="flex gap-2">
            <button onClick={handleSaveEndTime} className="btn btn-primary btn-sm">
              Save
            </button>
            <button onClick={() => setShowEndTimeEditor(false)} className="btn btn-secondary btn-sm">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Blocked Songs in Queue Alert */}
      {blockedInQueue.length > 0 && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <ShieldAlert className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-sm font-medium text-red-700 dark:text-red-300">
              {blockedInQueue.length} blocked song{blockedInQueue.length !== 1 ? 's' : ''} in your queue
            </span>
          </div>
          <button
            onClick={() => setShowBlockedReviewModal(true)}
            className="btn btn-sm bg-red-100 hover:bg-red-200 dark:bg-red-800/50 dark:hover:bg-red-800 text-red-700 dark:text-red-300 border-red-300 dark:border-red-700 flex-shrink-0"
          >
            Review
          </button>
        </div>
      )}

      {/* Blocked Songs Review Modal */}
      {showBlockedReviewModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBlockedReviewModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-red-500" />
                Blocked Songs in Queue
              </h3>
              <button onClick={() => setShowBlockedReviewModal(false)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {blockedInQueue.map(request => (
                <div key={request.id} className="flex items-center justify-between gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-100 dark:border-red-800/50">
                  <div className="min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white truncate">{request.song.title}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{request.song.artist}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleIgnoreBlocked(request.id)}
                      className="btn btn-sm btn-secondary text-xs"
                    >
                      Ignore
                    </button>
                    <button
                      onClick={() => handleRemoveBlockedFromQueue(request)}
                      className="btn btn-sm bg-red-500 hover:bg-red-600 text-white text-xs"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowBlockedReviewModal(false)}
                className="btn btn-secondary btn-sm"
              >
                Close
              </button>
              {blockedInQueue.length > 1 && (
                <button
                  onClick={handleRemoveAllBlocked}
                  className="btn btn-sm bg-red-500 hover:bg-red-600 text-white"
                >
                  Remove All ({blockedInQueue.length})
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Flag Review Modal */}
      {flagReviewModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setFlagReviewModal(null)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Flag className="w-5 h-5 text-amber-500" />
                Attendee Flags
              </h3>
              <button onClick={() => setFlagReviewModal(null)} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 border-b border-gray-200 dark:border-gray-700">
              <p className="font-medium text-gray-900 dark:text-white">{flagReviewModal.songTitle}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{flagReviewModal.songArtist}</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {flagsForReview.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">No pending flags for this song</p>
              ) : (
                flagsForReview.map(flag => (
                  <div key={flag.id} className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800/50">
                    <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">"{flag.reason}"</p>
                    <p className="text-xs text-gray-400 mb-2">Flagged by: {flag.flaggedBy.substring(0, 8)}...</p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => { handleReviewFlag(flag.id, 'kept'); }}
                        className="btn btn-sm btn-secondary text-xs"
                      >
                        Keep Song
                      </button>
                      <button
                        onClick={async () => {
                          handleReviewFlag(flag.id, 'removed');
                          await updateRequest(flagReviewModal.requestId, { status: 'rejected', rejectionReason: 'Removed after attendee flag' });
                          setRequests(prev => prev.filter(r => r.id !== flagReviewModal.requestId));
                          setFlagReviewModal(null);
                          toast.success('Song removed from queue');
                        }}
                        className="btn btn-sm bg-red-500 hover:bg-red-600 text-white text-xs"
                      >
                        Remove Song
                      </button>
                      <button
                        onClick={() => handleReviewFlag(flag.id, 'blocked-flagger', flag.flaggedBy)}
                        className="btn btn-sm bg-gray-500 hover:bg-gray-600 text-white text-xs"
                        title="Block this attendee from future flagging"
                      >
                        Block Flagger
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button onClick={() => setFlagReviewModal(null)} className="btn btn-secondary btn-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Controls */}
      <div className="flex flex-wrap gap-4 items-center">
        {/* Search */}
        <div className="flex items-center gap-2 flex-1 min-w-48">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search songs or artists..."
              className="input pl-10 pr-10 w-full"
            />
            {/* Clear search button */}
            {trimmedSearchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          {/* Search match counter + navigation */}
          {trimmedSearchQuery && (
            <div className="flex items-center gap-1 flex-shrink-0">
              <span className="text-xs text-gray-500 dark:text-gray-400 tabular-nums whitespace-nowrap">
                {searchMatchIds.length === 0 ? 'No results' : `${searchMatchIndex + 1} of ${searchMatchIds.length}`}
              </span>
              {searchMatchIds.length > 0 && (
                <>
                  <button
                    onClick={() => setSearchMatchIndex(i => (i - 1 + searchMatchIds.length) % searchMatchIds.length)}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                    title="Previous match"
                  >
                    <ChevronUp className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setSearchMatchIndex(i => (i + 1) % searchMatchIds.length)}
                    className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                    title="Next match"
                  >
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </>
              )}
            </div>
          )}
        </div>

        {/* Vote Filter */}
        <div className="relative">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <select
            value={voteFilter}
            onChange={(e) => setVoteFilter(e.target.value)}
            className="input pl-10 appearance-none cursor-pointer pr-10"
          >
            {VOTE_FILTERS.map((filter) => (
              <option key={filter.value} value={filter.value}>
                {filter.label}
              </option>
            ))}
          </select>
          {/* Clear vote filter button */}
          {voteFilter !== 'all' && (
            <button
              onClick={() => setVoteFilter('all')}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              title="Clear vote filter"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Sort Mode Switcher */}
        <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-0.5">
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 px-2">Sort:</span>
          {(['votes', 'copeland', 'fifo'] as const).map(mode => (
            <button
              key={mode}
              onClick={() => setDjSortMode(mode)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                djSortMode === mode
                  ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
              }`}
            >
              {mode === 'votes' ? 'Votes' : mode === 'copeland' ? 'Copeland' : 'FIFO'}
            </button>
          ))}
        </div>

        {/* Show Vote Tab toggle — appears when DJ uses vote sorting on a ranked-mode event */}
        {djSortMode === 'votes' && isRankedMode && (
          <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/30 px-3 py-2 rounded-lg border border-blue-200 dark:border-blue-800">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={eventData?.settings?.attendeeVoteTab ?? false}
                onChange={async (e) => {
                  if (!eventId || !eventData) return;
                  const newSettings = { ...eventData.settings, attendeeVoteTab: e.target.checked };
                  setEventData({ ...eventData, settings: newSettings });
                  await updateEvent(eventId, { settings: newSettings });
                }}
                className="rounded border-gray-300"
              />
              <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                Show Vote tab to attendees
              </span>
            </label>
          </div>
        )}

        {/* Event Ended Toggle */}
        <button
          onClick={handleToggleEventEnded}
          data-testid="end-event-toggle"
          className={`flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
            eventData?.status === 'completed'
              ? 'bg-red-100 dark:bg-red-900/30 border-red-300 dark:border-red-700 text-red-700 dark:text-red-300'
              : 'bg-gray-100 dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
          }`}
        >
          <Power className="w-4 h-4" />
          {eventData?.status === 'completed' ? 'Event Ended · Reopen' : 'End Event'}
        </button>

        {/* Scoring Mode Selector or Progress Indicator - Only show when Copeland sort is active */}
        {djSortMode === 'copeland' && (
          thresholdMet ? (
            <div className="flex items-center gap-2 bg-amber-50 dark:bg-amber-900/30 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
              <span className="text-xs font-medium text-amber-700 dark:text-amber-300">Score:</span>
              <div className="flex gap-1">
                <button
                  onClick={() => {
                    setScoringMode('consensus');
                  }}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    scoringMode === 'consensus'
                      ? 'bg-amber-600 text-white'
                      : 'bg-amber-100 dark:bg-amber-800 text-amber-700 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-700'
                  }`}
                  title="Consensus Mode: Unranked songs treated as last place"
                >
                  Consensus
                </button>
                <button
                  onClick={() => {
                    setScoringMode('discovery');
                  }}
                  className={`px-2 py-1 text-xs rounded transition-colors ${
                    scoringMode === 'discovery'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-emerald-100 dark:bg-emerald-800 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-700'
                  }`}
                  title="Discovery Mode: Only explicit rankings count (reveals niche favorites)"
                >
                  Discovery
                </button>
              </div>
            </div>
          ) : (
            <div className="bg-amber-50 dark:bg-amber-900/30 px-3 py-2 rounded-lg border border-amber-200 dark:border-amber-800">
              <div className="flex items-center gap-2 mb-1.5">
                <BarChart3 className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                  Copeland scores unlock at {minParticipants} rankings
                </span>
              </div>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-amber-200 dark:bg-amber-800 rounded-full h-2 overflow-hidden">
                  <div
                    className="bg-amber-600 dark:bg-amber-400 h-full rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(100, ((rankingStats?.totalRankings || 0) / minParticipants) * 100)}%` }}
                  />
                </div>
                <span className="text-xs text-amber-700 dark:text-amber-300 whitespace-nowrap">
                  {rankingStats?.totalRankings || 0} of {minParticipants}
                </span>
              </div>
              <p className="text-[10px] text-amber-600 dark:text-amber-400 mt-1">
                Sorting by votes until threshold is met
              </p>
            </div>
          )
        )}

        {/* Lock songs when queued (ITER-171) — manual play modes only */}
        {isManualPlayMode(playMode.id) && !isLocked && (
          <label
            className="btn btn-secondary flex items-center gap-2 cursor-pointer"
            title={`Queue Next also locks the song, up to ${autoLockCap(queueDepth)} at a time`}
          >
            <input
              type="checkbox"
              checked={lockWhenQueued}
              onChange={() => setPendingQueueSetting({ key: 'lockWhenQueued', value: !lockWhenQueued })}
              className="rounded border-gray-300 text-primary focus:ring-primary"
            />
            <Lock className="w-4 h-4" />
            Lock songs when queued
          </label>
        )}

        {/* Sort Direction Toggle - Only show when not in manual order mode */}
        {!useManualOrder && (
          <button
            onClick={() => setSortDirection(prev => prev === 'desc' ? 'asc' : 'desc')}
            className="btn btn-secondary flex items-center gap-2"
            title={sortDirection === 'desc'
              ? `Sorted by ${djSortMode === 'fifo' ? 'oldest' : 'highest'} ${djSortMode === 'copeland' ? (scoringMode === 'consensus' ? 'consensus score' : 'discovery score') : djSortMode === 'fifo' ? 'requests' : 'votes'} first`
              : `Sorted by ${djSortMode === 'fifo' ? 'newest' : 'lowest'} ${djSortMode === 'copeland' ? (scoringMode === 'consensus' ? 'consensus score' : 'discovery score') : djSortMode === 'fifo' ? 'requests' : 'votes'} first`
            }
          >
            {sortDirection === 'desc' ? (
              <>
                <ArrowDown className="w-4 h-4" />
                {djSortMode === 'fifo' ? 'Oldest First' : 'Highest First'}
              </>
            ) : (
              <>
                <ArrowUp className="w-4 h-4" />
                {djSortMode === 'fifo' ? 'Newest First' : 'Lowest First'}
              </>
            )}
          </button>
        )}

        {/* Show when in manual order mode */}
        {useManualOrder && (
          <button
            onClick={handleResetToSortOrder}
            className="btn btn-secondary flex items-center gap-2"
            title="Reset to automatic sort-based ordering"
          >
            <ArrowUpDown className="w-4 h-4" />
            Reset to {djSortMode === 'copeland' ? 'Copeland' : djSortMode === 'fifo' ? 'FIFO' : 'Votes'}
          </button>
        )}

        {/* Clear All Filters */}
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="btn btn-secondary flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Clear All
          </button>
        )}
      </div>

      {/* Filter Status */}
      {hasActiveFilters && (
        <div className="text-sm text-gray-500 dark:text-gray-400">
          Showing {filteredRequests.length} of {requests.length} songs
          {trimmedSearchQuery && ` matching "${trimmedSearchQuery}"`}
          {voteFilter !== 'all' && ` with ${voteFilter} votes`}
        </div>
      )}

      {/* Manual Order Indicator + Revert Config */}
      {useManualOrder && (
        <div className="p-3 bg-primary/10 border border-primary/20 rounded-lg text-sm space-y-2">
          <div className="flex items-center gap-2">
            <GripVertical className="w-4 h-4 text-primary" />
            <span className="text-primary font-medium">Custom order active</span>
            <span className="text-gray-600 dark:text-gray-400">— Drag songs to reorder</span>
          </div>
          {manualRevertConfig ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-primary font-medium">{manualRevertConfig.songsRemaining} song{manualRevertConfig.songsRemaining !== 1 ? 's' : ''} until auto-revert</span>
              <span className="text-gray-500">to {manualRevertConfig.revertToMode === 'copeland' ? 'Copeland' : manualRevertConfig.revertToMode === 'fifo' ? 'FIFO' : 'Votes'}</span>
              <button
                onClick={() => setManualRevertConfig(null)}
                className="text-xs text-red-500 hover:text-red-700 font-medium"
              >
                Cancel auto-revert
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-gray-600 dark:text-gray-400">Auto-revert after</span>
              <input
                type="number"
                min={1}
                max={50}
                defaultValue={5}
                className="input w-16 h-7 text-sm text-center"
                id="revert-count"
              />
              <span className="text-gray-600 dark:text-gray-400">songs to</span>
              <select className="input h-7 text-sm" id="revert-mode" defaultValue={djSortMode}>
                <option value="votes">Votes</option>
                <option value="copeland">Copeland</option>
                <option value="fifo">FIFO</option>
              </select>
              <button
                onClick={() => {
                  const count = Number((document.getElementById('revert-count') as HTMLInputElement)?.value) || 5;
                  const mode = ((document.getElementById('revert-mode') as HTMLSelectElement)?.value || 'votes') as 'votes' | 'copeland' | 'fifo';
                  setManualRevertConfig({ songsRemaining: count, revertToMode: mode });
                  toast.success(`Will revert to ${mode === 'copeland' ? 'Copeland' : mode === 'fifo' ? 'FIFO' : 'Votes'} after ${count} songs`);
                }}
                className="btn btn-primary btn-sm text-xs py-1 px-2"
              >
                Set
              </button>
            </div>
          )}
        </div>
      )}

      {/* People's Pick Pool — hidden via feature flag */}
      {!PEOPLES_PICK_HIDDEN && nominationGroups.length > 0 && (() => {
        const sorted = [...nominationGroups].sort((a, b) => {
          const aCount = poolUpvoteCounts.get(makePPGroupKey(a.title, a.artist)) ?? a.count;
          const bCount = poolUpvoteCounts.get(makePPGroupKey(b.title, b.artist)) ?? b.count;
          return (bCount / b.threshold) - (aCount / a.threshold);
        });
        const closeCount = sorted.filter(g => {
          const displayCount = poolUpvoteCounts.get(makePPGroupKey(g.title, g.artist)) ?? g.count;
          return displayCount >= g.threshold - 1;
        }).length;
        return (
          <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-lg">
            {/* Header — click to expand/collapse */}
            <button
              onClick={() => setNominationPanelExpanded(!nominationPanelExpanded)}
              className="w-full flex items-center justify-between gap-2"
            >
              <span className="text-sm font-semibold text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
                👑 People's Pick Pool ({nominationGroups.length})
                {!nominationPanelExpanded && closeCount > 0 && (
                  <span className="inline-flex items-center gap-1 ml-1">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500"></span>
                    </span>
                    <span className="text-xs font-bold text-amber-600 dark:text-amber-400">{closeCount} close</span>
                  </span>
                )}
              </span>
              {nominationPanelExpanded
                ? <ChevronUp className="w-4 h-4 text-amber-500" />
                : <ChevronDown className="w-4 h-4 text-amber-500" />}
            </button>

            {/* Expanded view — compact list with inline approve buttons */}
            {nominationPanelExpanded && (
              <div className="space-y-1 mt-2">
                {sorted.map((g, i) => {
                  const displayCount = poolUpvoteCounts.get(makePPGroupKey(g.title, g.artist)) ?? g.count;
                  return (
                    <div key={i} className="flex items-center gap-2 text-sm">
                      <div className="flex-1 min-w-0 truncate text-gray-800 dark:text-gray-200">
                        {g.title} — <span className="text-gray-500">{g.artist}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${displayCount >= g.threshold ? 'bg-green-500' : displayCount >= g.threshold - 1 ? 'bg-amber-500' : 'bg-primary'}`}
                            style={{ width: `${Math.min(100, (displayCount / g.threshold) * 100)}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium ${displayCount >= g.threshold ? 'text-green-600 dark:text-green-400' : displayCount >= g.threshold - 1 ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500'}`}>
                          {displayCount}/{g.threshold}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleApproveNomination(g.title, g.artist); }}
                          className="ml-1 p-1 rounded-md bg-green-600 hover:bg-green-700 text-white transition-colors"
                          title="Approve to Queue"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* Next Up Summary Strip — shows the #1 song for each inactive sort method */}
      {(() => {
        const activeSongs = filteredRequests.filter(r => !isVotedOut(r));
        if (activeSongs.length === 0) return null;

        const copelandScores = scoringMode === 'consensus' ? consensusScores : discoveryScores;

        const nextByVotes = [...activeSongs].sort((a, b) =>
          sortDirection === 'desc' ? b.voteCount - a.voteCount : a.voteCount - b.voteCount
        )[0];

        const nextByCopeland = [...activeSongs].sort((a, b) => {
          const aScore = copelandScores.get(a.id) || 0;
          const bScore = copelandScores.get(b.id) || 0;
          const diff = sortDirection === 'desc' ? bScore - aScore : aScore - bScore;
          if (diff !== 0) return diff;
          return sortDirection === 'desc'
            ? b.voteCount - a.voteCount
            : a.voteCount - b.voteCount;
        })[0];

        const nextByFifo = [...activeSongs].sort((a, b) => {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          return sortDirection === 'desc' ? aTime - bTime : bTime - aTime;
        })[0];

        const alternatives = [
          { mode: 'votes' as const, label: 'Votes', song: nextByVotes },
          { mode: 'copeland' as const, label: 'Copeland', song: nextByCopeland },
          { mode: 'fifo' as const, label: 'FIFO', song: nextByFifo },
        ].filter(a => a.mode !== djSortMode);

        return (
          <div className="flex flex-wrap items-center gap-2 p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700 text-xs">
            <span className="text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap">Next up by:</span>
            {alternatives.map(({ mode, label, song }) => (
              <button
                key={mode}
                onClick={() => setDjSortMode(mode)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 hover:border-primary/50 hover:bg-primary/5 transition-colors min-w-0"
                title={`Switch to ${label} sort`}
              >
                <span className="font-semibold text-gray-700 dark:text-gray-300 flex-shrink-0">{label}:</span>
                {song ? (
                  <span className="text-gray-600 dark:text-gray-400 truncate">
                    "{song.song.title}" — {song.song.artist}
                  </span>
                ) : (
                  <span className="text-gray-400 italic">empty</span>
                )}
              </button>
            ))}
          </div>
        );
      })()}

      {/* Player mismatch with an empty VoteBeats queue — no Now Playing card to hold the note */}
      {!nowPlayingRequest && shouldShowPlayerMismatch(playerMismatch, eventId, null) && playerMismatch.noAdvance && (
        <UnmatchedSongPrompt
          title={playerMismatch.title}
          artist={playerMismatch.artist}
          onChooseFromQueue={() => setFixDialog({ entry: 'choose' })}
          onLetItPlay={() => handleRecoveryOutcome({
            kind: 'letItPlay',
            track: detectedTrack ?? { title: playerMismatch.title, artist: playerMismatch.artist, durationMs: null },
            previousTo: 'played',
          })}
        />
      )}
      {!nowPlayingRequest && shouldShowPlayerMismatch(playerMismatch, eventId, null) && !playerMismatch.noAdvance && (
        <div className="flex items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg">
          <span className="text-sm text-gray-600 dark:text-gray-400">Nothing in the VoteBeats queue</span>
          <PlayerMismatchNote
            title={playerMismatch.title}
            artist={playerMismatch.artist}
            acknowledged={playerMismatch.acknowledged}
            onAcknowledge={() => handleAcknowledgePlayerMismatch(playerMismatch.detectedAt)}
          />
        </div>
      )}

      {/* Local files: which device plays them, and what to do around them */}
      {hasLocalFiles && (
        <LocalFilePlayerStrip
          claim={localPlayerClaim}
          blocked={localDeck.blocked}
          onClaim={handleClaimLocalPlayer}
          onUnblock={localDeck.unblock}
        >
          {level === 'checklist' && nowPlayingRequest && !nowPlayingIsLocalFile && nextUp?.localFile && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-gray-800 dark:text-gray-200">
                Next is a local file: <span className="font-semibold">{nextUp.song.title}</span>. Let {playerLabel} stop after this song.
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Press Played → start local file</p>
            </div>
          )}
          {level === 'checklist' && playerSongAfterFiles && (
            <div className="flex flex-wrap items-center gap-2">
              {isQueuedInPlayer(playerSongAfterFiles) ? (
                <p className="text-sm text-gray-800 dark:text-gray-200">
                  <span className="font-semibold">{playerSongAfterFiles.song.title}</span> is queued in {playerLabel}.
                </p>
              ) : (
                <>
                  <p className="flex-1 min-w-0 text-sm text-gray-800 dark:text-gray-200">
                    Queue <span className="font-semibold">{playerSongAfterFiles.song.title}</span> in {playerLabel} now, so it&rsquo;s ready.
                  </p>
                  <button
                    onClick={() => handleToggleQueuedInPlayer(playerSongAfterFiles)}
                    disabled={!canQueueOrLock(playerSongAfterFiles)}
                    title={canQueueOrLock(playerSongAfterFiles) ? undefined : VOTED_OUT_QUEUE_REFUSAL}
                    className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Queue it
                  </button>
                </>
              )}
            </div>
          )}
          {localFilePlaysHere && level === 'checklist' && !nextUp?.localFile && localDeck.timeLeftMs !== null && (
            <LocalFileEndCountdown
              msUntil={pressPlayCountdownMs(Math.max(localDeck.timeLeftMs, localFadeMs), localFadeMs)}
              verb="Press play"
              hint={`in ${playerLabel}`}
            />
          )}
          {localFilePlaysHere && level === 'bridge' && localDeck.bridgeMissedHandoff && (
            <LocalFileEndCountdown
              msUntil={null}
              verb="Press play"
              overrideText={`Bridge didn't start ${playerLabel} — press play now`}
            />
          )}
        </LocalFilePlayerStrip>
      )}

      {/* Now Playing Section — positioned directly above queue */}
      {nowPlayingRequest && (
        <div className={`relative p-4 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg space-y-3 ${nowPlayingRequest.song.explicitFlag ? 'border-l-4 border-l-red-500' : ''}`}>
          <div className="flex flex-wrap items-center gap-4">
            <div className="p-3 bg-primary/20 rounded-full">
              <Disc3 className="w-6 h-6 text-primary" />
            </div>
            {nowPlayingRequest.song.albumArtUrl ? (
              <img
                src={nowPlayingRequest.song.albumArtUrl}
                alt={`${nowPlayingRequest.song.title} album art`}
                className="album-art album-art-md flex-shrink-0"
              />
            ) : (
              <div className="w-16 h-16 flex-shrink-0 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                <Music className="w-8 h-8 text-gray-400" />
              </div>
            )}
            <div className="flex-1 min-w-[12rem]">
              <p className="text-xs font-semibold text-primary uppercase tracking-wider mb-1">Now Playing</p>
              <p className="font-bold text-lg text-gray-900 dark:text-white truncate flex items-center gap-1.5">
                <span className="truncate">{nowPlayingRequest.song.title}</span>
                {nowPlayingRequest.song.explicitFlag && (
                  <span className="explicit-badge flex-shrink-0" title="Explicit content">E</span>
                )}
              </p>
              <p className="text-gray-600 dark:text-gray-400 truncate">{nowPlayingRequest.song.artist}</p>
            </div>
            <div className="text-right mr-4">
              <span className="text-sm font-medium text-primary">{nowPlayingRequest.voteCount} votes</span>
              <p className="text-xs text-gray-500">{attendeeDisplayName(nowPlayingRequest.requestedBy.nickname, nowPlayingRequest.requestedBy.oderId)}</p>
            </div>
            <div className="flex flex-wrap items-center gap-2 ml-auto">
              {level === 'checklist' && (
                <button
                  onClick={handleChecklistPlayed}
                  className="btn btn-primary flex items-center gap-2 whitespace-nowrap"
                  title="Mark this song played and move the queue on"
                >
                  <CheckCircle className="w-4 h-4" />
                  {nextUp ? (nextUp.localFile ? 'Played → start local file' : 'Played → next') : 'Played'}
                </button>
              )}
              {!nowPlayingRequest.localFile && (
                <button
                  onClick={() => setFixDialog({ entry: 'fix' })}
                  className="btn flex items-center gap-1.5 whitespace-nowrap bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/40"
                  title="Get back on track when the wrong song is playing"
                >
                  <Wrench className="w-4 h-4" />
                  Fix Now Playing
                </button>
              )}
              {level === 'bridge' && !nowPlayingRequest.localFile && !nowPlayingRequest.unplannedPlayAt && (
                nowPlayingFlag ? (
                  <span className="flex items-center gap-1.5 text-xs px-2 py-1 rounded-md bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300">
                    <EyeOff className="w-3.5 h-3.5" />
                    Flagged for {playerLabel}
                    <button
                      type="button"
                      disabled={flagBusy}
                      onClick={() => handleSetRecognitionFlag(nowPlayingRequest, false, 'manual')}
                      className="underline font-medium disabled:opacity-50"
                    >
                      Remove
                    </button>
                  </span>
                ) : (
                  <button
                    type="button"
                    disabled={flagBusy}
                    onClick={() => handleSetRecognitionFlag(nowPlayingRequest, true, 'manual')}
                    className="btn flex items-center gap-1.5 whitespace-nowrap bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
                    title={`Flag this song so the queue warns you next time it comes up in ${playerLabel}`}
                  >
                    <EyeOff className="w-4 h-4" />
                    Bridge didn&rsquo;t catch this
                  </button>
                )
              )}
              <button
                onClick={() => handleAnalyzeSong(nowPlayingRequest.song.title, nowPlayingRequest.song.artist)}
                className="p-2 text-purple-500 hover:bg-purple-500/10 rounded-lg transition-colors"
                title="AI Song Analysis"
                aria-label={`AI analysis for ${nowPlayingRequest.song.title} by ${nowPlayingRequest.song.artist}`}
              >
                <Sparkles className="w-4 h-4" />
              </button>
              {lastSongSupport !== 'no-bridge' && !nowPlayingRequest.localFile && (
                lastSongSupport === 'outdated' ? (
                  <span className="flex flex-col items-start gap-0.5">
                    <button
                      type="button"
                      disabled
                      className="btn flex items-center gap-2 bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 opacity-60 cursor-not-allowed"
                      title={`Update Bridge to ${LAST_SONG_MIN_BRIDGE_VERSION} to stop after the last song`}
                    >
                      <StopCircle className="w-4 h-4" />
                      Last Song
                    </button>
                    <span className="text-xs text-gray-500 dark:text-gray-400">
                      Update Bridge to {LAST_SONG_MIN_BRIDGE_VERSION} to stop after the last song
                    </span>
                  </span>
                ) : (
                  <span className="flex flex-col items-end gap-1.5">
                  <button
                    type="button"
                    disabled={lastSongBusy}
                    onClick={() => handleToggleLastSong(nowPlayingRequest)}
                    className={`btn flex items-center gap-2 disabled:opacity-60 ${
                      lastSongActive
                        ? 'bg-red-500 text-white hover:bg-red-600'
                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                    title={lastSongActive ? 'Last Song is ON. Click to keep the queue going' : `Make this the last song. Bridge pauses ${playerLabel} when it ends`}
                  >
                    <StopCircle className="w-4 h-4" />
                    {lastSongActive ? 'Last Song ON' : 'Last Song'}
                  </button>
                  {lastSongActive && lastSongSupport === 'supported' && (
                    <label className="flex items-center gap-2 text-xs text-gray-700 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={eventData?.endAfterLastSong === true}
                        onChange={(e) => handleToggleEndAfterLastSong(e.target.checked)}
                        disabled={endAfterBusy}
                        data-testid="end-after-last-song"
                      />
                      End the event when this song finishes
                    </label>
                  )}
                  </span>
                )
              )}
            </div>
          </div>

          {lastSongActive && (
            <p className="px-1 text-xs font-medium text-red-600 dark:text-red-400" data-testid="last-song-note">
              {level === 'bridge' && nowPlayingCountdown !== null && nowPlayingCountdown <= 0
                ? 'Stopping…'
                : eventData?.endAfterLastSong === true
                  ? `Last song: Bridge pauses ${playerLabel} when it ends, then the event ends.`
                  : `Last song: Bridge pauses ${playerLabel} when it ends.`}
            </p>
          )}
          {lastSongActive && lastSongSupport === 'no-bridge' && (
            <p className="flex items-start gap-1.5 px-1 text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
              Bridge isn&rsquo;t connected, so Last Song won&rsquo;t stop {playerLabel}.
            </p>
          )}

          {/* A hand-picked song counts down from the player only (ITER-182); a Let it play song came from outside the queue (ITER-173) */}
          {level === 'bridge' && (nowPlayingRequest.manualPickAt || nowPlayingRequest.unplannedPlayAt) && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-gray-600 dark:text-gray-400" data-testid="recovery-note">
              <span>
                {nowPlayingRequest.manualPickAt
                  ? (countdown.kind === 'running' ? 'Picked by hand. ' : 'Picked by hand. Waiting for Bridge to read the time left. ')
                  : 'Played unplanned. '}
                {nextSong
                  ? <>Make sure <span className="font-semibold">{nextSong.song.title}</span> is queued next in {playerLabel}.</>
                  : `Make sure the next song is queued in ${playerLabel}.`}
              </span>
              {nowPlayingRequest.unplannedPlayAt && !playerMismatchHere?.noAdvance && (
                <button
                  type="button"
                  onClick={() => setFixDialog({ entry: 'choose', noFlag: true })}
                  className="text-primary hover:text-primary/80 underline font-medium whitespace-nowrap"
                >
                  Choose from queue instead
                </button>
              )}
            </div>
          )}

          {/* Time left — from the player's position via Bridge, or estimated from the song's length */}
          {level === 'bridge' && countdown.kind === 'running' && nowPlayingCountdown !== null && (
            <div className="px-1">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide">
                {countdown.source === 'player' ? 'Time left' : 'Est. remaining'}
              </span>
              <span className={`text-sm font-semibold tabular-nums ${
                nowPlayingCountdown < 30000 ? 'text-red-500' :
                nowPlayingCountdown < 60000 ? 'text-amber-500' :
                'text-primary'
              }`}>
                {Math.floor(nowPlayingCountdown / 60000)}:{String(Math.floor((nowPlayingCountdown % 60000) / 1000)).padStart(2, '0')}
              </span>
              {/* Test shortcut: jump countdown to 50s remaining */}
              {countdown.source === 'estimate' && nowPlayingRequest?.timestamps?.startedPlaying && (
                <button
                  onClick={() => {
                    const duration = knownDurationMs(nowPlayingRequest.song) ?? ESTIMATE_DEFAULT_MS;
                    const newStartedAt = Date.now() - (duration - 50_000);
                    // Override startedPlaying so the countdown recalculates to ~50s
                    setNowPlayingRequest(prev => prev ? {
                      ...prev,
                      timestamps: { ...prev.timestamps, startedPlaying: new Date(newStartedAt).toISOString() },
                    } : prev);
                  }}
                  className="text-xs px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600 font-mono"
                  title="Testing: jump countdown to 50s remaining"
                >
                  →:50
                </button>
              )}
            </div>
            {countdown.source === 'estimate' && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                Bridge isn&rsquo;t reporting {playerLabel}&rsquo;s position. Estimating from the song&rsquo;s length
                {!countdown.lengthKnown && ' (length unknown, using 3:30)'}
              </p>
            )}
            </div>
          )}

          {/* Without Bridge there is no time left; say why and offer the fix */}
          {level === 'checklist' && !nowPlayingRequest.localFile && (
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 text-xs text-gray-500 dark:text-gray-400">
              {bridgeStatus.kind === 'live-elsewhere' ? (
                <>
                  <span>Bridge is following {bridgeStatus.eventName}. Switch it here to see time left.</span>
                  {eventId && (
                    <button
                      onClick={() => sendSwitchEventCommand(eventId, eventData?.name || 'Event')}
                      className="text-primary hover:text-primary/80 underline font-medium whitespace-nowrap"
                    >
                      Switch here
                    </button>
                  )}
                </>
              ) : (
                <>
                  <span>
                    {bridgeStatus.kind === 'lost'
                      ? 'Bridge stopped responding. Time left comes back when it reconnects.'
                      : 'Open Bridge to see time left.'}
                  </span>
                  <button
                    onClick={launchBridge}
                    className="text-primary hover:text-primary/80 underline font-medium whitespace-nowrap"
                  >
                    Open Bridge
                  </button>
                </>
              )}
            </div>
          )}

          {/* Remote Playback Controls — the player via Bridge, or the local file playing on this device */}
          {nowPlayingIsLocalFile && !isLocalPlayerDevice ? (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mr-1">Remote</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">Local file playing on another device</span>
            </div>
          ) : (detectedPlayerName || localFilePlaysHere) && (
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-gray-400 dark:text-gray-500 uppercase tracking-wide mr-1">Remote</span>
              <button
                onClick={handleRemotePlayerPrevious}
                disabled={localFilePlaysHere}
                className="p-2 rounded-full bg-white/80 dark:bg-gray-700/80 hover:bg-white dark:hover:bg-gray-600 shadow-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title={localFilePlaysHere ? 'Not available for local files' : `Previous in ${playerLabel}`}
              >
                <SkipBack className="w-4 h-4 text-gray-700 dark:text-gray-200" />
              </button>
              <button
                onClick={() => (localFilePlaysHere ? localDeck.pauseFile() : handleRemotePlayerPause())}
                className="p-2 rounded-full bg-white/80 dark:bg-gray-700/80 hover:bg-white dark:hover:bg-gray-600 shadow-sm transition-colors"
                title={localFilePlaysHere ? 'Pause the local file' : `Pause ${playerLabel}`}
              >
                <Pause className="w-4 h-4 text-gray-700 dark:text-gray-200" />
              </button>
              <button
                onClick={() => (localFilePlaysHere ? localDeck.resumeFile() : handleRemotePlayerPlay())}
                className="p-2 rounded-full bg-white/80 dark:bg-gray-700/80 hover:bg-white dark:hover:bg-gray-600 shadow-sm transition-colors"
                title={localFilePlaysHere ? 'Resume the local file' : `Play ${playerLabel}`}
              >
                <Play className="w-4 h-4 text-green-600 dark:text-green-400" />
              </button>
              <button
                onClick={() => (localFilePlaysHere ? localDeck.skipFile() : handleRemotePlayerSkip())}
                className="p-2 rounded-full bg-white/80 dark:bg-gray-700/80 hover:bg-white dark:hover:bg-gray-600 shadow-sm transition-colors"
                title={localFilePlaysHere ? 'End the local file now' : `Skip in ${playerLabel}`}
              >
                <SkipForward className="w-4 h-4 text-gray-700 dark:text-gray-200" />
              </button>
              {localFilePlaysHere && localDeck.paused && (
                <span className="text-xs text-amber-600 dark:text-amber-400">Local file paused</span>
              )}
            </div>
          )}

          {shouldShowPlayerMismatch(playerMismatch, eventId, nowPlayingRequest.id) && playerMismatch.noAdvance && (
            <UnmatchedSongPrompt
              title={playerMismatch.title}
              artist={playerMismatch.artist}
              onChooseFromQueue={() => setFixDialog({ entry: 'choose' })}
              onLetItPlay={() => setFixDialog({ entry: 'letItPlay' })}
            />
          )}
          {shouldShowPlayerMismatch(playerMismatch, eventId, nowPlayingRequest.id) && !playerMismatch.noAdvance && (
            <div className="flex justify-end">
              <PlayerMismatchNote
                title={playerMismatch.title}
                artist={playerMismatch.artist}
                acknowledged={playerMismatch.acknowledged}
                onAcknowledge={() => handleAcknowledgePlayerMismatch(playerMismatch.detectedAt)}
              />
            </div>
          )}
        </div>
      )}

      {level === 'bridge' && !nowPlayingIsLocalFile && !nextUp?.localFile && (warn30Dismissed || warn45Dismissed) && (
        <div className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300">
          <BellOff className="w-4 h-4 flex-shrink-0" />
          <span>Song-end warnings hidden</span>
          <button
            type="button"
            onClick={restoreSongEndWarnings}
            className="underline font-medium"
          >
            Turn back on
          </button>
        </div>
      )}

      {/* 30s Warning — song ending soon and the next player song is queued: soft reminder
          Only shows when the 45s takeover is NOT showing (the next song is queued, or the takeover was dismissed),
          and never in the late-queue case, where the next song's card warns instead (ITER-183).
          Uses lastKnownCountdownRef so it persists even if nowPlayingRequest clears. */}
      {level === 'bridge' && !nowPlayingIsLocalFile && !nextUp?.localFile && nextSong && songEndBannerShown === 'reminder' && (
        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/40 rounded-full flex-shrink-0 mt-0.5">
              <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-amber-800 dark:text-amber-300">
                Song ending in {Math.ceil((songEndCountdown ?? 30000) / 1000)}s
              </p>
              <p className="text-sm text-amber-700 dark:text-amber-400 mt-0.5">
                <span className="font-medium">{nextSong.song.title}</span> by {nextSong.song.artist} is up next.
                {!isQueuedInPlayer(nextSong) && ' Make sure it\'s queued in your player.'}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {!isQueuedInPlayer(nextSong) && (
                <button
                  onClick={() => handleToggleQueuedInPlayer(nextSong)}
                  disabled={!canQueueOrLock(nextSong)}
                  title={canQueueOrLock(nextSong) ? undefined : VOTED_OUT_QUEUE_REFUSAL}
                  className="px-3 py-1.5 text-sm font-semibold rounded-lg bg-amber-500 text-white hover:bg-amber-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Queue it
                </button>
              )}
              {!nextSong.lockedNext && (
                <button
                  onClick={() => handleToggleLockedNext(nextSong)}
                  disabled={!canQueueOrLock(nextSong)}
                  title={canQueueOrLock(nextSong) ? undefined : VOTED_OUT_QUEUE_REFUSAL}
                  className="px-3 py-1.5 text-sm font-medium rounded-lg border border-amber-400 dark:border-amber-600 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Lock className="w-3.5 h-3.5" /> Lock
                </button>
              )}
              <button
                onClick={() => setWarn30DismissedForCurrentSong(true)}
                className="px-3 py-1.5 text-sm font-medium rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
              >
                Dismiss for this song
              </button>
              <button
                onClick={() => {
                  if (window.confirm('Hide the 30-second warning for the rest of this event?')) {
                    dismissWarn30();
                  }
                }}
                className="px-3 py-1.5 text-sm font-medium rounded-lg text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
              >
                Dismiss for rest of event
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 45s Warning — song ending soon with an empty queue or the next player song not queued: hard CTA takeover.
          Overrides the 30s banner. Uses lastKnownCountdownRef so it fires even if
          nowPlayingRequest has been cleared. */}
      {level === 'bridge' && !nowPlayingIsLocalFile && !nextUp?.localFile && (() => {
        const cd = songEndCountdown;
        if (cd === null || songEndBannerShown !== 'takeover') return null;
        const queueEmpty = filteredRequests.length === 0;
        const songEnded = cd <= 0;
        const cdSec = Math.ceil(cd / 1000);
        return (
          <div className="p-5 bg-red-50 dark:bg-red-900/20 border-2 border-red-400 dark:border-red-600 rounded-xl">
            <div className="text-center mb-4">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/40 mb-3">
                <AlertTriangle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <p className="text-lg font-bold text-red-700 dark:text-red-300">
                {songEnded
                  ? 'Song has ended!'
                  : queueEmpty ? 'Queue is empty!' : queuedInPlayerCount === 0 ? 'Nothing queued in player!' : 'Next song isn\'t queued!'}
              </p>
              <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                {songEnded
                  ? (queueEmpty
                    ? 'Approve a request or add a song now to keep the music going.'
                    : 'Queue the next song in your player now to keep the music going.')
                  : (queueEmpty
                    ? `Current song ends in ~${cdSec}s — approve a request or add a song now.`
                    : `Current song ends in ~${cdSec}s — queue the next song in your player now.`)}
              </p>
            </div>
            <div className="flex items-center justify-center gap-3">
              {queueEmpty ? (
                <button
                  onClick={() => {
                    document.getElementById('pending-requests-section')?.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn bg-red-500 text-white hover:bg-red-600 flex items-center gap-2 px-5 py-2.5 font-semibold"
                >
                  <Plus className="w-4 h-4" />
                  Approve a Song
                </button>
              ) : nextSong ? (
                <button
                  onClick={() => handleToggleQueuedInPlayer(nextSong)}
                  disabled={!canQueueOrLock(nextSong)}
                  title={canQueueOrLock(nextSong) ? undefined : VOTED_OUT_QUEUE_REFUSAL}
                  className="btn bg-red-500 text-white hover:bg-red-600 flex items-center gap-2 px-5 py-2.5 font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="w-4 h-4" />
                  Queue "{nextSong.song.title}"
                </button>
              ) : null}
              <button
                onClick={dismissWarn45}
                className="px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors"
              >
                Dismiss
              </button>
            </div>
          </div>
        );
      })()}

      {/* Stopped after the last song (ITER-175): Bridge paused the player and nothing advanced */}
      {!isLocked && eventData?.showStop && !nowPlayingRequest && (
        <ShowStoppedCard
          showStop={eventData.showStop}
          songTitle={playedRequests.find(r => r.id === eventData.showStop?.requestId)?.song.title ?? null}
          playerLabel={playerLabel}
          level={level}
          eventEnded={eventData?.status === 'completed'}
          onResume={eventData?.status === 'completed' ? handleReopenAndResume : handleResumeShow}
        />
      )}

      {/* Start Show — prominent CTA when nothing is playing */}
      {!isLocked && !nowPlayingRequest && !eventData?.showStop && filteredRequests.length > 0 && (
        <div className="p-4 bg-gradient-to-r from-primary/10 to-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-primary/20 rounded-full">
              <Play className="w-6 h-6 text-primary" />
            </div>
            {filteredRequests[0].song.albumArtUrl && (
              <img src={filteredRequests[0].song.albumArtUrl} alt="" className="w-12 h-12 rounded album-art-md" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-primary uppercase tracking-wider">Ready to Play</p>
              <p className="font-bold text-gray-900 dark:text-white truncate">
                {filteredRequests[0].song.title}
                {filteredRequests[0].song.durationMs ? (
                  <span className={`text-xs font-normal ml-2 ${
                    filteredRequests[0].durationVerified === 'exact' ? 'text-green-500' :
                    filteredRequests[0].durationVerified === 'approximate' ? 'text-amber-400' :
                    'text-gray-400'
                  }`}>{filteredRequests[0].durationVerified === 'approximate' ? '~' : ''}{formatDuration(filteredRequests[0].song.durationMs)}{filteredRequests[0].durationVerified === 'exact' ? ' \u2713' : ''}</span>
                ) : (
                  <span className="text-xs font-normal ml-2 text-gray-400">Length unknown</span>
                )}
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{filteredRequests[0].song.artist}</p>
            </div>
            <button
              onClick={() => handleMarkNowPlaying(filteredRequests[0].id, filteredRequests[0].song.title)}
              className="btn bg-primary text-white hover:bg-primary/90 flex items-center gap-2 px-6 py-3 text-base font-semibold rounded-xl shadow-lg"
            >
              <Play className="w-5 h-5" />
              Start Show
            </button>
          </div>
        </div>
      )}

      {filteredRequests.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-4">
            <Music className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {hasActiveFilters ? 'No Matching Songs' : 'Queue is Empty'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {hasActiveFilters
              ? 'Try adjusting your filters'
              : 'Approve pending requests to add songs to your queue'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={handleClearFilters}
              className="mt-4 btn btn-primary"
            >
              Clear Filters
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          <SortableList
            ids={dragDisplayRequests.map(r => r.id)}
            onDragStart={() => setFrozenQueueIds(dragDisplayRequests.map(r => r.id))}
            onDragEnd={() => setFrozenQueueIds(null)}
            onMove={(activeId, overId) => { void handleQueueDragMove(activeId, overId); }}
          >
          {dragDisplayRequests.map((request, index) => (
            <div key={request.id}>
              {/* F17: Cutoff divider */}
              {cutoffIndex >= 0 && index === cutoffIndex && (
                <div className="flex items-center gap-3 py-2 my-1">
                  <div className="flex-1 h-px bg-red-400 dark:bg-red-500" />
                  <span className="text-xs font-semibold text-red-500 dark:text-red-400 whitespace-nowrap">
                    Won't make the cut
                  </span>
                  <div className="flex-1 h-px bg-red-400 dark:bg-red-500" />
                </div>
              )}
              {/* Auto-revert divider — marks where manual order ends */}
              {manualRevertConfig && index === manualRevertConfig.songsRemaining && (
                <div className="flex items-center gap-3 py-2 my-1">
                  <div className="flex-1 h-px bg-amber-400 dark:bg-amber-500" />
                  <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap">
                    ↑ Sort reverts after this point
                  </span>
                  <div className="flex-1 h-px bg-amber-400 dark:bg-amber-500" />
                </div>
              )}
            <SortableItem id={request.id} disabled={isLocked || isDragLocked(request) || dragBlockedByFilter}>{sortable => (
            <div
              id={`queue-card-${request.id}`}
              ref={sortable.setNodeRef}
              className={`rounded-lg ${sortable.isDragging ? 'shadow-lg' : 'transition-all'}
                ${lateOutlineFor(request)
                  ? `bg-amber-50 dark:bg-amber-900/20 ${lateOutlineFor(request)}`
                  : topStatus?.unqueued.has(request.id) ? 'bg-amber-50 dark:bg-amber-900/20 ring-2 ring-amber-500' : 'bg-gray-50 dark:bg-gray-800/50'}
                ${cutoffIndex >= 0 && index >= cutoffIndex ? 'opacity-50' : ''}
                ${sortable.isDragging ? 'ring-2 ring-primary' : ''}
                ${isDragLocked(request) ? `border-2 border-amber-400 ${topStatus?.unqueued.has(request.id) ? '' : 'ring-2 ring-amber-400/30'}` : 'border-2 border-transparent'}
                ${manualRevertConfig && index === manualRevertConfig.songsRemaining - 1 ? 'border-b-2 border-b-amber-400' : ''}
                ${searchMatchIds.includes(request.id) ? (searchMatchIds[searchMatchIndex] === request.id ? 'ring-2 ring-yellow-500' : 'ring-2 ring-yellow-400') : ''}
              `}
              style={{ ...(sortable.isDragging ? {} : { contentVisibility: 'auto', containIntrinsicSize: '0 120px' }), ...sortable.style }}
            >
              {request.lockedNext && (
                <div className="flex items-center justify-between px-4 pt-2">
                  <span className="text-xs font-semibold text-amber-600 dark:text-amber-400 flex items-center gap-1">
                    <Lock className="w-3 h-3" /> Locked
                  </span>
                  <button
                    onClick={() => handleToggleLockedNext(request)}
                    className="text-xs text-amber-600 dark:text-amber-400 hover:text-amber-800 dark:hover:text-amber-300 underline"
                  >
                    Unlock
                  </button>
                </div>
              )}
              {request.id === predictionPinnedId && (
                <div className="flex items-center px-4 pt-2">
                  <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 flex items-center gap-1">
                    <Crosshair className="w-3 h-3" /> Prediction answer
                  </span>
                </div>
              )}
              <div
                className={`flex items-start gap-3 p-4 group ${request.song.explicitFlag ? 'border-l-4 border-l-red-500 bg-red-50/60 dark:bg-red-900/15 ring-1 ring-red-300 dark:ring-red-800 rounded-lg' : ''}`}
              >
                {/* Left column: drag handle + position */}
                <div className="flex items-center gap-2 pt-1 flex-shrink-0">
                  {!isLocked && (isDragLocked(request) ? (
                    <div className="opacity-50">
                      <Lock className="w-5 h-5 text-amber-500" />
                    </div>
                  ) : (
                    <DragHandle
                      bindings={sortable.handleProps}
                      label={`Drag ${request.song.title}`}
                      disabledHint={dragBlockedByFilter ? 'Clear the vote filter to drag' : undefined}
                      className="-m-1.5"
                      iconSize={20}
                    />
                  ))}
                  <span className="w-8 text-center text-sm font-medium text-gray-500 flex-shrink-0">
                    {index + 1}
                  </span>
                </div>

                {/* Right column: content in two rows */}
                <div className="flex-1 min-w-0">
                  {/* Row 1: album art + title + scores */}
                  <div className="flex items-center gap-3">
                    {/* Album Art */}
                    {request.song.albumArtUrl ? (
                      <img
                        src={request.song.albumArtUrl}
                        alt={`${request.song.title} album art`}
                        className="album-art album-art-sm flex-shrink-0"
                      />
                    ) : (
                      <div className="w-12 h-12 flex-shrink-0 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                        <Music className="w-6 h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className={`font-medium truncate flex items-center gap-1.5 ${isVotedOut(request) ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-white'}`} title={request.song.title}>
                        <span className="truncate">{request.song.title}</span>
                        {request.practiceMode && (
                          <span className="flex-shrink-0 px-1.5 py-0.5 rounded border border-dashed border-purple-400 dark:border-purple-500 text-purple-600 dark:text-purple-400 text-[10px] font-semibold leading-none" title="Practice/test data">TEST</span>
                        )}
                        {request.song.explicitFlag && (
                          <span className="explicit-badge flex-shrink-0" title="Explicit content">E</span>
                        )}
                        {songVerdicts.get(`${request.song.title.toLowerCase().trim()}|${request.song.artist.toLowerCase().trim()}`) === 'passed' && (
                          <span title="Previously passed"><ShieldCheck className="w-3.5 h-3.5 text-green-500 flex-shrink-0" /></span>
                        )}
                        {songVerdicts.get(`${request.song.title.toLowerCase().trim()}|${request.song.artist.toLowerCase().trim()}`) === 'blocked' && (
                          <span title="Previously blocked"><ShieldX className="w-3.5 h-3.5 text-red-500 flex-shrink-0" /></span>
                        )}
                        {(flagCounts.get(request.id) || 0) > 0 && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleOpenFlagReview(request.id, request.song.title, request.song.artist); }}
                            className="inline-flex items-center gap-0.5 text-xs bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded-full flex-shrink-0 font-medium hover:bg-amber-200 dark:hover:bg-amber-900/60 transition-colors"
                            title={`${flagCounts.get(request.id)} flag(s) from attendees`}
                          >
                            <Flag className="w-3 h-3" />
                            {flagCounts.get(request.id)}
                          </button>
                        )}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 truncate" title={request.song.artist}>
                        {request.song.artist}
                        {request.song.durationMs ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); openDurationEditor(request.id, request.song.durationMs); }}
                            className={`text-xs ml-2 hover:underline cursor-pointer ${
                              request.durationVerified === 'exact' ? 'text-green-500 dark:text-green-400' :
                              request.durationVerified === 'approximate' ? 'text-amber-400 dark:text-amber-500' :
                              'text-gray-400 dark:text-gray-500'
                            }`} title={
                              request.durationVerified === 'exact' ? 'Duration verified — click to edit' :
                              request.durationVerified === 'approximate' ? 'Approximate duration — click to edit' :
                              'Duration not verified — click to edit'
                            }>{request.durationVerified === 'approximate' ? '~' : ''}{formatDuration(request.song.durationMs)}{request.durationVerified === 'exact' ? ' \u2713' : ''}</button>
                        ) : (
                          <button
                            onClick={(e) => { e.stopPropagation(); openDurationEditor(request.id); }}
                            className="text-xs ml-2 text-gray-400 hover:text-primary cursor-pointer"
                            title="Add the song's length"
                          >Length unknown</button>
                        )}
                      </p>
                      {request.song.explicitFlag && (
                        <p className="text-xs text-red-600 dark:text-red-400 mt-0.5 font-semibold flex items-center gap-1">
                          <AlertTriangle className="w-3 h-3" />
                          Explicit
                        </p>
                      )}
                      {/* Show note indicator if has notes and not expanded */}
                      {request.djNotes && !expandedNotes.has(request.id) && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
                          <StickyNote className="w-3 h-3" />
                          <span className="truncate">{request.djNotes}</span>
                        </p>
                      )}
                      <ReviewedByBadge reviewedBy={request.reviewedBy} />
                      {/* Entry source badge */}
                      {(request.entrySource === 'auto-matched' || request.entrySource === 'imported') && (
                        <span className="inline-flex items-center mt-0.5 text-xs px-1.5 py-0.5 rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">
                          {request.entrySource === 'auto-matched' ? 'Auto-matched' : 'Imported'}
                        </span>
                      )}
                      {/* Review button for unverified auto-matched/imported songs */}
                      {request.platformLink && request.matchVerified !== true &&
                        (request.entrySource === 'auto-matched' || request.entrySource === 'imported') && (
                        <button
                          className="text-xs text-amber-600 dark:text-amber-400 underline mt-0.5 block"
                          onClick={(e) => { e.stopPropagation(); openMatchModal(request); }}
                        >
                          Review match
                        </button>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="flex items-center justify-end gap-1 flex-wrap">
                        {trendingIds.has(request.id) && (
                          <span title="Trending! Getting lots of recent votes"><Flame className="w-4 h-4 text-orange-500 animate-pulse" /></span>
                        )}
                        {/* Hidden gem indicator */}
                        {thresholdMet && hiddenGems.some(g => g.requestId === request.id) && (
                          <span className="text-emerald-500" title="Hidden Gem: Ranks higher in Discovery mode">💎</span>
                        )}
                        {/* Crowd decisions (ITER-144 / ITER-075): labels only, the DJ can't override them */}
                        {isVotedOut(request) && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400">
                            {votedOutLabel(request, participantCount, voteRules)}
                          </span>
                        )}
                        {!isVotedOut(request) && wouldBeVotedOut(request, participantCount, voteRules) && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                            Majority downvoted — would have been voted out
                          </span>
                        )}
                        {isCrowdFavorite(request) && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full font-medium bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-400">
                            {crowdFavoriteLabel(request, participantCount)}
                          </span>
                        )}
                        <span className="text-sm font-medium text-primary">{request.voteCount} votes</span>
                        {/* Downvote indicator badge with percentage of active participants */}
                        {request.downvoteCount > 0 && (
                          <span
                            className="inline-flex items-center gap-0.5 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 px-1.5 py-0.5 rounded-full"
                            title={downvoteBadgeTitle(request, participantCount, voteRules)}
                          >
                            ↓{downvoteBadgeText(request, participantCount)}
                          </span>
                        )}
                        {request.vipPriority && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full">
                            <Crown className="w-3 h-3" /> VIP
                          </span>
                        )}
                        {request.vipTriggerable && !request.vipPriority && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-medium bg-amber-50 dark:bg-amber-900/20 text-amber-500 dark:text-amber-500 px-1.5 py-0.5 rounded-full" title="Available to VIP">
                            <Crown className="w-3 h-3" />
                          </span>
                        )}
                        {request.localFile && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-1.5 py-0.5 rounded-full" title="Local file">
                            <FileMusic className="w-3 h-3" />
                          </span>
                        )}
                        {request.visibleToAttendees === false && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 px-1.5 py-0.5 rounded-full" title="Hidden from attendees">
                            <EyeOff className="w-3 h-3" />
                          </span>
                        )}
                        {/* Unmatched / Platform link indicator */}
                        {!request.platformLink && needsLinkMatch(request, playMode) && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full" title="No platform match — paste a URL to link">
                            <AlertTriangle className="w-3 h-3" /> Unmatched
                          </span>
                        )}
                        {request.platformLink && needsLinkMatch(request, playMode) && (
                          <span className="inline-flex items-center gap-0.5 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 px-1.5 py-0.5 rounded-full" title={`Matched on ${request.platformName} — needs ${playerLabel} match`}>
                            <ExternalLink className="w-3 h-3" /> {request.platformName}
                          </span>
                        )}
                        {!request.localFile && !needsLinkMatch(request, playMode) && (() => {
                          const { url: directUrl } = openUrlFor(request, playMode);
                          const searchUrl = searchUrlFor(request.song.title, request.song.artist, playMode);
                          let longPressTimer: ReturnType<typeof setTimeout> | null = null;
                          const parsedLink = request.platformLink ? parseMusicLink(request.platformLink) : null;
                          const libraryLink = request.linkLibraryKey
                            ? libraryStats.get(request.linkLibraryKey)?.links[playMode.provider]
                            : undefined;
                          const countedLink = libraryLink && parsedLink?.kind === 'track' && parsedLink.trackId === libraryLink.trackId
                            ? libraryLink
                            : null;
                          const needsDoubleCheck = countedLink !== null && isBelowThreshold(countedLink, confidenceThreshold);
                          const chipTitle = request.platformLink ? `Click to copy search link · Hold to open matched song` : `Click to copy ${playerLabel} search link · Hold to open in browser`;
                          return (
                            <button
                              onMouseDown={() => {
                                longPressTimer = setTimeout(() => {
                                  if (directUrl.startsWith('http://') || directUrl.startsWith('https://')) {
                                    window.open(directUrl, '_blank', 'noopener');
                                  } else {
                                    window.location.href = directUrl;
                                  }
                                  longPressTimer = null;
                                }, 600);
                              }}
                              onMouseUp={() => {
                                if (longPressTimer) {
                                  clearTimeout(longPressTimer);
                                  longPressTimer = null;
                                  navigator.clipboard.writeText(searchUrl).then(() => {
                                    toast.success(`${playerLabel} search link copied`, { duration: 3000 });
                                  });
                                }
                              }}
                              onMouseLeave={() => { if (longPressTimer) { clearTimeout(longPressTimer); longPressTimer = null; } }}
                              className={`inline-flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded-full transition-colors cursor-pointer select-none ${
                                needsDoubleCheck
                                  ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 hover:bg-amber-200 dark:hover:bg-amber-900/50'
                                  : 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                              }`}
                              title={[
                                needsDoubleCheck ? `Fewer than ${confidenceThreshold} clean uses — double-check this link` : null,
                                countedLink ? confidenceText(countedLink) : null,
                                chipTitle,
                              ].filter(Boolean).join(' · ')}
                            >
                              <ExternalLink className="w-3 h-3" /> {request.platformName || playerLabel}
                              {countedLink && ` · ✓${countedLink.cleanUses}`}
                            </button>
                          );
                        })()}
                      </div>
                      <div className="flex items-center justify-end gap-1 mt-0.5">
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            djSortMode === 'copeland' && scoringMode === 'consensus'
                              ? 'bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300 font-medium'
                              : !thresholdMet
                              ? 'text-gray-400 dark:text-gray-600'
                              : 'text-amber-500 dark:text-amber-500'
                          }`}
                          title="Consensus Score: Unranked = last place"
                        >
                          C:{consensusScores.get(request.id) || 0}
                        </span>
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            djSortMode === 'copeland' && scoringMode === 'discovery'
                              ? 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300 font-medium'
                              : !thresholdMet
                              ? 'text-gray-400 dark:text-gray-600'
                              : 'text-emerald-500 dark:text-emerald-500'
                          }`}
                          title="Discovery Score: Only explicit rankings count"
                        >
                          D:{discoveryScores.get(request.id) || 0}
                        </span>
                        <span
                          className={`text-xs px-1.5 py-0.5 rounded ${
                            djSortMode === 'fifo'
                              ? 'bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 font-medium'
                              : 'text-gray-400 dark:text-gray-500'
                          }`}
                          title={`Requested at ${new Date(request.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}
                        >
                          {new Date(request.createdAt).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">{attendeeDisplayName(request.requestedBy.nickname, request.requestedBy.oderId)}</p>
                    </div>
                  </div>

                  {(() => {
                    if (level !== 'bridge' || request.localFile) return null;
                    const warningLevel = recognitionWarningLevel(playerSongsInOrder(displayQueue).indexOf(request));
                    if (!warningLevel || !recognition.flagOf(request.id)) return null;
                    return (
                      <RecognitionWarning
                        level={warningLevel}
                        playerLabel={playerLabel}
                        lockState={nextSongLockState(request, isQueuedInPlayer(request))}
                        removing={flagBusy}
                        onRemove={() => handleSetRecognitionFlag(request, false, 'manual')}
                      />
                    );
                  })()}

                  {lateStage && request.id === nextSong?.id && !request.localFile && (
                    <LateQueueWarning stage={lateStage} remainingMs={songEndCountdown ?? 0} playerLabel={playerLabel} />
                  )}

                  {/* Row 2: status badges + action buttons */}
                  <div className="flex items-center justify-between mt-2">
                    {/* Prep & Queue status badges */}
                    <div className="flex gap-1.5">
                      {(() => {
                        const topPosition = topStatus?.unqueued.get(request.id);
                        return (
                          <button
                            onClick={() => handleToggleQueuedInPlayer(request)}
                            disabled={!isQueuedInPlayer(request) && !canQueueOrLock(request)}
                            className={`px-2 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                              isQueuedInPlayer(request)
                                ? 'text-blue-600 bg-blue-500/10 border-blue-500/20 hover:bg-blue-500/20 dark:text-blue-400'
                                : topPosition !== undefined
                                  ? 'text-white bg-amber-500 border-amber-600 hover:bg-amber-600 font-semibold'
                                  : 'text-gray-500 bg-gray-100 border-gray-200 hover:bg-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:hover:bg-gray-600 dark:text-gray-400'
                            }`}
                            title={isQueuedInPlayer(request)
                              ? `Mark as removed from ${playerLabel} queue`
                              : canQueueOrLock(request) ? `Queue in ${playerLabel} (opens ${playerLabel} to search)` : VOTED_OUT_QUEUE_REFUSAL}
                          >
                            {isQueuedInPlayer(request) ? (
                              <><ListMusic className="w-3 h-3 inline mr-1" />Queued</>
                            ) : topPosition !== undefined ? `Queue now — #${topPosition}` : 'Queue Next'}
                          </button>
                        );
                      })()}
                      {(() => {
                        const { url: songUrl, isDirect } = openUrlFor(request, playMode);
                        return (
                          <button
                            onClick={() => {
                              if (songUrl.startsWith('http://') || songUrl.startsWith('https://')) {
                                window.open(songUrl, '_blank', 'noopener');
                              } else {
                                window.location.href = songUrl;
                              }
                              if (!request.durationVerified && !durationDismissedForEvent) {
                                openDurationEditor(request.id, request.song.durationMs);
                              }
                            }}
                            className="px-2 py-1 rounded-full text-xs font-medium border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/40 transition-colors whitespace-nowrap inline-flex items-center gap-1"
                            title={isDirect ? `Open in ${playerLabel}` : `Search in ${playerLabel}`}
                          >
                            <ExternalLink className="w-3 h-3" />
                            {playerLabel}
                          </button>
                        );
                      })()}
                      {!request.platformLink && !request.spotifyUri && !request.localFile && (
                        <button
                          onClick={() => {
                            setPasteUrlRequestId(pasteUrlRequestId === request.id ? null : request.id);
                            setPasteUrlValue('');
                          }}
                          className={`px-2 py-1 rounded-full text-xs font-medium border transition-colors whitespace-nowrap inline-flex items-center gap-1 ${
                            pasteUrlRequestId === request.id
                              ? 'border-amber-400 dark:border-amber-600 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400'
                              : 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/40'
                          }`}
                          title="Paste track URL to link this song"
                          aria-label={`Paste track URL for ${request.song.title}`}
                        >
                          <Link2 className="w-3 h-3" />
                          Link
                        </button>
                      )}
                    </div>

                    {/* Action buttons */}
                    {!isLocked && <div className="flex items-center gap-1">
                      {/* Lock position button */}
                      <button
                        onClick={() => handleToggleLockedNext(request)}
                        disabled={!request.lockedNext && !canQueueOrLock(request)}
                        className={`p-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                          request.lockedNext
                            ? 'text-amber-500 bg-amber-500/10 hover:bg-amber-500/20'
                            : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                        title={request.lockedNext ? 'Position locked — click to unlock' : canQueueOrLock(request) ? 'Lock this position (votes cannot displace it)' : VOTED_OUT_QUEUE_REFUSAL}
                        aria-label={`${request.lockedNext ? 'Unlock' : 'Lock'} position for ${request.song.title}`}
                      >
                        <Lock className="w-4 h-4" />
                      </button>
                      {/* VIP toggle button (only shown when VIP Trigger is enabled) */}
                      {eventData?.settings?.vipTriggerEnabled && (
                        <button
                          onClick={async () => {
                            if (request.vipTriggerable) {
                              await updateRequest(request.id, { vipTriggerable: false, vipOneTime: false });
                              toast.success('Removed from VIP list');
                            } else {
                              await updateRequest(request.id, { vipTriggerable: true, vipOneTime: true });
                              toast.success('Available to VIP (one-time)');
                            }
                          }}
                          className={`p-2 rounded-lg transition-colors ${
                            request.vipTriggerable
                              ? 'text-amber-500 hover:bg-amber-500/10'
                              : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                          }`}
                          title={request.vipTriggerable ? 'Remove from VIP list (click to toggle one-time)' : 'Make available to VIP'}
                          aria-label={`Toggle VIP for ${request.song.title}`}
                        >
                          <Crown className="w-4 h-4" />
                        </button>
                      )}
                      {/* One-time toggle (only shown when vipTriggerable) */}
                      {eventData?.settings?.vipTriggerEnabled && request.vipTriggerable && (
                        <button
                          onClick={async () => {
                            await updateRequest(request.id, { vipOneTime: !request.vipOneTime });
                            toast.success(request.vipOneTime ? 'VIP: reusable' : 'VIP: one-time only');
                          }}
                          className={`p-1 rounded text-xs font-medium transition-colors ${
                            request.vipOneTime
                              ? 'bg-amber-100 dark:bg-amber-900/30 text-amber-600'
                              : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
                          }`}
                          title={request.vipOneTime ? 'One-time (click to make reusable)' : 'Reusable (click to make one-time)'}
                        >
                          {request.vipOneTime ? '1x' : '∞'}
                        </button>
                      )}
                      {/* Notes toggle button */}
                      <button
                        onClick={() => handleToggleNotes(request.id, request.djNotes)}
                        className={`p-2 rounded-lg transition-colors ${
                          request.djNotes
                            ? 'text-amber-500 hover:bg-amber-500/10'
                            : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                        title={expandedNotes.has(request.id) ? 'Hide notes' : 'Add/Edit notes'}
                        aria-label={`${expandedNotes.has(request.id) ? 'Hide' : 'Show'} notes for ${request.song.title}`}
                      >
                        {expandedNotes.has(request.id) ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <StickyNote className="w-4 h-4" />
                        )}
                      </button>
                      <button
                        onClick={() => handleViewVoters(request.id, request.song.title)}
                        className={`p-2 rounded-lg transition-colors ${
                          request.voteCount > 0
                            ? 'text-blue-500 hover:bg-blue-500/10'
                            : 'text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700'
                        }`}
                        title={`View voters (${request.voteCount})`}
                        aria-label={`View voters for ${request.song.title}`}
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleAnalyzeSong(request.song.title, request.song.artist)}
                        className="p-2 text-purple-500 hover:bg-purple-500/10 rounded-lg transition-colors"
                        title="AI Song Analysis"
                        aria-label={`AI analysis for ${request.song.title} by ${request.song.artist}`}
                      >
                        <Sparkles className="w-4 h-4" />
                      </button>
                      {(() => {
                        const { url: appUrl, isDirect } = openUrlFor(request, playMode);
                        const action = isDirect ? 'play' : 'search for';
                        return (
                          <button
                            onClick={() => {
                              if (nowPlayingRequest) {
                                if (!window.confirm(`"${nowPlayingRequest.song.title}" is currently playing. Override and ${action} "${request.song.title}" in ${playerLabel}?`)) return;
                              }
                              if (appUrl.startsWith('http://') || appUrl.startsWith('https://')) {
                                window.open(appUrl, '_blank', 'noopener');
                              } else {
                                window.location.href = appUrl;
                              }
                              if (!request.durationVerified && !durationDismissedForEvent) {
                                openDurationEditor(request.id, request.song.durationMs);
                              }
                            }}
                            className="p-2 text-success hover:bg-success/10 rounded-lg transition-colors"
                            title={isDirect ? `Play in ${playerLabel}` : `Search in ${playerLabel}`}
                            aria-label={`Play ${request.song.title} in ${playerLabel}`}
                          >
                            <Play className="w-4 h-4" />
                          </button>
                        );
                      })()}
                      {!(songEndedNoAdvance && isQueuedInPlayer(request)) && (
                        <button
                          onClick={() => handleMarkNowPlaying(request.id, request.song.title)}
                          className="p-2 text-cyan-500 hover:bg-cyan-500/10 rounded-lg transition-colors"
                          title="Mark as Now Playing"
                          aria-label={`Mark ${request.song.title} as Now Playing`}
                        >
                          <Disc3 className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteRequest(request.id, request.song.title)}
                        className="p-2 text-danger hover:bg-danger/10 rounded-lg transition-colors"
                        title="Remove from queue"
                        aria-label={`Remove ${request.song.title} from queue`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>}
                  </div>
                </div>
                {songEndedNoAdvance && isQueuedInPlayer(request) && (
                  <div className="mt-2 px-4 pb-3">
                    <button
                      onClick={() => handleMarkNowPlaying(request.id, request.song.title)}
                      className="w-full py-2 px-4 bg-cyan-500 text-white font-semibold rounded-lg animate-pulse-cta motion-reduce:animate-none hover:bg-cyan-600 flex items-center justify-center gap-2 transition-colors"
                      title="Mark as Now Playing"
                      aria-label={`Mark ${request.song.title} as Now Playing`}
                    >
                      <Disc3 className="w-4 h-4" />
                      Now Playing
                    </button>
                  </div>
                )}
              </div>

              {/* Expanded Notes Section */}
              {expandedNotes.has(request.id) && (
                <div className="px-4 pb-4 pt-0 border-t border-gray-200 dark:border-gray-700">
                  <div className="mt-3">
                    <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
                      DJ Notes (private - not visible to attendees)
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={editingNotes[request.id] || ''}
                        onChange={(e) => handleNotesChange(request.id, e.target.value)}
                        placeholder="e.g., Play after break, Crowd favorite, etc."
                        className="input flex-1 text-sm"
                        maxLength={200}
                      />
                      <button
                        onClick={() => handleSaveNotes(request.id)}
                        disabled={savingNotes.has(request.id)}
                        className="btn btn-primary flex items-center gap-1.5 text-sm"
                      >
                        {savingNotes.has(request.id) ? (
                          <>
                            <span className="animate-spin">⏳</span>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="w-4 h-4" />
                            Save
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Paste track URL inline input */}
              {pasteUrlRequestId === request.id && (
                <div className="px-4 pb-3 pt-0 border-t border-amber-200 dark:border-amber-800">
                  <div className="mt-2">
                    <label className="block text-xs font-medium text-amber-600 dark:text-amber-400 mb-1">
                      Paste track URL to link this song
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={pasteUrlValue}
                        onChange={(e) => setPasteUrlValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handlePasteUrlOnSong(request.id); }}
                        placeholder="Paste a Spotify, TIDAL or Apple Music track link..."
                        className="input flex-1 text-sm"
                        autoFocus
                      />
                      <button
                        onClick={() => handlePasteUrlOnSong(request.id)}
                        disabled={pasteUrlLoading || !pasteUrlValue.trim()}
                        className="btn btn-primary flex items-center gap-1.5 text-sm"
                      >
                        {pasteUrlLoading ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Link2 className="w-4 h-4" />
                        )}
                        Link
                      </button>
                      <button
                        onClick={() => { setPasteUrlRequestId(null); setPasteUrlValue(''); }}
                        className="btn btn-secondary text-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Inline duration editor */}
              {editingDurationId === request.id && (
                <div className="px-4 pb-3 pt-0 border-t border-gray-200 dark:border-gray-700">
                  <div className="mt-2">
                    {!request.durationVerified && !durationDismissedForEvent && (
                      <p className="text-xs text-amber-500 dark:text-amber-400 mb-1.5 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" />
                        Duration not verified — enter from player
                      </p>
                    )}
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">Duration:</label>
                      <input
                        type="text"
                        value={durationEditValue}
                        onChange={(e) => {
                          const raw = e.target.value;
                          if (raw === '') { setDurationEditValue(''); return; }
                          const digits = raw.replace(/\D/g, '').slice(0, 5);
                          if (digits.length >= 3) {
                            const secs = digits.slice(-2);
                            const mins = digits.slice(0, -2);
                            setDurationEditValue(`${mins}:${secs}`);
                          } else {
                            setDurationEditValue(digits);
                          }
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleSaveDuration(request.id, 'exact'); }}
                        placeholder="m:ss"
                        className="input text-sm w-20 text-center py-1"
                        autoFocus
                      />
                      <button
                        onClick={() => handleSaveDuration(request.id, 'exact')}
                        disabled={durationEditSaving || !durationEditValue.trim()}
                        className="btn bg-green-500 text-white hover:bg-green-600 text-xs py-1 px-2 flex items-center gap-1"
                        title="Duration is exact"
                      >
                        {durationEditSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Exact
                      </button>
                      <button
                        onClick={() => handleSaveDuration(request.id, 'approximate')}
                        disabled={durationEditSaving}
                        className="btn bg-amber-500 text-white hover:bg-amber-600 text-xs py-1 px-2"
                        title="Close enough"
                      >
                        ~Close Enough
                      </button>
                      <button
                        onClick={() => { handleSaveDuration(request.id, null); }}
                        disabled={durationEditSaving}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 py-1"
                      >
                        Dismiss
                      </button>
                    </div>
                    <div className="flex gap-3 mt-1.5">
                      {!durationDismissedForEvent && (
                        <button
                          onClick={handleDurationDismissForEvent}
                          className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          Don't remind me this event
                        </button>
                      )}
                      <button
                        onClick={() => setEditingDurationId(null)}
                        className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}</SortableItem>
            </div>
          ))}
          </SortableList>

          {/* Infinite Scroll Load Trigger */}
          <div ref={loadMoreRef} className="py-4">
            {isLoadingMore && (
              <div className="flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Loading more...</span>
              </div>
            )}
            {!hasMore && filteredRequests.length > INITIAL_ITEMS_COUNT && (
              <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                All {filteredRequests.length} songs loaded
              </div>
            )}
          </div>
        </div>
      )}

      {/* Voters Modal */}
      {votersModal.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleCloseVotersModal}
          />
          {/* Modal */}
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Voters for "{votersModal.songTitle}"
              </h3>
              <button
                onClick={handleCloseVotersModal}
                className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Vote stats (ITER-144) */}
            <div className="mb-4 p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 text-sm">
              {voteStatsState.status === 'loading' && (
                <p className="text-gray-500 dark:text-gray-400">Loading vote stats…</p>
              )}
              {voteStatsState.status === 'error' && (
                <p className="text-red-600 dark:text-red-400">Couldn&apos;t load the vote stats. Close and open this again to retry.</p>
              )}
              {voteStatsState.status === 'loaded' && (
                <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-gray-700 dark:text-gray-300">
                  <dt>Upvotes</dt>
                  <dd className="text-right font-medium">{voteStatsState.stats.up}</dd>
                  <dt>Downvotes</dt>
                  <dd className="text-right font-medium">{voteStatsState.stats.down}</dd>
                  <dt>Participants who didn&apos;t vote on it</dt>
                  <dd className="text-right font-medium">
                    {Math.max(0, participantCount - voteStatsState.stats.up - voteStatsState.stats.down)} of {participantCount}
                  </dd>
                  {voteStatsState.stats.upBefore === null ? (
                    <dd className="col-span-2 text-xs text-gray-500 dark:text-gray-400">No start time set</dd>
                  ) : (
                    <>
                      <dt>Before start / after start</dt>
                      <dd className="text-right font-medium">
                        ↑{voteStatsState.stats.upBefore} / ↑{voteStatsState.stats.upAfter} · ↓{voteStatsState.stats.downBefore} / ↓{voteStatsState.stats.downAfter}
                      </dd>
                    </>
                  )}
                  {voteStatsState.newSinceStart !== null && (
                    <>
                      <dt>New since the start</dt>
                      <dd className="text-right font-medium">{voteStatsState.newSinceStart} of {participantCount}</dd>
                    </>
                  )}
                </dl>
              )}
            </div>

            {votersModal.voters.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No votes yet for this song</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                  {votersModal.voters.length} {votersModal.voters.length === 1 ? 'voter' : 'voters'}
                </p>
                {votersModal.voters.map((voter, index) => (
                  <div
                    key={voter.attendeeId}
                    className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium text-sm">
                      {index + 1}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-white">
                        {attendeeDisplayName(voter.nickname, voter.attendeeId)}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        ID: {voter.attendeeId.slice(0, 8)}...
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={handleCloseVotersModal}
                className="w-full btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* AI Analysis Modal */}
      {analysisModal.isOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={handleCloseAnalysisModal}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-purple-500" />
                AI Song Analysis
              </h3>
              <button
                onClick={handleCloseAnalysisModal}
                className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-3">
              <p className="font-medium text-gray-900 dark:text-white">{analysisModal.songTitle}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{analysisModal.songArtist}</p>
            </div>

            {analysisModal.loading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 mx-auto mb-3 text-purple-500 animate-spin" />
                <p className="text-gray-500 dark:text-gray-400">Analyzing song...</p>
              </div>
            ) : analysisModal.error ? (
              <div className="text-center py-8">
                <AlertTriangle className="w-8 h-8 mx-auto mb-3 text-danger" />
                <p className="text-danger">{analysisModal.error}</p>
              </div>
            ) : analysisModal.analysis ? (
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Summary</h4>
                  <p className="text-gray-900 dark:text-white">{analysisModal.analysis.summary}</p>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-1">Profanity Analysis</h4>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      analysisModal.analysis.profanityLevel === 'none'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : analysisModal.analysis.profanityLevel === 'mild'
                        ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                        : analysisModal.analysis.profanityLevel === 'moderate'
                        ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {analysisModal.analysis.profanityLevel === 'none' ? 'Clean' :
                       analysisModal.analysis.profanityLevel.charAt(0).toUpperCase() +
                       analysisModal.analysis.profanityLevel.slice(1) + ' Profanity'}
                    </span>
                  </div>
                  <p className="text-gray-900 dark:text-white text-sm">{analysisModal.analysis.profanityAnalysis}</p>
                </div>

                <p className={`text-xs ${
                  analysisModal.analysis.lyricsSource === 'web_search' || analysisModal.analysis.lyricsSource === 'manual'
                    ? 'text-emerald-500 dark:text-emerald-400'
                    : 'text-amber-500 dark:text-amber-400'
                }`}>
                  {analysisModal.analysis.lyricsSource === 'web_search'
                    ? 'Analyzed with song lyrics \u00b7 Results are cached'
                    : analysisModal.analysis.lyricsSource === 'manual'
                    ? 'Analyzed with pasted lyrics \u00b7 Results are cached'
                    : 'Analyzed from AI knowledge (no lyrics found) \u00b7 Results are cached'}
                </p>

                {analysisModal.analysis.lyricsSource !== 'manual' && (
                  <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">Paste lyrics from Musixmatch for a more accurate analysis:</p>
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(`${analysisModal.songTitle} ${analysisModal.songArtist}`);
                          toast.success('Copied to clipboard');
                        }}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                      >
                        <Copy className="w-3 h-3" />
                        Copy Title & Artist
                      </button>
                      <button
                        onClick={() => window.open('https://www.musixmatch.com/search', '_blank')}
                        className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-md hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                      >
                        <ExternalLink className="w-3 h-3" />
                        Open Musixmatch
                      </button>
                    </div>
                    <textarea
                      value={lyricsPaste.text}
                      onChange={(e) => setLyricsPaste(prev => ({ ...prev, text: e.target.value }))}
                      placeholder="Paste lyrics here..."
                      rows={4}
                      className="w-full px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-md resize-y focus:outline-none focus:ring-2 focus:ring-purple-500"
                    />
                    <button
                      onClick={handleReanalyzeWithLyrics}
                      disabled={!lyricsPaste.text.trim() || lyricsPaste.loading}
                      className="mt-2 w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {lyricsPaste.loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                      {lyricsPaste.loading ? 'Re-analyzing...' : 'Re-analyze with Lyrics'}
                    </button>
                  </div>
                )}
              </div>
            ) : null}

            {/* DJ Verdict Buttons */}
            {analysisModal.analysis && (
              <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Your Verdict</p>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSetVerdict('passed')}
                    disabled={analysisModal.verdictLoading}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                      analysisModal.djVerdict === 'passed'
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                    }`}
                  >
                    <ShieldCheck className="w-4 h-4" />
                    Pass
                  </button>
                  <button
                    onClick={() => handleSetVerdict('blocked')}
                    disabled={analysisModal.verdictLoading}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg font-medium text-sm transition-colors ${
                      analysisModal.djVerdict === 'blocked'
                        ? 'bg-red-500 text-white'
                        : 'bg-gray-100 dark:bg-gray-700 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                    }`}
                  >
                    <ShieldX className="w-4 h-4" />
                    Block
                  </button>
                </div>
              </div>
            )}

            <div className="mt-3">
              <button
                onClick={handleCloseAnalysisModal}
                className="w-full btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Song Match Modal (platform-agnostic) */}
      <div {...reviewHold.activityHandlers}>
      <SongMatchModal
        request={matchingRequest}
        eventName={eventData?.name}
        batchIndex={pendingMatchIndex}
        batchTotal={pendingMatchTotal}
        playMode={playMode.id}
        djUid={user?.uid || ''}
        onApproveWithPlatformLink={handleModalApproveWithPlatformLink}
        onApproveDirect={handleModalApproveDirect}
        onReject={handleModalReject}
        onDefer={handleModalDefer}
        onSkip={handleModalSkip}
        onClose={handleCloseMatchModal}
        onAnalyzeSong={handleAnalyzeSong}
        previewSessionConnected={previewSession?.status === 'connected'}
        onSendToPreview={handleSendToPreview}
        previewResultUrl={previewResultUrl}
        onPreviewResultConsumed={() => setPreviewResultUrl(null)}
        initialLinkUrl={matchInitialLinkUrl}
        holdBanner={<ReviewHoldBanner warning={reviewHold.warning} onKeep={reviewHold.renew} />}
      />
      </div>

      {/* Limit Raise Modal */}
      {eventData && (
        <LimitRaiseModal
          isOpen={showLimitRaiseModal}
          onClose={() => setShowLimitRaiseModal(false)}
          eventId={eventId!}
          currentLimit={eventData.settings.maxRequestsPerUser ?? 5}
          overflowSongs={overflowRequests}
        />
      )}

      {/* Preview Device QR Code Modal */}
      {showPreviewQr && previewSession && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" onClick={() => setShowPreviewQr(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full mx-4 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Smartphone className="w-5 h-5 text-green-500" />
                Connect Preview Device
              </h3>
              <button onClick={() => setShowPreviewQr(false)} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Scan this QR code on your preview phone to connect it for song previewing.
            </p>
            <div className="flex justify-center mb-4">
              <img
                src={generatePreviewQrUrl(eventId!, previewSession.token)}
                alt="Preview Device QR Code"
                className="w-64 h-64"
              />
            </div>
            <p className="text-center text-xs text-gray-400 dark:text-gray-500 break-all mb-4">
              {getPreviewPageUrl(eventId!, previewSession.token)}
            </p>
            <div className="text-center">
              <span className={`text-sm font-medium ${previewSession.status === 'connected' ? 'text-green-600' : 'text-amber-600'}`}>
                {previewSession.status === 'connected' ? 'Connected!' : 'Waiting for connection...'}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Played Songs Section (F5) */}
      {playedRequests.length > 0 && (
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setPlayedCollapsed(!playedCollapsed)}
            className="w-full flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center gap-2 flex-wrap">
              <CheckCircle className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <span className="font-medium text-gray-700 dark:text-gray-300">
                Played
              </span>
              <span className="px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 text-xs font-bold">
                {playedRequests.length}
              </span>
              {eventData?.settings?.triviaEnabled && (
                <>
                  <span className={`flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${
                    curatedPlayedCount >= playedRequests.length
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400'
                  }`}>
                    <Brain className="w-3 h-3" />
                    {curatedPlayedCount}/{playedRequests.length} curated
                  </span>
                  {playedClueBreakdown && (
                    <span
                      className="text-xs px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300"
                      title={`Difficulty of ${playedClueBreakdown.counted} locked clue${playedClueBreakdown.counted !== 1 ? 's' : ''}`}
                    >
                      {formatDifficultyBreakdown(playedClueBreakdown)}
                    </span>
                  )}
                </>
              )}
            </div>
            {playedCollapsed ? (
              <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            ) : (
              <ChevronUp className="w-4 h-4 text-gray-500 dark:text-gray-400" />
            )}
          </button>
          {!playedCollapsed && (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {playedRequests.map((request) => (
                <div key={request.id} className="p-3 bg-white dark:bg-gray-800/30">
                <div className="flex items-center gap-3">
                  {request.song.albumArtUrl ? (
                    <img
                      src={request.song.albumArtUrl}
                      alt={`${request.song.title} album art`}
                      className="w-10 h-10 rounded flex-shrink-0 object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0 flex items-center justify-center">
                      <Music className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{request.song.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{request.song.artist}</p>
                    {request.unplannedPlayAt && (
                      <p className="text-xs text-gray-400 dark:text-gray-500 italic">Played unplanned — not in the queue</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <span className="text-xs text-gray-400 mr-1">{request.voteCount} votes</span>
                    {request.wrongSongReportedAt && !request.wrongSongFixedAt && (
                      <button
                        onClick={() => setWrongSongPanel({ requestId: request.id, mode: 'fix' })}
                        className="text-xs px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 hover:bg-amber-200 dark:hover:bg-amber-800/40 flex items-center gap-1"
                        title="This song was reported as the wrong song — paste the right link"
                      >
                        <AlertTriangle className="w-3 h-3" /> Fix link
                      </button>
                    )}
                    {songVerdicts.get(`${request.song.title.toLowerCase().trim()}|${request.song.artist.toLowerCase().trim()}`) === 'passed' && (
                      <span title="Previously passed"><ShieldCheck className="w-3.5 h-3.5 text-green-500" /></span>
                    )}
                    {songVerdicts.get(`${request.song.title.toLowerCase().trim()}|${request.song.artist.toLowerCase().trim()}`) === 'blocked' && (
                      <span title="Previously blocked"><ShieldX className="w-3.5 h-3.5 text-red-500" /></span>
                    )}
                    {!request.unplannedPlayAt && (
                      <button
                        onClick={() => handleBackToQueueFromPlayed(request)}
                        disabled={movingBackIds.has(request.id)}
                        className="p-1.5 rounded-lg transition-colors text-gray-400 hover:text-blue-500 hover:bg-blue-500/10 disabled:opacity-50"
                        title="Back to queue — it didn't really play"
                        aria-label="Move back to queue"
                      >
                        <RotateCcw className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleAnalyzeSong(request.song.title, request.song.artist)}
                      className="p-1.5 text-purple-500 hover:bg-purple-500/10 rounded-lg transition-colors"
                      title="AI Song Analysis"
                    >
                      <Sparkles className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setCurationRequest(request)}
                      className={`relative p-1.5 rounded-lg transition-colors ${
                        lockedClueIds.has(request.id)
                          ? 'text-indigo-600 dark:text-indigo-400 bg-indigo-100 dark:bg-indigo-900/40'
                          : 'text-gray-400 hover:text-indigo-500 hover:bg-indigo-500/10'
                      }`}
                      title={lockedClueIds.has(request.id) ? 'Trivia clue locked' : 'Add trivia clue'}
                      aria-label="Curate trivia clue"
                    >
                      <Brain className="w-4 h-4" />
                      {lockedClueIds.has(request.id) && (
                        <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-500 rounded-full border border-white dark:border-gray-800" />
                      )}
                    </button>
                  </div>
                </div>
                {wrongSongPanel?.requestId === request.id && (
                  <WrongSongPanel
                    key={`${wrongSongPanel.requestId}-${wrongSongPanel.mode}`}
                    request={request}
                    playMode={playMode}
                    mode={wrongSongPanel.mode}
                    onDone={() => setWrongSongPanel(null)}
                  />
                )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* QR Code Modal (F9) */}
      {showQrModal && eventData?.eventUrl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowQrModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Event QR Code</h3>
              <button
                onClick={() => setShowQrModal(false)}
                className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            <div className="flex justify-center mb-4">
              <img
                src={generateQrCodeUrl(eventData.eventUrl)}
                alt="Event QR Code"
                className="w-64 h-64"
              />
            </div>
            <p className="text-center text-sm text-gray-600 dark:text-gray-400 break-all mb-4">
              {window.location.origin}/e/{eventData.eventUrl}
            </p>
            <button
              onClick={() => { setShowQrModal(false); setShowQrPrint(true); }}
              className="w-full btn btn-primary flex items-center justify-center gap-2"
            >
              <Printer className="w-4 h-4" />
              Print QR Code
            </button>
            {nfcSupported && (
              <div className="mt-3">
                {nfcWriting ? (
                  <div className="space-y-2">
                    <div className="w-full btn bg-blue-500 text-white flex items-center justify-center gap-2 animate-pulse cursor-default">
                      <Smartphone className="w-4 h-4" />
                      Hold tag to phone...
                    </div>
                    <button onClick={handleCancelNfc} className="w-full btn btn-secondary text-sm">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <>
                    <button
                      onClick={handleWriteNfc}
                      className="w-full btn bg-blue-500 hover:bg-blue-600 text-white flex items-center justify-center gap-2"
                    >
                      <Smartphone className="w-4 h-4" />
                      Write NFC Tag
                    </button>
                    <label className="flex items-center gap-2 mt-2 text-xs text-gray-500 dark:text-gray-400 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={nfcLockTag}
                        onChange={(e) => setNfcLockTag(e.target.checked)}
                        className="rounded border-gray-300 text-blue-500 focus:ring-blue-500"
                      />
                      <span>Lock tag after writing <span className="text-amber-500">(permanent — cannot be rewritten)</span></span>
                    </label>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* QR Print Modal */}
      {showQrPrint && eventData && (
        <QrPrintModal
          eventName={eventData.name}
          eventDate={eventData.date}
          startTime={eventData.startTime}
          endTime={eventData.endTime}
          eventUrl={eventData.eventUrl}
          location={eventData.location}
          address={eventData.address}
          contactInfo={eventData.contactInfo}
          onClose={() => setShowQrPrint(false)}
        />
      )}

      {/* Local File Upload Modal */}
      {showLocalFileUpload && eventId && user?.uid && (
        <LocalFileUploadModal
          eventId={eventId}
          djId={user.uid}
          onClose={() => setShowLocalFileUpload(false)}
          onAdded={async () => {
            setRequests(sortRequests(
              (await getRequestsByEventId(eventId)).filter(r => r.status === 'queued')
            ));
          }}
        />
      )}

      {/* DJ Local Library Modal */}
      {showLocalLibrary && eventId && user?.uid && (
        <DjLocalLibraryModal
          eventId={eventId}
          djId={user.uid}
          onClose={() => setShowLocalLibrary(false)}
          onAdded={async () => {
            setRequests(sortRequests(
              (await getRequestsByEventId(eventId)).filter(r => r.status === 'queued')
            ));
          }}
        />
      )}

      {/* F3: DJ Add Song Modal */}
      {fixDialog && (
        <FixNowPlayingDialog
          entry={fixDialog.entry}
          nowPlaying={nowPlayingRequest}
          queue={displayQueue}
          playMode={playMode}
          detected={detectedTrack}
          canFlag={canFlag && !fixDialog.noFlag}
          onOutcome={handleRecoveryOutcome}
          onClose={() => setFixDialog(null)}
        />
      )}

      {positionPrompt && (
        <QueuePositionPrompt
          songTitle={positionPrompt.songTitle}
          queueLength={displayQueue.length}
          busy={placingPosition}
          onPlace={(position) => { void handlePlacePosition(position); }}
          onCancel={() => setPositionPrompt(null)}
        />
      )}

      {showRemoveFlagPrompt && nowPlayingRequest && (
        <RemoveFlagPrompt
          title={nowPlayingRequest.song.title}
          cleanCount={nowPlayingRequest.bridgeCleanWhileFlagged ?? 0}
          playerLabel={playerLabel}
          busy={flagBusy}
          onRemove={() => handleSetRecognitionFlag(nowPlayingRequest, false, 'prompt')}
          onDismiss={() => setDismissedRemovePrompts(prev => new Set(prev).add(nowPlayingRequest.id))}
        />
      )}

      {showAddSongModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => { setShowAddSongModal(false); setAddSongQuery(''); setAddSongResults([]); }}
          />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                Add Song to Queue
              </h3>
              <button
                onClick={() => { setShowAddSongModal(false); setAddSongQuery(''); setAddSongResults([]); }}
                className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                aria-label="Close modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Search for a song to add directly to the queue (bypasses pending approval).
            </p>

            {/* Local File buttons (when enabled) */}
            {eventData?.settings?.localFilesEnabled && (
              <div className="flex gap-2 mb-4 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                <button
                  onClick={() => { setShowAddSongModal(false); setShowLocalFileUpload(true); }}
                  className="btn btn-sm bg-purple-500 hover:bg-purple-600 text-white border-none flex-1"
                >
                  <FileMusic className="w-4 h-4" />
                  Upload Local File
                </button>
                <button
                  onClick={() => { setShowAddSongModal(false); setShowLocalLibrary(true); }}
                  className="btn btn-sm btn-secondary flex-1"
                >
                  <ListMusic className="w-4 h-4" />
                  From Library
                </button>
              </div>
            )}

            {/* Search bar */}
            <div className="flex gap-2 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={addSongQuery}
                  onChange={(e) => setAddSongQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddSongSearch()}
                  placeholder="Search for a song..."
                  className="input pl-9 w-full text-sm"
                  autoFocus
                />
              </div>
              <button
                onClick={handleAddSongSearch}
                disabled={addSongLoading}
                className="btn btn-primary text-sm"
              >
                {addSongLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              </button>
            </div>

            {/* Results */}
            {addSongLoading ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 mx-auto mb-3 text-primary animate-spin" />
                <p className="text-gray-500 dark:text-gray-400">Searching...</p>
              </div>
            ) : addSongResults.length === 0 && addSongQuery ? (
              <div className="text-center py-8">
                <SearchX className="w-8 h-8 mx-auto mb-3 text-gray-400" />
                <p className="text-gray-500 dark:text-gray-400">No results found. Try a different search.</p>
              </div>
            ) : addSongResults.length > 0 ? (
              <div className="space-y-1 max-h-80 overflow-y-auto">
                <p className="text-xs text-gray-500 mb-2">{addSongResults.length} results — click to add</p>
                {addSongResults.map((result) => {
                  const key = String(result.trackId);
                  const title = result.trackName;
                  const artist = result.artistName;
                  const art = result.artworkUrl100;
                  const duration = result.trackTimeMillis;
                  return (
                    <div
                      key={key}
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-primary/5 transition-colors border border-transparent hover:border-primary/30"
                    >
                      <button
                        onClick={() => handleAddSongSelect(result)}
                        disabled={addingSongId === key}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        {art ? (
                          <img src={art} alt={title} className="w-10 h-10 rounded flex-shrink-0 object-cover" />
                        ) : (
                          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded flex-shrink-0 flex items-center justify-center">
                            <Music className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white text-sm truncate">{title}</p>
                          <p className="text-xs text-gray-600 dark:text-gray-400 truncate">{artist}</p>
                        </div>
                        {addingSongId === key ? (
                          <Loader2 className="w-4 h-4 animate-spin text-primary flex-shrink-0" />
                        ) : (
                          <span className="text-xs text-gray-400 flex-shrink-0">{formatDuration(duration)}</span>
                        )}
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAnalyzeSong(title, artist); }}
                        className="p-1.5 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/30 text-purple-400 hover:text-purple-500 transition-colors flex-shrink-0"
                        title="AI Song Analysis"
                      >
                        <Sparkles className="w-4 h-4" />
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : null}

            <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => { setShowAddSongModal(false); setAddSongQuery(''); setAddSongResults([]); }}
                className="w-full btn btn-secondary"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Switch Event Confirmation Dialog */}
      {switchEventConfirm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-sm w-full p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <h3 className="font-semibold text-gray-900 dark:text-white">Switch Playback?</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <span className="font-medium text-gray-900 dark:text-white">{switchEventConfirm.lockedEventName}</span> is currently playing. Starting playback here will take over Spotify.
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSwitchEventConfirm(null)}
                className="btn btn-secondary btn-sm flex-1"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  const action = switchEventConfirm.action;
                  setSwitchEventConfirm(null);
                  await action();
                }}
                className="btn btn-primary btn-sm flex-1"
              >
                Switch
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Library Queue Modal */}
      {showLibraryQueueModal && eventId && user && (
        <LibraryQueueModal
          djUid={user.uid}
          eventId={eventId}
          existingRequests={[...pendingRequests, ...requests]}
          playMode={playMode}
          onSongsAdded={() => setShowLibraryQueueModal(false)}
          onClose={() => setShowLibraryQueueModal(false)}
        />
      )}

      {/* Trivia Clue Curation Panel */}
      {curationRequest && eventId && user && (
        <div className="mx-4 mb-6 rounded-xl border border-indigo-400/40 dark:border-indigo-500/30 bg-white dark:bg-gray-900 shadow-lg overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 bg-indigo-50 dark:bg-indigo-900/20 border-b border-indigo-200/50 dark:border-indigo-500/20">
            {curationRequest.song.albumArtUrl && (
              <img
                src={curationRequest.song.albumArtUrl}
                alt=""
                className="w-10 h-10 rounded-md object-cover flex-shrink-0 shadow-sm"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest mb-0.5 flex items-center gap-1">
                <Brain className="w-3 h-3" /> Trivia Clue
              </p>
              <p className="font-semibold text-sm text-gray-900 dark:text-white truncate leading-tight">{curationRequest.song.title}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{curationRequest.song.artist}</p>
            </div>
            <button
              onClick={() => setCurationRequest(null)}
              className="flex-shrink-0 p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="p-4">
            <TriviaCluePanel
              key={curationRequest.id}
              eventId={eventId}
              djId={user.uid}
              songRequestId={curationRequest.id}
              songTitle={curationRequest.song.title}
              songArtist={curationRequest.song.artist}
              songAlbumArtUrl={curationRequest.song.albumArtUrl}
              onClose={() => setCurationRequest(null)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
